<template>
  <!-- `:style` carries ONLY what the payload authored (see `cardStyle` — the simulation
       colour variables plus `data.mainFrameColor`); a card that authored none gets no
       inline style at all and every band below falls back to this file's own palette.

       🔴 The same rule holds for every `sx(…)` / `fx(…)` binding further down: each one
       answers null when the payload carried no hint for it, so an unstyled card renders
       exactly as it did before hints existed. Do NOT "tidy" any of them into a default. -->
  <div class="tsc" data-testid="tpg-card" :style="cardStyle">
    <!-- ── unknown card type — never throw, never blank the chat ──────────── -->
    <div v-if="!isKnownType" class="tsc-unknown" data-testid="tpg-card-unknown">
      <v-alert
        type="warning"
        variant="tonal"
        density="compact"
        class="mb-2"
      >ยังไม่รองรับการ์ดชนิดนี้: {{ cardType || '(ไม่ระบุชนิด)' }}</v-alert>
      <!-- 🔴 Its own testid: the footer's JSON panel uses `v-show`, so that node is in
           the DOM even while hidden. Reusing `tpg-card-json` here would put TWO nodes
           with that id on an unknown card and every strict-mode locator would fail. -->
      <pre class="tsc-json" data-testid="tpg-card-json-unknown">{{ prettyRaw }}</pre>
    </div>

    <template v-else>
      <!-- ── headline band (TAX_INCOME_EXPENSE only — driven by data.headline) ─
           🔴 `!isForm` as well as `headline`: the two renderers below are mutually
           exclusive, and a hand-edited TAX_AMOUNT_FORM payload that still carries a
           stray `headline` must not put a dark income band on top of a data-entry
           form — that band is a claim about the customer's money. -->
      <!-- 🔴 The band's own fill is authorable now (`headline.frameColor`). It used to be the card's
           `--tsc-dark` and nothing else, which made it the one surface on this card a designer could not
           reach — and the labels sitting on it ARE authorable, so a palette change could put navy text on
           a navy band with no way to fix either side. -->
      <!-- 🔴 `!isSummary` as well as `!isForm`, and it is a FIX not a tightening: TAX_SUMMARY
           carries the same `data.headline` object and draws its own `.tsc-sum-hero` from it
           (label / ฿amount / caption). Gated on `!isForm` alone this band rendered TOO, so a
           summary card showed its headline twice — once as a dark navy masthead with a `บาท`
           suffix and `.00` decimals, then again as the hero the design actually specifies. -->
      <div
        v-if="!isForm && !isSummary && headline"
        class="tsc-headline"
        data-testid="tpg-card-headline"
        :style="fx(headline, 'frameColor')"
      >
        <!-- `secondLabel` is a second run on the SAME line, right after the label (the app draws it
             inline too) — its own colour / size / weight, e.g. the orange bold «ยังไม่ใช่จำนวนที่ลดหย่อนจริง». -->
        <div v-if="headline.label || headline.secondLabel" class="tsc-hl-label" :style="sx(headline, 'label')">
          {{ headline.label }}<template v-if="headline.secondLabel">
            <span class="tsc-hl-label2" :style="mx(sx(headline, 'secondLabel'), wx(headline, 'secondLabel'))">{{ headline.secondLabel }}</span></template>
        </div>
        <div class="tsc-hl-amount">
          <span class="tsc-hl-num" :style="sx(headline, 'amount')">{{ fmtAmount(headline.amount) }}</span>
          <!-- 🔴 The unit beside the figure. It takes the headline's own `unitLabel` hints — and, when the
               payload carries one, its own `unitLabel` TEXT (collapsed to the turn's language server-side),
               so an English card no longer ends in "บาท". No text ⇒ "บาท", as every older card printed. -->
          <span class="tsc-hl-unit" :style="sx(headline, 'unitLabel')">{{ moneyUnit(headline) }}</span>
        </div>
        <div v-if="stats.length" class="tsc-hl-stats">
          <div
            v-for="(s, si) in stats"
            :key="si"
            class="tsc-pill"
            :data-testid="'tpg-card-stat-' + si"
            :style="fx(s, 'rowFrameColor')"
          >
            <div class="tsc-pill-label" :style="sx(s, 'label')">{{ s.label }}</div>
            <!-- 🔴 THE FIGURE AND ITS UNIT ARE TWO RUNS, the same shape as the headline's
                 `.tsc-hl-num` / `.tsc-hl-unit` pair above. One element carrying both would make
                 `unitLabelFontSize` unreachable — the unit would inherit whatever `amountFontSize`
                 said, and a stat that authored only `unitLabelColor` would come out at the
                 figure's size. The unit is per-stat because a tile can count baht while its
                 neighbour counts something else.
                 🔴 `toneClass` sits on the FIGURE, not the row: the tone is a claim about the
                 amount (POSITIVE / NEGATIVE), and it has never been a claim about the word after
                 it. Class first and hint second, so an authored `amountColor` still wins — that
                 is the precedence this card has always had. -->
            <div class="tsc-pill-amt">
              <span class="tsc-pill-num" :class="toneClass(s)" :style="sx(s, 'amount')">{{ fmtAmount(s.amount) }}</span>
              <span v-if="s.unitLabel" class="tsc-pill-unit" :style="sx(s, 'unitLabel')">{{ s.unitLabel }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ── TAX_AMOUNT_FORM — the mini-form the app draws, LIVE ─────────────────────
           🔴 The boxes really take typing and both buttons really post. This is the ONLY
           surface anywhere that exercises the `TAX_SHORT_FORM` postback, so an inert
           preview here would leave the mobile app as the first client ever to run that
           path — with the three-state rule below untested against a real screen.
           🔴 `placeholder` is printed VERBATIM and never goes through `fmtAmount`: the
           "0" it usually holds is admin-authored placeholder TEXT, not an amount, so
           rule 1 (null ⇒ em dash) does not apply and must not be made to apply — an empty
           box has to show the same "0" the customer sees. -->
      <div v-if="isForm" class="tsc-body tsc-body--form" data-testid="tpg-card-form">
        <div
          v-if="data.title"
          class="tsc-form-title"
          data-testid="tpg-card-form-title"
          :style="sx(data, 'title')"
        >{{ data.title }}</div>
        <div
          v-if="data.subtitle"
          class="tsc-form-sub"
          data-testid="tpg-card-form-subtitle"
          :style="sx(data, 'subtitle')"
        >{{ data.subtitle }}</div>

        <div
          v-if="!formGroups.length"
          class="tsc-empty"
          data-testid="tpg-card-form-empty"
        >— การ์ดนี้ไม่มีช่องกรอก —</div>

        <div
          v-for="(g, gi) in formGroups"
          :key="gi"
          class="tsc-form-group"
          :data-testid="'tpg-card-form-group-' + gi"
        >
          <!-- 🔴 The rule marks the BOUNDARY between groups, so the first group opens
               without one (`v-if="gi"`). Fields inside one group are deliberately NOT
               separated: that nesting is the only thing on the wire saying "these rows
               belong together", and drawing a line between them would erase it.
               🔴 OPT-IN, because the app draws no line at all — it spaces the groups instead.
               A rule this screen drew unconditionally was a divider the customer never saw on
               every card ever authored. `dividerColor` is the card asking for one. -->
          <div
            v-if="gi && formDividerStyle"
            class="tsc-form-rule"
            :style="formDividerStyle"
            :data-testid="'tpg-card-form-rule-' + gi"
          ></div>

          <!-- 🔴 THE ROW takes the field's spacing hints (`marginTop` … `paddingRight`, dp→px 1:1 —
               see `spacingStyle`), whichever kind of control it holds: the app's mock puts them on
               the FIELD, and this is the one element every kind — tick group, tick, counter, money
               box — sits inside, together with its own captions. An authored `marginTop` wins the
               stylesheet's 16px gap between rows, which is the point; a row with no hints gets an
               empty object and keeps that gap untouched. -->
          <div
            v-for="(f, fi) in fieldsOf(g)"
            :key="fi"
            class="tsc-form-row"
            :data-testid="'tpg-card-form-field-' + gi + '-' + fi"
            :style="spx(f)"
          >
            <!-- 🔴 ABOVE THE CONTROL, NOT ABOVE THE ROW — and on a money row that means BELOW the label,
                 between it and the box (see the money branch, where this same block is repeated inside the
                 stack). The app reads `noteAbove` now, so the «ยังไม่ขึ้นบนแอป» tag and its dashed frame are
                 gone; what replaced them was briefly a caption over the LABEL, which is a third position no
                 phone draws. This copy is the one for rows with no label/box stack to sit inside — a counter
                 (label beside its control) and a tick (the label IS the control). -->
            <div
              v-if="f.noteAbove && !isMoneyField(f)"
              class="tsc-form-note tsc-form-note--above"
              :data-testid="'tpg-card-form-noteabove-' + gi + '-' + fi"
              :style="sx(f, 'noteAbove')"
            >{{ f.noteAbove }}</div>
            <!-- 🔴 Label ABOVE the box, box full width, unit INSIDE it — that is the app's
                 own layout (mock `2.3b1`), and this preview exists to answer "what will the
                 customer see". A label/box pair side by side fits more rows on screen but
                 stops being an answer to that question. -->
            <!-- 🔴 A COUNTER, not a money box: `control: "STEPPER"` on the row. The customer is
                 saying how many PEOPLE (children, dependants), and a keyboard on that question
                 invites "2 คน" / "สองคน" into a box the backend can only read as digits. The
                 wire is identical — the value still travels as the string "2" in the same
                 `values` map — so this branch is purely what the screen looks like. -->
            <!-- 🔴 A TICK, not a box: the row names ONE person (บิดา / มารดา) who either qualifies or does not.
                 The value on the wire is still the decimal string "1" / "0", so the sibling row holds a number
                 and the formula adds the ticks up to get "how many people" — a checkbox is a counter that can
                 only be 0 or 1, which is why it needs no separate reader. -->
            <!-- 🔴 A HEADING OVER TICKS, which is the same row shape wearing a list: «อุปการะเลี้ยงดูพ่อแม่
                 (ตนเอง)» over พ่อ / แม่, two per line (`TaxFundField.options`). The heading answers NOTHING — each
                 option carries its own `id` and its own answer row — so there is no input beside its label, and
                 the ⓘ still hangs where it does on a plain tick. Regrouping four standalone ticks under two
                 headings must change what the customer READS and nothing about where their answers LAND. -->
            <div v-if="isCheckboxField(f) && tickOptions(f).length" class="tsc-check-group">
              <div class="tsc-check-head">
                <span class="tsc-check-label" :style="sx(f, 'label')">{{ f.label }}</span>
                <span
                  v-if="f.note"
                  class="tsc-check-info"
                  :title="f.note"
                  :aria-label="f.note"
                  :data-testid="'tpg-card-form-checkinfo-' + gi + '-' + fi"
                >&#9432;</span>
              </div>
              <div class="tsc-check-opts">
                <label
                  v-for="(o, oi) in tickOptions(f)"
                  :key="oi"
                  class="tsc-check tsc-check--opt"
                >
                  <input
                    type="checkbox"
                    class="tsc-check-input"
                    :checked="isChecked(o)"
                    :disabled="!formCanEdit || formBusy"
                    :data-testid="'tpg-card-form-check-' + gi + '-' + fi + '-' + oi"
                    @change="onCheck(o, $event)"
                  >
                  <span class="tsc-check-label" :style="sx(o, 'label')">{{ o.label }}</span>
                </label>
              </div>
            </div>
            <label v-else-if="isCheckboxField(f)" class="tsc-check">
              <input
                type="checkbox"
                class="tsc-check-input"
                :checked="isChecked(f)"
                :disabled="!formCanEdit || formBusy"
                :data-testid="'tpg-card-form-check-' + gi + '-' + fi"
                @change="onCheck(f, $event)"
              >
              <span class="tsc-check-label" :style="sx(f, 'label')">{{ f.label }}</span>
              <!-- 🔴 On a TICK row the app does not print `note` as a grey line under the box — there is no box —
                   it hangs it on an ⓘ beside the label (`TaxFundField.note`, and the DTO says the same). Drawing
                   the paragraph here instead is what made a two-line tick row on this screen a one-line one on
                   every phone. The `v-else` on the note block below is what keeps it from being drawn twice. -->
              <span
                v-if="f.note"
                class="tsc-check-info"
                :title="f.note"
                :aria-label="f.note"
                :data-testid="'tpg-card-form-checkinfo-' + gi + '-' + fi"
              >&#9432;</span>
            </label>
            <!-- 🔴 `--inline`, unlike the money row below: the app puts a counter's label to the LEFT of its
                 − n + and the box's label ABOVE it. Two different layouts because the two controls are two
                 different widths — a counter is small enough to sit beside its label and a full-width box is
                 not — and this screen answers "what will the customer see". -->
            <div v-else-if="isStepperField(f)" class="tsc-form-line tsc-form-line--inline">
              <span class="tsc-form-label" :style="sx(f, 'label')">{{ f.label }}</span>
              <div
                class="tsc-step"
                :class="{ 'tsc-step--static': !formCanEdit }"
                :data-testid="'tpg-card-form-step-' + gi + '-' + fi"
              >
                <button
                  type="button"
                  class="tsc-step-btn"
                  :disabled="!formCanEdit || formBusy || stepAt(f) <= stepMin(f)"
                  :aria-label="'ลด ' + f.label"
                  :data-testid="'tpg-card-form-step-minus-' + gi + '-' + fi"
                  @click="onStep(f, -1)"
                >−</button>
                <span
                  class="tsc-step-value"
                  :data-testid="'tpg-card-form-step-value-' + gi + '-' + fi"
                >{{ stepAt(f) }}</span>
                <button
                  type="button"
                  class="tsc-step-btn"
                  :disabled="!formCanEdit || formBusy || stepOverMax(f)"
                  :aria-label="'เพิ่ม ' + f.label"
                  :data-testid="'tpg-card-form-step-plus-' + gi + '-' + fi"
                  @click="onStep(f, 1)"
                >+</button>
                <span v-if="unitOf(f)" class="tsc-step-unit">{{ unitOf(f) }}</span>
              </div>
            </div>
            <div v-else class="tsc-form-line">
              <span class="tsc-form-label" :style="sx(f, 'label')">{{ f.label }}</span>
              <!-- 🔴 INSIDE the stack, between the label and the box — that is where the app draws it, and
                   the reason it is here rather than at row level (where the row's other kinds keep their
                   copy of it): the caption explains WHICH number to type, so it belongs to the box, not to
                   the row. `.tsc-form-line` is a column with a gap, so this needs no spacing of its own. -->
              <div
                v-if="f.noteAbove"
                class="tsc-form-note tsc-form-note--above"
                :data-testid="'tpg-card-form-noteabove-' + gi + '-' + fi"
                :style="sx(f, 'noteAbove')"
              >{{ f.noteAbove }}</div>
              <!-- 🔴 `type="text"` with a numeric keypad, never `type="number"`: a browser
                   number field reports anything it cannot parse as the EMPTY STRING, and an
                   empty box on this card is the customer saying "I have none of this" (the
                   backend stores 0). A typo would then be filed as a declared zero. Text
                   hands over exactly what was typed and lets the backend judge it.
                   🔴 The label is a `span`, not a `<label for>`: several of these cards can be
                   mounted at once in the transcript, and a per-field id would repeat across
                   them — clicking one card's label would focus another card's box. -->
              <div
                class="tsc-form-box"
                :class="{
                  'tsc-form-box--static': !formCanEdit,
                  'tsc-form-box--error': formFieldInvalid(f),
                }"
              >
                <input
                  class="tsc-form-input"
                  type="text"
                  inputmode="numeric"
                  autocomplete="off"
                  :aria-label="f.label"
                  :value="formValue(f)"
                  :placeholder="f.placeholder"
                  :readonly="!formCanEdit || formBusy"
                  :data-testid="'tpg-card-form-input-' + gi + '-' + fi"
                  :style="placeholderVars(f)"
                  @input="onFormInput(f, $event)"
                >
                <!-- 🔴 The unit takes the row's own hint. It was the one key on the wire with no binding at all,
                     so an authored `unitLabelColor` reached the phone and not this screen. -->
                <span v-if="unitOf(f)" class="tsc-form-unit" :style="sx(f, 'unitLabel')">{{ unitOf(f) }}</span>
              </div>
            </div>
            <!-- 🔴 UNDER the box, because that is where the app puts it (`TaxFundField.note` — "an optional
                 grey explanatory line under the box"; on a tick row it becomes the ⓘ icon's text). It used to be
                 drawn FIRST here, over the label, on the reasoning that a caveat is more use before the number is
                 typed than after. That reasoning is fine and it is not ours to act on: the backend ships one
                 `note` key and the APP decides where it lands, so drawing it above only made this screen disagree
                 with every real phone. A caption that genuinely belongs above a box needs its own key. -->
            <div
              v-if="f.note && !isCheckboxField(f)"
              class="tsc-form-note"
              :data-testid="'tpg-card-form-note-' + gi + '-' + fi"
              :style="sx(f, 'note')"
            >{{ f.note }}</div>
            <!-- 🔴 The one thing this card must never do is accept a number it cannot send.
                 An unreadable box is dropped by the backend, its row is then read as one that
                 was never on screen, and the checkpoint still closes as answered — so without
                 this line the customer's amount vanishes with nothing to show for it. -->
            <div
              v-if="formFieldInvalid(f)"
              class="tsc-form-err"
              :data-testid="'tpg-card-form-error-' + gi + '-' + fi"
            >กรอกเป็นตัวเลขเท่านั้น (เว้นว่างได้ถ้าไม่มีรายการนี้)</div>
          </div>
        </div>

        <div
          v-if="submitLabel || secondaryLabel"
          class="tsc-form-actions"
          data-testid="tpg-card-form-actions"
        >
          <!-- 🔴 Both buttons post the SAME set of boxes (see `formSubmissionValues`).
               "ไม่มีรายการเหล่านี้" is an ANSWER — "none of these apply to me" — not a
               cancel, so it is a submission with every box blank rather than a silence.
               🔴 Only the PRIMARY one waits for the boxes to be readable. The secondary
               sends them all blank, so there is nothing unreadable left to send — and it
               is the only way off a card whose one box the customer cannot get right. -->
          <button
            v-if="submitLabel"
            type="button"
            class="tsc-form-btn tsc-form-btn--primary"
            :class="{ 'tsc-form-btn--static': !formCanEdit }"
            :disabled="!formCanEdit || formBusy || formInvalidIds.length > 0"
            data-testid="tpg-card-form-submit"
            :style="mx(fx(data, 'submitFrameColor'), sx(data, 'submitLabel'))"
            @click="onFormSubmit"
          >{{ submitLabel }}</button>
          <button
            v-if="secondaryLabel"
            type="button"
            class="tsc-form-btn tsc-form-btn--secondary"
            :class="{ 'tsc-form-btn--static': !formCanEdit }"
            :disabled="!formCanEdit || formBusy"
            data-testid="tpg-card-form-secondary"
            :style="mx(
              fx(data, 'secondaryFrameColor'),
              fx(data, 'secondaryBorderColor', 'borderColor'),
              sx(data, 'secondaryLabel'),
            )"
            @click="onFormDecline"
          >{{ secondaryLabel }}</button>
        </div>
      </div>

      <!-- ── TAX_SUMMARY — the end-of-flow tax status card ────────────────────────────
           🔴 Its own branch because its payload shares NOTHING with the other cards: no
           `categories`, no `groups`. Everything here comes from named figures the calc
           layer produced (headline · stats · brackets · breakdown · allowances), which is
           why the backend renders it from a literal template rather than a repeat.
           🔴 Same rule as every other branch: no Thai label is invented here. A block
           whose key the template omitted (@omitWhenNull, because no formula produced the
           figure) simply does not render — never a 0, which the customer reads as a
           statement about their taxes. -->
      <!-- 🔴 `tsc-sum--bare` is what keeps the FILING CHOICE from arriving wrapped in an
           empty summary. Since 2026-08-27 that card ships as `TAX_SUMMARY` / `type3.1`
           carrying nothing but `data.filingOptions`, so it lands in THIS branch — and every
           section here is `v-if`-ed on its own data, which means nothing false renders, but
           the branch's own chrome (the tinted ground + the white `.tsc-sum-box`) would still
           frame the two tiles in a card that has no other content and whose payload asks for
           `mainFrameColor: #00000000`. The modifier drops that chrome so the tiles read as
           the standalone card they are, exactly as the TAX_FILING_COMPARE branch draws them. -->
      <div
        v-else-if="isSummary"
        class="tsc-body tsc-sum"
        :class="{ 'tsc-sum--bare': sumIsFilingOnly }"
        data-testid="tpg-card-summary"
      >
        <div v-if="sumHeadline" class="tsc-sum-hero" data-testid="tpg-card-sum-hero">
          <div
            v-if="sumHeadline.label"
            class="tsc-sum-hero-label"
            :style="sx(sumHeadline, 'label')"
          >{{ sumHeadline.label }}</div>
          <!-- 🔴 The unit comes from the PAYLOAD, not from this file. It used to be a literal ฿
               welded to the figure, which meant the one number the whole card is about could not
               be shown in anything else, and the template had no way to say so. `unit` is the
               vocabulary this card already speaks for its stats (`BAHT` / `PERCENT`) — the same
               reader, so the two cannot drift. A headline that authors no `unit` prints the bare
               figure rather than a currency this component chose for it. -->
          <div class="tsc-sum-hero-amt" :style="sx(sumHeadline, 'amount')">{{ fmtUnit(sumHeadline, sumHeadline.amount) }}</div>
          <div
            v-if="sumHeadline.caption"
            class="tsc-sum-hero-cap"
            :style="sx(sumHeadline, 'caption')"
          >{{ sumHeadline.caption }}</div>
        </div>

        <div v-if="sumStats.length" class="tsc-sum-stats" data-testid="tpg-card-sum-stats">
          <div v-for="(s, i) in sumStats" :key="i" class="tsc-sum-stat" :style="fx(s, 'rowFrameColor')">
            <div class="tsc-sum-stat-label" :style="sx(s, 'label')">{{ s.label }}</div>
            <!-- unit is the template's own vocabulary (PERCENT / BAHT) — see fmtStat -->
            <div class="tsc-sum-stat-val" :style="sx(s, 'value')">{{ fmtStat(s) }}</div>
          </div>
        </div>

        <!-- ── ขั้นบันได ── each row is one bracket: a bar whose length grows with the
             rate, and the tax that bracket actually produced. -->
        <section v-if="sumBrackets" class="tsc-sum-box" data-testid="tpg-card-sum-brackets">
          <!-- 🔴 The title is a BUTTON only when the payload says the block collapses. A heading
               that looks pressable and is not is worse than one that does not look pressable. -->
          <button
            v-if="sumBrackets.title && sumBrackets.collapsible"
            type="button"
            class="tsc-sum-box-title tsc-br-head"
            :aria-expanded="bracketsOpen ? 'true' : 'false'"
            data-testid="tpg-card-sum-brackets-toggle"
            :style="sx(sumBrackets, 'title')"
            @click="toggle('b')"
          >
            <span>{{ sumBrackets.title }}</span>
            <v-icon class="tsc-chev" :class="{ 'tsc-chev--open': bracketsOpen }" size="18" icon="mdi-chevron-down" />
          </button>
          <h4
            v-else-if="sumBrackets.title"
            class="tsc-sum-box-title"
            :style="sx(sumBrackets, 'title')"
          >{{ sumBrackets.title }}</h4>

          <!-- 🔴 PER FILER (type3.2 — a couple filing SEPARATELY): `filers[]` carries one ladder
               each and the block-level `rows` / `columns` are absent. Both shapes iterate ONE list
               (`sumBracketGroups`) so the table markup exists once; the legacy shape is the single
               group with no `filer`, which keeps `bracketsOpen` and the bar chart exactly as before.
               The 4-entry `columns` (title + one per cell) is drawn once, above the groups. -->
          <div
            v-if="sumBracketFilers.length && sumBracketColumns.length"
            class="tsc-br-filer-cols"
            :style="mx(sumBracketFilerVars, sx(sumBrackets, 'columns'))"
          >
            <span
              v-for="(c, ci) in sumBracketColumns"
              :key="ci"
              :class="ci === 0 ? 'tsc-br-filer-title' : 'tsc-br-filer-cell'"
            >{{ c }}</span>
          </div>

          <div
            v-for="(g, fi) in sumBracketGroups"
            :key="g.key"
            :class="g.filer ? 'tsc-sum-filer' : null"
            :data-testid="g.filer ? 'tpg-card-sum-brk-filer-' + fi : null"
          >
            <button
              v-if="g.filer"
              type="button"
              class="tsc-br-filer-head"
              :aria-expanded="isOpen(g.key) ? 'true' : 'false'"
              :data-testid="'tpg-card-sum-brk-filer-toggle-' + fi"
              :style="sumBracketFilerVars"
              @click="toggle(g.key)"
            >
              <span class="tsc-br-filer-title" :style="sx(g.filer, 'title')">{{ g.filer.title }}</span>
              <!-- unit is the cell's own vocabulary (PERCENT / BAHT) — see fmtUnit -->
              <span
                v-for="(cell, ci) in g.cells"
                :key="ci"
                class="tsc-br-filer-cell"
                :style="sx(cell, 'value')"
              >{{ fmtUnit(cell, cell && cell.value) }}</span>
              <v-icon class="tsc-chev" :class="{ 'tsc-chev--open': isOpen(g.key) }" size="18" icon="mdi-chevron-down" />
            </button>

            <v-expand-transition>
              <div v-show="g.filer ? isOpen(g.key) : bracketsOpen">
                <!-- 🔴 TWO SHAPES, and `displayStyle` is what picks. `TABLE` states the ladder as
                     a grid — rate, the income band it covers, the tax it produced — which is the
                     shape that can carry `columns[]` headers. Anything else keeps the bar chart
                     this block has always drawn. The key was authored and read by nothing, so a
                     card asking for TABLE silently got bars.
                     🔴 A FILER's ladder is always the table: `displayStyle` is not authored per filer. -->
                <table v-if="g.filer || sumBracketsIsTable" class="tsc-sum-tbl tsc-br-tbl">
                  <thead v-if="g.columns.length">
                    <tr>
                      <th
                        v-for="(c, ci) in g.columns"
                        :key="ci"
                        :class="brColClass(ci)"
                        :style="sx(g.hints, 'columns')"
                      >{{ c }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(r, i) in g.rows" :key="i" :style="fx(r, 'rowFrameColor')">
                      <!-- 🔴 NO `barStyle` here: in a table the rate is a chip, not a bar, so
                           `pillColor` is the whole fill and finally means what it says. In the bar
                           shape below it has to merge under a gradient and barely shows. -->
                      <!-- 🔴 The header's alignment first, the cell's own hints second — so a column
                           the author centred is centred all the way down, and a cell that states its
                           own `…TextAlign` still overrides it. -->
                      <td :class="brColClass(0)" :style="colAlign(g.hints)">
                        <span
                          class="tsc-br-chip"
                          :style="mx(fx(r, 'pillColor'), sx(r, 'rate'))"
                        >{{ rateTextOf(r) }}</span>
                      </td>
                      <td
                        :class="brColClass(1)"
                        :style="mx(colAlign(g.hints), sx(r, 'range'))"
                      >{{ r.rangeLabel }}</td>
                      <td
                        :class="brColClass(2)"
                        :style="mx(colAlign(g.hints), sx(r, 'taxAmount'))"
                      >฿{{ fmtBaht(r.taxAmount) }}</td>
                    </tr>
                  </tbody>
                </table>

                <div
                  v-for="(r, i) in (g.filer || sumBracketsIsTable ? [] : g.rows)"
                  :key="i"
                  class="tsc-br-row"
                  :style="fx(r, 'rowFrameColor')"
                >
                  <!-- 🔴 `barStyle(i)` FIRST and the hints after it: the bar's WIDTH is arithmetic on
                       the rate and is not a hint anyone may override, while `pillColor` is the fill
                       and `rateColor`/`rateFontSize` the text on it. Merging in this order keeps a
                       palette from ever changing how long a bracket's bar is. -->
                  <span
                    class="tsc-br-pill"
                    :class="{ 'tsc-br-pill--zero': !Number(r.ratePercent) }"
                    :style="mx(barStyle(i), fx(r, 'pillColor'), sx(r, 'rate'))"
                  >{{ rateTextOf(r) }}</span>
                  <!-- 🔴 The band this row covers ("750,001 - 1,000,000"). It is authored on every row
                       and was never drawn — the ladder said which RATE applied and how much tax it
                       produced, but not what income it applied to, which is the one thing a reader is
                       checking. Its own hints (`rangeColor` / `rangeFontSize`), like every other run. -->
                  <span
                    v-if="r.rangeLabel"
                    class="tsc-br-range"
                    :style="sx(r, 'range')"
                  >{{ r.rangeLabel }}</span>
                  <span class="tsc-br-amt" :style="sx(r, 'taxAmount')">฿{{ fmtBaht(r.taxAmount) }}</span>
                </div>
              </div>
            </v-expand-transition>
          </div>
        </section>

        <!-- ── รายละเอียดสถานะภาษี ── sections collapse; the first one opens with a
             per-person table underneath. -->
        <section v-if="sumBreakdown" class="tsc-sum-box" data-testid="tpg-card-sum-breakdown">
          <h4
            v-if="sumBreakdown.title"
            class="tsc-sum-box-title"
            :style="sx(sumBreakdown, 'title')"
          >{{ sumBreakdown.title }}</h4>
          <!-- 🔴 PER FILER (type3.2 — a couple filing SEPARATELY): `filers[]` carries one breakdown
               each — label, caption, net income and its own `sections[]` — and the block-level
               `sections` / `footer` are absent. Both shapes iterate ONE list (`sumSectionGroups`)
               so the section markup exists once; the legacy shape is the single group with no
               `filer`, whose open keys and test ids come out exactly as they always did. -->
          <div
            v-for="(g, fi) in sumSectionGroups"
            :key="g.key || 'all'"
            :class="g.filer ? 'tsc-sum-filer' : null"
            :data-testid="g.filer ? 'tpg-card-sum-filer-' + fi : null"
            :style="fx(g.filer, 'rowFrameColor')"
          >
            <button
              v-if="g.filer"
              type="button"
              class="tsc-sum-filer-head"
              :aria-expanded="isOpen(g.key) ? 'true' : 'false'"
              :data-testid="'tpg-card-sum-filer-toggle-' + fi"
              @click="toggle(g.key)"
            >
              <span class="tsc-sum-filer-text">
                <span class="tsc-sum-sec-label" :style="sx(g.filer, 'label')">{{ g.filer.label }}</span>
                <span
                  v-if="g.filer.caption"
                  class="tsc-sum-filer-cap"
                  :style="sx(g.filer, 'caption')"
                >{{ g.filer.caption }}</span>
              </span>
              <span class="tsc-sum-sec-amt" :style="sx(g.filer, 'amount')">฿{{ fmtBaht(g.filer.amount) }}</span>
              <v-icon
                class="tsc-chev"
                :class="{ 'tsc-chev--open': isOpen(g.key) }"
                size="18"
                icon="mdi-chevron-down"
              />
            </button>

            <v-expand-transition>
              <div v-show="!g.filer || isOpen(g.key)">
                <div
                  v-for="(sec, i) in g.sections"
                  :key="i"
                  class="tsc-sum-sec"
                  :data-testid="'tpg-card-sum-sec-' + g.tid + i"
                  :style="fx(sec, 'rowFrameColor')"
                >
                  <button
                    type="button"
                    class="tsc-sum-sec-head"
                    :aria-expanded="isOpen(secKey(g, i)) ? 'true' : 'false'"
                    :data-testid="'tpg-card-sum-sec-toggle-' + g.tid + i"
                    @click="toggle(secKey(g, i))"
                  >
                    <span class="tsc-sum-sec-label" :style="sx(sec, 'label')">{{ sec.label }}</span>
                    <span
                      class="tsc-sum-sec-amt"
                      :class="{ 'tsc-sum-accent': sec.accent }"
                      :style="sx(sec, 'amount')"
                    >
                      ฿{{ fmtBaht(sec.amount) }}
                    </span>
                    <v-icon
                      class="tsc-chev"
                      :class="{ 'tsc-chev--open': isOpen(secKey(g, i)) }"
                      size="18"
                      icon="mdi-chevron-down"
                    />
                  </button>

                  <v-expand-transition>
                    <div v-show="isOpen(secKey(g, i))" class="tsc-sum-sec-body">
                      <!-- 🔴 A section body is a list of BLOCKS, each one table. The legacy shape is
                           the single block with no `group` — its table sits where it always did — and
                           a deduction section that authors `groups[]` INSTEAD of `items[]` is one
                           block per group, each under its own collapsible head with its own columns
                           and an optional notes line beneath them. ONE copy of the table markup
                           serves both: `sectionBlocks` decides, the markup only iterates. -->
                      <div
                        v-for="blk in sectionBlocks(sec, g.filer)"
                        :key="blk.key"
                        :class="blk.group ? 'tsc-sum-group' : null"
                        :style="blk.group ? mx(fx(blk.group, 'rowFrameColor'), groupGap(sec, blk.gi)) : null"
                        :data-testid="blk.group ? 'tpg-card-sum-group-' + g.tid + i + '-' + blk.gi : null"
                      >
                        <button
                          v-if="blk.group"
                          type="button"
                          class="tsc-sum-group-head"
                          :style="groupIndent(sec)"
                          :aria-expanded="isOpen(secGroupKey(g, i, blk.gi)) ? 'true' : 'false'"
                          :data-testid="'tpg-card-sum-group-toggle-' + g.tid + i + '-' + blk.gi"
                          @click="toggle(secGroupKey(g, i, blk.gi))"
                        >
                          <span class="tsc-sum-sec-label" :style="sx(blk.group, 'label')">{{ blk.group.label }}</span>
                          <span
                            class="tsc-sum-sec-amt"
                            :class="{ 'tsc-sum-accent': blk.group.accent }"
                            :style="sx(blk.group, 'amount')"
                          >
                            ฿{{ fmtBaht(blk.group.amount) }}
                          </span>
                          <v-icon
                            class="tsc-chev"
                            :class="{ 'tsc-chev--open': isOpen(secGroupKey(g, i, blk.gi)) }"
                            size="18"
                            icon="mdi-chevron-down"
                          />
                        </button>

                        <!-- No expand transition on the group's table: `v-show` on a `<table>` is the
                             same plain collapse the item rows already use, and a height animation
                             on a table element is the one thing browsers will not do cleanly. -->
                        <table v-show="!blk.group || isOpen(secGroupKey(g, i, blk.gi))" class="tsc-sum-tbl">
                          <thead v-if="blk.columns.length">
                            <tr>
                              <!-- 🔴 The columns' hints come off the SECTION, not off the breakdown
                                   block: `columnsColor` exists on both and the app's mock authors the
                                   per-section one, so a section that overrides it must win here too.
                                   🔴 …and so does the section's own `columns[]` ARRAY — the allowance
                                   section heads its first column "ประเภทลดหย่อน" where the income one
                                   says "ประเภทรายได้". `columnsOf` picks; the block list is the fallback.
                                   A GROUP's own list and hints sit on top of all of that. -->
                              <th
                                v-for="(c, ci) in blk.columns"
                                :key="ci"
                                :class="{ 'tsc-sum-th-num': ci > 0 }"
                                :style="mx(sx(sumBreakdown, 'columns'), sx(g.filer, 'columns'), sx(sec, 'columns'), sx(blk.group, 'columns'))"
                              >{{ c }}</th>
                            </tr>
                            <!-- 🔴 A second HEADER line, not a first body row: "(จำนวนที่ระบุ/เพดาน)"
                                 qualifies the column above it, so it sits inside `<thead>`, one cell
                                 per column, and takes the column's alignment. -->
                            <tr v-if="blk.columnNotes.length" class="tsc-sum-colnotes">
                              <th
                                v-for="(c, ci) in blk.columns"
                                :key="ci"
                                :class="{ 'tsc-sum-th-num': ci > 0 }"
                                :style="mx(sx(sec, 'columnNotes'), sx(blk.group, 'columnNotes'))"
                              >{{ noteText(blk.columnNotes[ci]) }}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <template v-for="(it, j) in blk.items" :key="j">
                              <tr :style="fx(it, 'rowFrameColor')">
                                <td :style="colAlign(sumBreakdown, g.filer, sec)">
                                  <!-- The §40 chip above the label — its own fill, its own text hints. -->
                                  <span
                                    v-if="it.tag"
                                    class="tsc-sum-tag"
                                    :style="mx(fx(it, 'tagFrameColor'), sx(it, 'tag'))"
                                  >{{ it.tag }}</span>
                                  <span class="tsc-sum-item-label" :style="sx(it, 'label')">{{ it.label }}</span>
                                  <!-- Under the label: the "used / cap" note and the "room left" pill,
                                       each only when the payload wrote one. -->
                                  <span
                                    v-if="noteText(it.labelNote)"
                                    class="tsc-sum-note"
                                    :style="noteStyle(it, 'labelNote')"
                                  >{{ it.labelNote }}</span>
                                  <span
                                    v-if="noteText(it.labelChip)"
                                    class="tsc-sum-chip"
                                    :style="chipStyle(it, 'labelChip')"
                                  >{{ it.labelChip }}</span>
                                </td>
                                <!-- 🔴 One cell per COLUMN, not per value: a row whose spouse figure
                                     has no formula yet carries null there, and the cell must still
                                     exist or the whole row shifts left under the wrong header.
                                     `valueNotes` / `valueChips` are index-paired with the columns the
                                     same way — an entry of "" is "nothing for this column". -->
                                <td
                                  v-for="(c, ci) in blk.columns.slice(1)"
                                  :key="ci"
                                  class="tsc-sum-td-num"
                                  :style="mx(colAlign(sumBreakdown, g.filer, sec), sx(it, 'values'))"
                                >
                                  {{ fmtBaht(valueAt(it, ci)) }}
                                  <span
                                    v-if="noteAt(it, 'valueNotes', ci)"
                                    class="tsc-sum-note"
                                    :style="noteStyle(it, 'valueNote')"
                                  >{{ noteAt(it, 'valueNotes', ci) }}</span>
                                  <span
                                    v-if="noteAt(it, 'valueChips', ci)"
                                    class="tsc-sum-chip"
                                    :style="chipStyle(it, 'valueChip')"
                                  >{{ noteAt(it, 'valueChips', ci) }}</span>
                                </td>
                              </tr>

                              <!-- What the row's income cost to earn. A row of its own because it carries
                                   a figure PER COLUMN, so it has to line up under the same headers. -->
                              <tr v-if="hasRow(it, 'expense')" class="tsc-sum-exp" :style="fx(it.expense, 'rowFrameColor')">
                                <td :style="mx(colAlign(sumBreakdown, g.filer, sec), sx(it.expense, 'label'))">{{ it.expense.label }}</td>
                                <td
                                  v-for="(c, ci) in blk.columns.slice(1)"
                                  :key="ci"
                                  class="tsc-sum-td-num"
                                  :style="mx(colAlign(sumBreakdown, g.filer, sec), sx(it.expense, 'values'))"
                                >{{ sumSignedAt(it.expense, ci) }}</td>
                              </tr>

                              <!-- 🔴 The item's own sub-rows — numbered `children[]` (each with its own
                                   expense) and then the grouped deduction's `subItems[]` (notes and
                                   chips, indented by the item's `subItemsIndent`). They collapse
                                   independently of the section (`blockItemKey`), because the payload
                                   says so: `expanded` sits on the ITEM, not on the section. -->
                              <template v-if="isOpen(blockItemKey(g, i, blk, j))">
                                <template v-for="sr in subRowsOf(it)" :key="sr.key">
                                  <tr :class="sr.sub ? 'tsc-sum-sub' : 'tsc-sum-child'" :style="fx(sr.row, 'rowFrameColor')">
                                    <td :style="mx(colAlign(sumBreakdown, g.filer, sec), sr.sub ? subIndent(it) : null)">
                                      <span
                                        v-if="sr.row.circleText"
                                        class="tsc-sum-circle"
                                        :style="mx(fx(sr.row, 'circleColor'), sx(sr.row, 'circleText'))"
                                      >{{ sr.row.circleText }}</span>
                                      <span :style="sx(sr.row, 'label')">{{ sr.row.label }}</span>
                                      <span
                                        v-if="noteText(sr.row.labelNote)"
                                        class="tsc-sum-note"
                                        :style="noteStyle(sr.row, 'labelNote')"
                                      >{{ sr.row.labelNote }}</span>
                                      <span
                                        v-if="noteText(sr.row.labelChip)"
                                        class="tsc-sum-chip"
                                        :style="chipStyle(sr.row, 'labelChip')"
                                      >{{ sr.row.labelChip }}</span>
                                    </td>
                                    <td
                                      v-for="(c, ci) in blk.columns.slice(1)"
                                      :key="ci"
                                      class="tsc-sum-td-num"
                                      :style="mx(colAlign(sumBreakdown, g.filer, sec), sx(sr.row, 'values'))"
                                    >
                                      {{ fmtBaht(valueAt(sr.row, ci)) }}
                                      <span
                                        v-if="noteAt(sr.row, 'valueNotes', ci)"
                                        class="tsc-sum-note"
                                        :style="noteStyle(sr.row, 'valueNote')"
                                      >{{ noteAt(sr.row, 'valueNotes', ci) }}</span>
                                      <span
                                        v-if="noteAt(sr.row, 'valueChips', ci)"
                                        class="tsc-sum-chip"
                                        :style="chipStyle(sr.row, 'valueChip')"
                                      >{{ noteAt(sr.row, 'valueChips', ci) }}</span>
                                    </td>
                                  </tr>
                                  <tr
                                    v-if="hasRow(sr.row, 'expense')"
                                    class="tsc-sum-exp tsc-sum-exp--child"
                                    :style="fx(sr.row.expense, 'rowFrameColor')"
                                  >
                                    <td :style="mx(colAlign(sumBreakdown, g.filer, sec), sx(sr.row.expense, 'label'))">{{ sr.row.expense.label }}</td>
                                    <td
                                      v-for="(c, ci) in blk.columns.slice(1)"
                                      :key="ci"
                                      class="tsc-sum-td-num"
                                      :style="mx(colAlign(sumBreakdown, g.filer, sec), sx(sr.row.expense, 'values'))"
                                    >{{ sumSignedAt(sr.row.expense, ci) }}</td>
                                  </tr>
                                </template>
                              </template>

                              <!-- The control that opens and closes those sub-rows: one centred chevron
                                   spanning the table, drawn only for an item that HAS any. -->
                              <tr v-if="subRowsOf(it).length" class="tsc-sum-childtoggle">
                                <td :colspan="Math.max(blk.columns.length, 1)">
                                  <button
                                    type="button"
                                    class="tsc-sum-childbtn"
                                    :aria-expanded="isOpen(blockItemKey(g, i, blk, j)) ? 'true' : 'false'"
                                    :aria-label="it.label"
                                    :data-testid="'tpg-card-sum-item-toggle-' + g.tid + i + blk.tid + '-' + j"
                                    @click="toggle(blockItemKey(g, i, blk, j))"
                                  >
                                    <v-icon
                                      class="tsc-chev"
                                      :class="{ 'tsc-chev--open': isOpen(blockItemKey(g, i, blk, j)) }"
                                      size="18"
                                      icon="mdi-chevron-down"
                                    />
                                  </button>
                                </td>
                              </tr>
                            </template>

                            <!-- 🔴 `values`, not `amount`: a total states one figure PER COLUMN exactly as
                                 the rows above it do, and the payload hints it as `valuesColor`. Reading
                                 `amountColor` here left the navy total bar unstyled while every row above
                                 it obeyed its author. `amount` stays as the fallback for older payloads.
                                 It is the SECTION's total, so it closes the section's last block only. -->
                            <tr v-if="sec.total && blk.last" class="tsc-sum-total" :style="fx(sec.total, 'rowFrameColor')">
                              <td :style="mx(colAlign(sumBreakdown, g.filer, sec), sx(sec.total, 'label'))">{{ sec.total.label }}</td>
                              <td
                                v-for="(c, ci) in blk.columns.slice(1)"
                                :key="ci"
                                class="tsc-sum-td-num"
                                :style="mx(colAlign(sumBreakdown, g.filer, sec), sx(sec.total, 'amount'), sx(sec.total, 'values'))"
                              >{{ fmtBaht(valueAt(sec.total, ci)) }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </v-expand-transition>
                </div>
              </div>
            </v-expand-transition>
          </div>

          <!-- 🔴 OUTSIDE the section loop and outside every section's collapse: this is the
               breakdown's own bottom line ("เงินได้สุทธิ"), the figure the tax is computed from.
               A reader who collapsed both sections must still see it. -->
          <div v-if="sumFooter" class="tsc-sum-foot" data-testid="tpg-card-sum-foot">
            <span class="tsc-sum-foot-label" :style="sx(sumFooter, 'label')">{{ sumFooter.label }}</span>
            <span class="tsc-sum-foot-amt" :style="sx(sumFooter, 'amount')">฿{{ fmtBaht(sumFooter.amount) }}</span>
          </div>
        </section>

        <!-- ── เพดานการลดหย่อน ── used / cap as a bar, plus what is left. -->
        <section v-if="sumAllowances" class="tsc-sum-box" data-testid="tpg-card-sum-allowances">
          <h4
            v-if="sumAllowances.title"
            class="tsc-sum-box-title"
            :style="sx(sumAllowances, 'title')"
          >{{ sumAllowances.title }}</h4>
          <div
            v-for="(a, i) in sumAllowanceItems"
            :key="i"
            class="tsc-alw"
            :data-testid="'tpg-card-sum-alw-' + i"
            :style="fx(a, 'rowFrameColor')"
          >
            <div class="tsc-alw-title" :style="sx(a, 'title')">{{ a.title }}</div>
            <div class="tsc-alw-track"><span class="tsc-alw-fill" :style="capStyle(a)" /></div>
            <!-- One line, one hint pair: `usedCap` covers the whole "ใช้ X / เพดาน Y" run,
                 which is how the app's model names it. -->
            <div
              class="tsc-alw-meta"
              :style="sx(a, 'usedCap')"
            >ใช้ ฿{{ fmtBaht(a.used) }} / เพดาน ฿{{ fmtBaht(a.cap) }}</div>
            <div class="tsc-alw-left">
              <span :style="sx(a, 'remainingLabel')">{{ a.remainingLabel }}</span>
              <span
                class="tsc-alw-left-amt"
                :style="sx(a, 'remaining')"
              >เหลือ ฿{{ fmtBaht(a.remaining) }}</span>
            </div>
          </div>
        </section>

        <!-- ── เลือกเพื่อคำนวณ วางแผนภาษี — the filing tiles, LAST (design 2.3b1 merges the
             two cards into one, the choice sitting under the figures it is made from).
             🔴 Same markup and the same `.tsc-fc-*` classes as the standalone
             TAX_FILING_COMPARE branch below, because this IS that card's payload
             (`data.filingOptions` is its `data`, hung here by the backend). Restyling it for
             surface would let the badge — the recommendation itself — end up drawn one way
             inside the summary and another way on its own.

             🔴 THE TILE BELOW AND THE ONE IN THE NEXT BRANCH ARE ONE MARKUP, COPIED. Vue
             cannot include a template fragment twice and `<style scoped>` reaches only the
             ROOT element of a child component, so pulling the tile into its own SFC would
             leave every `.tsc-fc-*` rule below matching nothing. What CAN be shared is
             shared: `filingTiles` reads the option list for both lanes, `fcDetails` /
             `fcDetailAmount` / `toneClass` decide every detail row for both. If you edit one
             tile, edit the other in the same breath — the two lanes are the SAME card. -->
        <section v-if="sumFilingOptions.length" class="tsc-sum-box" data-testid="tpg-card-sum-filing">
          <h4 v-if="sumFiling.title" class="tsc-sum-box-title" :style="sx(sumFiling, 'title')">{{ sumFiling.title }}</h4>
          <div class="tsc-fc-grid tsc-sum-fc">
            <!-- 🔴 A TILE IS PRESSABLE ONLY WHEN THE CARD CAN SEND THE PRESS HOME — it carries a
                 `postback` block, the block carries an `id`, and this tile carries a `value`
                 (see `filingCanPick`). The read-only lane is the older card, whose tiles are a
                 picture and whose answer is a chip; making those clickable would collect a
                 choice this surface has nowhere to post. -->
            <div
              v-for="(o, i) in sumFilingOptions"
              :key="i"
              class="tsc-fc-opt"
              :class="{
                'tsc-fc-best': o.recommended,
                'tsc-fc-opt--pick': filingCanPick,
                'tsc-fc-opt--picked': filingCanPick && filingPicked === o.value,
              }"
              :data-testid="'tpg-card-sum-filing-opt-' + i"
              :role="filingCanPick ? 'button' : null"
              :tabindex="filingCanPick ? 0 : null"
              :aria-pressed="filingCanPick ? String(filingPicked === o.value) : null"
              :style="fx(o, 'rowFrameColor')"
              @click="onFilingPick(o)"
              @keydown.enter.prevent="onFilingPick(o)"
              @keydown.space.prevent="onFilingPick(o)"
            >
              <div v-if="o.badge" class="tsc-fc-badge" :style="sx(o, 'badge')">{{ o.badge }}</div>
              <div class="tsc-fc-label" :style="sx(o, 'title')">{{ o.title }}</div>
              <!-- What the big figure IS ("ภาษีที่ต้องจ่าย"). Authored copy, so no fallback:
                   a tile that sends none simply puts its amount straight under the title. -->
              <div v-if="o.subtitle" class="tsc-fc-sub" :style="sx(o, 'subtitle')">{{ o.subtitle }}</div>
              <div class="tsc-fc-amt" :style="sx(o, 'amount')">฿{{ fmtBaht(o.amount) }}</div>
              <!-- 🔴 What the tile's amount is MADE OF — "ผู้ยื่นหลัก / คู่สมรส" on the split
                   tile, "ต้องจ่ายเพิ่ม" on the losing one. The option's own `dividerColor`
                   recolours the rule that separates them from the figure above. -->
              <div
                v-if="fcDetails(o).length"
                class="tsc-fc-det"
                :style="fx(o, 'dividerColor', 'borderTopColor')"
              >
                <div
                  v-for="(d, di) in fcDetails(o)"
                  :key="di"
                  class="tsc-fc-det-row"
                  :data-testid="'tpg-card-sum-filing-opt-' + i + '-det-' + di"
                >
                  <span class="tsc-fc-det-label" :style="sx(d, 'label')">{{ d.label }}</span>
                  <span
                    class="tsc-fc-det-val"
                    :class="toneClass(d)"
                    :style="sx(d, 'value')"
                  >{{ fcDetailAmount(d) }}</span>
                </div>
              </div>
              <div v-if="o.note" class="tsc-fc-note" :style="sx(o, 'note')">{{ o.note }}</div>
            </div>
          </div>
          <!-- The confirm button — present only when the config authored one. 🔴 Drawn even
               where it cannot be pressed (history, a preview), so a read-only rendering is
               honest about the screen the customer actually saw; the same rule the
               simulation's own CTA follows. -->
          <button
            v-if="filingSubmitLabel"
            type="button"
            class="tsc-fc-cta"
            :class="{ 'tsc-fc-cta--static': !filingCanPick }"
            :disabled="!filingCanPick || busy || !filingPicked"
            data-testid="tpg-card-filing-submit"
            :style="filingSubmitStyle"
            @click="onFilingSubmit"
          >{{ filingSubmitLabel }}</button>
        </section>

        <!-- The button that ENDS the summary screen (`data.submitButton`, design 2026-09-10).
             🔴 NOT a postback: pressing it sends `submitButton.message` INTO THE CHAT as plain
             text, exactly as a quick reply does — the app's own rule for this card — so the
             server reads it as the customer's answer to the question on screen (CONFIRM_TAX_SUMMARY).
             Drawn even where it cannot be pressed, so a preview and an older turn are honest
             about the screen the customer holds. -->
        <button
          v-if="sumSubmitButton"
          type="button"
          class="tsc-fc-cta tsc-sum-cta"
          :class="{ 'tsc-fc-cta--static': !sumSubmitCanSend }"
          :disabled="!sumSubmitCanSend"
          data-testid="tpg-card-sum-submit"
          :style="sumSubmitStyle"
          @click="onSumSubmit"
        >{{ sumSubmitLabel }}</button>
      </div>

      <!-- ── TAX_FILING_COMPARE — "เลือกเพื่อคำนวณ วางแผนภาษี" ────────────────────────
           Two tiles, one badged. 🔴 The badge is NOT decided here: `option.recommended` comes
           from a formula (TAX_FILING_BEST_IS_SEPARATE), because the tie rule is policy and
           comparing the two amounts in the FE would be a second, silently diverging answer
           to which method wins.
           🔴 The tiles are READ-ONLY here. This renderer is the playground's preview of
           what the app draws; the customer answers with the chips under the bubble, so a
           clickable tile would be a second way to answer that this surface cannot submit.

           🔴 THIS BRANCH IS THE REPLAY LANE AND MUST STAY. Since 2026-08-27 the backend
           ships the filing choice as `TAX_SUMMARY` / `type3.1` with the tiles under
           `data.filingOptions`, so a LIVE card now takes the summary branch above. Every
           turn logged before that — TaxLogV2 history, an old playground transcript — still
           carries `TAX_FILING_COMPARE` with the tiles on `data.options`, and dropping this
           branch would turn each of them into "ยังไม่รองรับการ์ดชนิดนี้". Two shapes, one
           tile: the markup below is the twin of the one above (see the note there). -->
      <div v-else-if="isFilingCompare" class="tsc-body tsc-fc" data-testid="tpg-card-filing">
        <div v-if="data.title" class="tsc-fc-title" :style="sx(data, 'title')">{{ data.title }}</div>
        <div
          v-if="!fcOptions.length"
          class="tsc-empty"
          data-testid="tpg-card-empty"
        >— การ์ดนี้ไม่มีตัวเลือก —</div>
        <div v-else class="tsc-fc-grid">
          <div
            v-for="(o, i) in fcOptions"
            :key="i"
            class="tsc-fc-opt"
            :class="{ 'tsc-fc-best': o.recommended }"
            :data-testid="'tpg-card-filing-opt-' + i"
            :style="fx(o, 'rowFrameColor')"
          >
            <div v-if="o.badge" class="tsc-fc-badge" :style="sx(o, 'badge')">{{ o.badge }}</div>
            <div class="tsc-fc-label" :style="sx(o, 'title')">{{ o.title }}</div>
            <!-- What the big figure IS ("ภาษีที่ต้องจ่าย"). Authored copy, so no fallback:
                 a tile that sends none simply puts its amount straight under the title. -->
            <div v-if="o.subtitle" class="tsc-fc-sub" :style="sx(o, 'subtitle')">{{ o.subtitle }}</div>
            <div class="tsc-fc-amt" :style="sx(o, 'amount')">฿{{ fmtBaht(o.amount) }}</div>
            <!-- 🔴 What the tile's amount is MADE OF — "ผู้ยื่นหลัก / คู่สมรส" on the split
                 tile, "ต้องจ่ายเพิ่ม" on the losing one. The option's own `dividerColor`
                 recolours the rule that separates them from the figure above. -->
            <div
              v-if="fcDetails(o).length"
              class="tsc-fc-det"
              :style="fx(o, 'dividerColor', 'borderTopColor')"
            >
              <div
                v-for="(d, di) in fcDetails(o)"
                :key="di"
                class="tsc-fc-det-row"
                :data-testid="'tpg-card-filing-opt-' + i + '-det-' + di"
              >
                <span class="tsc-fc-det-label" :style="sx(d, 'label')">{{ d.label }}</span>
                <span
                  class="tsc-fc-det-val"
                  :class="toneClass(d)"
                  :style="sx(d, 'value')"
                >{{ fcDetailAmount(d) }}</span>
              </div>
            </div>
            <div v-if="o.note" class="tsc-fc-note" :style="sx(o, 'note')">{{ o.note }}</div>
          </div>
        </div>
      </div>

      <!-- ── TAX_SIMULATION — "เลือกหมวดลดหย่อนที่จะเพิ่ม" + "สรุปผลการจำลอง" ──────────
           Two stacked blocks in one payload: the slider picker, then the before/after
           result.

           🔴 THE SLIDERS ARE REAL when `interactive`, and DRAGGING SENDS NOTHING. The
           whole plan goes back in ONE postback when the customer presses
           "แสดงผลิตภัณฑ์" — the same contract as the mini-form card, where a screen is
           filled in privately and submitted once. Nothing is asked of the server while
           they are still deciding.

           🔴 What moves locally is only what the customer is directly setting — the
           slider amounts, the group's used-total and the "ลดหย่อนเพิ่มเติม" line. Every
           SERVER figure is dimmed and labelled the moment a slider leaves the position
           those figures were computed for (`simDirty`), because a tax bill sitting under
           a slider that has moved past it is a wrong number the customer has every reason
           to believe. This component never computes tax, a cap or a bracket. -->
      <div v-else-if="isSimulation" class="tsc-body tsc-sim" data-testid="tpg-card-simulation">
        <div v-if="simPicker" class="tsc-sim-block">
          <!-- ── the profile strip ─────────────────────────────────────────────
               The three figures the whole plan is measured against (annual income, net
               income before tax-saving products, top bracket). 🔴 `value` arrives ALREADY
               FORMATTED ("1,500,000", "25%") and is printed verbatim — the server decided
               the unit and the rounding, and a formatter here would either strip the "%" or
               re-group a figure that was grouped for the phone. -->
          <div
            v-if="simProfile.length"
            class="tsc-sim-profile"
            :style="simProfileStyle"
            data-testid="tpg-card-sim-profile"
          >
            <div
              v-for="(p, pi) in simProfile"
              :key="'p' + pi"
              class="tsc-sim-profile-cell"
              :style="simProfileCellStyle(pi)"
            >
              <span class="tsc-sim-profile-label" :style="sx(p, 'label')">{{ p.label }}</span>
              <b class="tsc-sim-profile-value" :style="sx(p, 'value')">{{ p.value }}</b>
            </div>
          </div>

          <div v-if="simPicker.title" class="tsc-sim-title" :style="sx(simPicker, 'title')">{{ simPicker.title }}</div>
          <div v-if="simPicker.subtitle" class="tsc-sim-sub" :style="sx(simPicker, 'subtitle')">{{ simPicker.subtitle }}</div>

          <!-- The colour key. 🔴 A swatch is the ONLY thing on this screen that explains why
               part of every bar is a colour the customer cannot move. Three swatch styles:
               `solid` (a filled block), `dashed` (the cap line), `stripes` (the over-zone).
               A solid row with no `swatchColor` falls back to the palette's `minimum`. -->
          <div v-if="simLegend.length" class="tsc-sim-legend" :style="simLegendStyle">
            <div
              v-for="(lg, li) in simLegend"
              :key="'lg' + li"
              class="tsc-sim-legend-row"
              :data-testid="'tpg-card-sim-legend-' + li"
            >
              <i class="tsc-sim-swatch" :class="'tsc-sim-swatch--' + simSwatchKind(lg)" :style="simSwatchStyle(lg)"></i>
              <div class="tsc-sim-legend-text">
                <b :style="sx(lg, 'label')">{{ lg.label }}</b><template v-if="lg.description"> — <em :style="sx(lg, 'description')">{{ lg.description }}</em></template>
              </div>
            </div>
          </div>

          <div
            v-if="!simGroups.length"
            class="tsc-empty"
            data-testid="tpg-card-empty"
          >— การ์ดนี้ไม่มีรายการให้ปรับ —</div>

          <!-- ── one group ──────────────────────────────────────────────────────
               A header (title · status pill · the group's own ceiling bar · notes), then
               one row per DRAWN bar. 🔴 Everything numeric in the header comes from
               `simGroupViews`, i.e. from the ALLOCATION — a bar whose `relation_cap` names
               another group spends its baht THERE first, and a header that summed only its
               own rows would show the same money under two ceilings on one screen. A hidden
               bar (`hidden: true`) is not drawn but still counts here. -->
          <div
            v-for="gv in simGroupViews"
            :key="'g' + gv.gi"
            class="tsc-sim-grp"
            :style="simGroupStyle"
            :data-testid="'tpg-card-sim-grp-' + gv.gi"
          >
            <div class="tsc-sim-ghead">
              <div class="tsc-sim-ghead-top">
                <div v-if="gv.g.title" class="tsc-sim-grp-title" :style="sx(gv.g, 'title')">{{ gv.g.title }}</div>
                <span
                  v-if="gv.badge"
                  class="tsc-sim-badge"
                  :class="simBadgeClass(gv.badge.kind)"
                  :style="simBadgeStyle(gv.badge.kind)"
                  :data-testid="'tpg-card-sim-gbadge-' + gv.gi"
                >{{ gv.badge.text }}</span>
              </div>
              <!-- The group bar: bought (dark) · planned (identity colour) · over (stripes), on a
                   scale that runs a little past the ceiling or the holding, whichever is taller;
                   the tinted zone past the ceiling says "buyable, not deductible". -->
              <div v-if="gv.bar" class="tsc-sim-gbar-row">
                <div class="tsc-sim-gtrack" :style="simCapTrackStyle(gv.g)">
                  <span
                    class="tsc-sim-gzone"
                    :style="{ left: gv.bar.capPct + '%', background: simColorOf(gv.key, 'maximumZone') }"
                  ></span>
                  <span
                    v-for="(seg, si) in gv.segments"
                    :key="'gs' + si"
                    class="tsc-sim-gseg"
                    :data-kind="seg.kind"
                    :style="seg.style"
                  ></span>
                </div>
                <span
                  class="tsc-sim-gamt"
                  :style="simCapUsedStyle(gv.g, gv.gi)"
                  :data-testid="'tpg-card-sim-cap-' + gv.gi"
                >{{ fmtBaht(gv.slot.deductible) }} / {{ fmtBaht(gv.slot.cap) }}<template v-if="gv.unit"> {{ gv.unit }}</template></span>
              </div>
              <!-- 🔴 Over the ceiling the note is REPLACED by `overCapNote`, not appended — the
                   cross-group sentence explains an arrangement that is no longer the customer's
                   problem. `{used}` / `{cap}` / `{excess}` come from the live figures. -->
              <div
                v-if="gv.note"
                class="tsc-sim-gnote"
                :class="{ 'tsc-sim-over-text': gv.slot.over > 0 }"
                :style="sx(gv.g, gv.slot.over > 0 ? 'overCapNote' : 'note')"
                :data-testid="'tpg-card-sim-note-' + gv.gi"
              >{{ gv.note }}</div>
              <!-- The lender's own explanation: how much of its bar went to fill another
                   ceiling (`{lent}`) and how much stayed home (`{home}`). Hidden while nothing
                   was lent — a sentence about 0 baht moving is noise. -->
              <div
                v-if="gv.spill"
                class="tsc-sim-gnote"
                :style="sx(gv.g, 'spillNote')"
                :data-testid="'tpg-card-sim-spill-' + gv.gi"
              >{{ gv.spill }}</div>
              <div
                v-for="(r, ri) in gv.remarks"
                :key="'rm' + ri"
                class="tsc-sim-remark"
                :style="sx(gv.g, 'remarks')"
              >{{ r }}</div>
            </div>

            <!-- ── one bar ──────────────────────────────────────────────────────
                 Geometry comes from `rowGeometry` (tested): the dark bought slice, the striped
                 part of the holding already past the ceiling, what was added, what was added
                 past the ceiling, the dashed cap line with its flag, the thumb on the total and
                 the two-tone tag above it.

                 🔴 A HAND-BUILT TRACK, not `<input type="range">` — the thumb has to sit ON the
                 value, the scale is the design's `scaleMax` rather than the ceiling, and the
                 whole strip is the pointer target. Pointer capture keeps a drag alive off the
                 row; `role="slider"` + arrow keys keep it announced and operable.

                 🔴 `min` is the cover the customer ALREADY holds and the thumb cannot go below
                 it; `sliderMax` (when shipped) is how far past the dashed ceiling it may go —
                 into the striped zone that is buyable and not deductible. -->
            <div
              v-for="row in gv.rows"
              :key="'i' + row.ii"
              class="tsc-sim-row"
              :data-testid="'tpg-card-sim-row-' + gv.gi + '-' + row.ii"
            >
              <div class="tsc-sim-row-head">
                <span class="tsc-sim-row-label" :style="sx(row.it, 'label')">{{ row.it.label }}</span>
                <span
                  v-if="row.badge"
                  class="tsc-sim-badge"
                  :class="simBadgeClass(row.badge.kind)"
                  :style="simBadgeStyle(row.badge.kind)"
                  :data-testid="'tpg-card-sim-badge-' + gv.gi + '-' + row.ii"
                >{{ row.badge.text }}</span>
              </div>

              <div
                class="tsc-sim-bar"
                :class="{
                  'tsc-sim-bar--static': !simRowEditable(row.it),
                  'tsc-sim-bar--dragging': simDragItemId === row.it.id,
                }"
                role="slider"
                :tabindex="simRowEditable(row.it) ? 0 : -1"
                :aria-label="row.it.label"
                :aria-valuemin="simMin(row.it)"
                :aria-valuemax="row.ariaMax"
                :aria-valuenow="row.geo.total"
                :aria-disabled="!simRowEditable(row.it)"
                :data-testid="'tpg-card-sim-range-' + gv.gi + '-' + row.ii"
                @pointerdown="onSimPointerDown(row.it, $event)"
                @pointermove="onSimPointerMove(row.it, $event)"
                @pointerup="onSimPointerUp($event)"
                @pointercancel="onSimPointerUp($event)"
                @keydown="onSimKey(row.it, $event)"
              >
                <!-- The live tag over the thumb — "bought | +added", the plan half turning
                     `maximum` past the ceiling. `showTag: false` drops it. -->
                <div
                  v-if="simShowTag(row.it)"
                  class="tsc-sim-tag"
                  :style="{ left: row.geo.tagPct + '%' }"
                >
                  <span class="tsc-sim-tag-a" :style="simTagStyle(row, gv, 'a')">{{ fmtBaht(row.geo.bought) }}</span>
                  <span class="tsc-sim-tag-b" :style="simTagStyle(row, gv, 'b')">{{ simTagPrefix(row.it) }}{{ fmtBaht(row.geo.added) }}</span>
                </div>
                <div class="tsc-sim-track" :style="simRailStyle(gv.g, row.it)">
                  <span
                    v-if="simShowOverZone(row.it) && row.geo.cap !== null"
                    class="tsc-sim-zone"
                    :style="{ left: row.geo.capPct + '%', background: simColorOf(gv.key, 'maximumZone') }"
                  ></span>
                  <span
                    v-for="(seg, si) in row.segments"
                    :key="'rs' + si"
                    class="tsc-sim-seg"
                    :data-kind="seg.kind"
                    :style="seg.style"
                  ></span>
                  <span
                    v-if="row.geo.divider"
                    class="tsc-sim-divider"
                    :style="simDividerStyle(row.it, row.geo)"
                  ></span>
                </div>
                <span
                  v-if="row.geo.cap !== null && simShowCapLine(row.it)"
                  class="tsc-sim-capline"
                  :style="simCapLineStyle(row.it, row.geo)"
                ></span>
                <span
                  v-if="row.geo.capFlagVisible && simShowCapLine(row.it)"
                  class="tsc-sim-capflag"
                  :class="{ 'tsc-sim-capflag--end': row.geo.capFlagAlign === 'end' }"
                  :style="mx({ left: row.geo.capPct + '%' }, sx(row.it, 'capFlag'))"
                >{{ simCapFlag(row.it, row.geo) }}</span>
                <span
                  class="tsc-sim-thumb"
                  :class="{ 'tsc-sim-thumb--plain': row.it.thumbGrip === false }"
                  :style="simThumbStyle(row, gv)"
                ><i></i><i></i><i></i></span>
                <span class="tsc-sim-boughtcap" :style="sx(row.it, 'boughtCaption')">
                  <i class="tsc-sim-dot" :style="{ background: simColorOf(gv.key, 'minimum') }"></i>{{ simBoughtCaption(row.it, row.geo) }}
                </span>
              </div>

              <!-- 🔴 THE BOX SHOWS THE INCREASE, not the holding: the question this screen asks
                   is "how much MORE would you buy". Typing moves the bar on every keystroke; the
                   string is left exactly as typed until commit (Enter / blur / a drag), when it
                   is snapped to `step` and regrouped. `readonly`, never `disabled`, on a locked
                   row — the app draws a figure the customer may read and not change. -->
              <div class="tsc-sim-figs">
                <label
                  class="tsc-sim-fig tsc-sim-fig--plan"
                  :class="{ 'tsc-sim-fig--live': simRowLive(row.it) }"
                  :style="simPlanBoxStyle(row.it)"
                >
                  <span class="tsc-sim-fig-lbl" :style="sx(row.it, 'planLabel')">
                    <i class="tsc-sim-dot" :style="{ background: simColorOf(gv.key, 'normal') }"></i>{{ simPlanLabel(row.it) }}
                  </span>
                  <input
                    class="tsc-sim-row-input"
                    type="text"
                    inputmode="numeric"
                    :value="simAddedText(row.it)"
                    :readonly="!simRowEditable(row.it)"
                    :aria-label="row.it.label"
                    :style="simInputStyle(row.it)"
                    :data-testid="'tpg-card-sim-box-' + gv.gi + '-' + row.ii"
                    @input="onSimBox(row.it, $event)"
                    @focus="onSimBoxFocus(row.it, $event)"
                    @change="onSimBoxCommit(row.it, $event)"
                    @keyup.enter="onSimBoxCommit(row.it, $event)"
                    @blur="onSimBoxBlur(row.it, $event)"
                  />
                </label>
                <div class="tsc-sim-fig tsc-sim-fig--total" :style="simCombinedBoxStyle(row.it)">
                  <span class="tsc-sim-fig-lbl" :style="sx(row.it, 'combinedLabel')">{{ simCombinedLabel(row.it) }}</span>
                  <b class="tsc-sim-fig-total" :style="simCombinedAmountStyle(row.it)">{{ fmtBaht(row.geo.total) }}</b>
                  <small v-if="simCombinedCap(row.it, row.geo)" class="tsc-sim-fig-cap" :style="sx(row.it, 'combinedCap')">{{ simCombinedCap(row.it, row.geo) }}</small>
                </div>
              </div>
            </div>
          </div>

          <!-- ── the three tiles ────────────────────────────────────────────────
               The design's dock, drawn INSIDE the card rather than fixed to the screen. All
               three move as a bar is dragged: DEDUCTIBLE and NEW_MONEY are the customer's own
               arithmetic, and TAX_SAVED is a PREVIEW walked up the ladder the server shipped
               in `picker.tax` — the em dash when it shipped none. The figures that count are
               still the server's, on the result card after submit. -->
          <div
            v-if="simStats.length"
            class="tsc-sim-tiles"
            :style="simStatsStyle"
            data-testid="tpg-card-sim-stats"
          >
            <div
              v-for="(s, si) in simStats"
              :key="'st' + si"
              class="tsc-sim-tile"
              :data-testid="'tpg-card-sim-stat-' + s.key"
            >
              <span class="tsc-sim-tile-lbl" :style="sx(s, 'label')">{{ s.label }}</span>
              <b
                class="tsc-sim-tile-amt"
                :class="{ 'tsc-sim-tile-amt--save': simStatKey(s) === 'TAX_SAVED' }"
                :style="simStatAmountStyle(s)"
              >{{ simStatText(s) }}</b>
            </div>
          </div>

          <!-- The button that COMMITS the plan — the round trip that produces the figures that
               count. 🔴 Drawn even where it cannot be pressed, so a read-only rendering is
               honest about the screen the customer actually sees on their phone. -->
          <button
            v-if="simGroups.length"
            type="button"
            class="tsc-sim-cta tsc-sim-cta--submit"
            :class="{ 'tsc-sim-cta--static': !simCanEdit }"
            :disabled="!simCanEdit || simBusy"
            data-testid="tpg-card-sim-submit"
            :style="simSubmitStyle"
            @click="onSimSubmit"
          >{{ simSubmitLabel }}</button>

          <div v-if="simFootnote" class="tsc-sim-footnote" :style="simFootnoteStyle">{{ simFootnote }}</div>

          <!-- Legacy: the server-computed read-out an OLDER payload may still carry. Greyed
               once a slider moves, because those figures belong to the plan before it did. -->
          <div v-if="simSummary.length" class="tsc-sim-stats" :class="{ 'tsc-sim-stale': simDirty }">
            <div
              v-for="(s, si) in simSummary"
              :key="'s' + si"
              class="tsc-sim-stat"
              :style="fx(s, 'rowFrameColor')"
            >
              <span :style="sx(s, 'label')">{{ s.label }}</span>
              <!-- `tone` is a MEANING the payload states; the hint decorates it. -->
              <span :class="simToneClass(s.tone)" :style="sx(s, 'amount')">{{ fmtBaht(s.amount) }} {{ moneyUnit(s) }}</span>
            </div>
          </div>

          <!-- 🔴 Said out loud rather than left to a grey tint — but only when there IS a server
               figure on this card to be stale about (a legacy read-out or a result block). The
               three tiles above are live and need no such note. -->
          <div v-if="simDirty && (simSummary.length || simResult)" class="tsc-sim-note" data-testid="tpg-card-sim-stale">
            {{ simBusy
              ? 'กำลังบันทึกแผนนี้…'
              : 'ตัวเลขจากเซิร์ฟเวอร์ด้านล่างคิดจากแผนก่อนปรับ — กด «' + simSubmitLabel + '» เพื่อคำนวณแผนนี้' }}
          </div>
        </div>

        <!-- ── สรุปผลการจำลอง ─────────────────────────────────────────────────
             Absent whenever the what-if run produced nothing: the backend omits the
             whole block rather than reprinting the STORED figures under the heading
             "หลังจำลอง", which would state the customer's current tax bill as the
             outcome of a plan they are considering. -->
        <div v-if="simResult" class="tsc-sim-block tsc-sim-result" :class="{ 'tsc-sim-stale': simDirty }">
          <div v-if="simResult.title" class="tsc-sim-title" :style="sx(simResult, 'title')">{{ simResult.title }}</div>
          <div v-if="simResult.subtitle" class="tsc-sim-sub" :style="sx(simResult, 'subtitle')">{{ simResult.subtitle }}</div>

          <!-- 🔴 TWO SHAPES for the same comparison, and the payload picks by which key it carries.
               `table` states it as ONE grid — label, before, after — which is what the app draws and
               what lets a reader follow a single row across. The `before`/`after` pair below states
               it as two stacked lists with a `›` between them; every card authored before `table`
               existed speaks that, so it stays as the fallback rather than being replaced. -->
          <table v-if="simTable" class="tsc-sim-tbl" :style="fx(simTable, 'frameColor')">
            <thead>
              <tr :style="simTableRule">
                <th :style="sx(simTable, 'header')">{{ simTable.labelHeader }}</th>
                <th class="tsc-num-cell" :style="sx(simTable, 'header')">{{ simTable.beforeHeader }}</th>
                <th class="tsc-num-cell" :style="sx(simTable, 'header')">{{ simTable.afterHeader }}</th>
              </tr>
            </thead>
            <tbody>
              <!-- 🔴 `before` / `after` are printed VERBATIM, never through `fmtBaht`. The rows mix
                   money ("560,000") with a band name ("ขั้น 4 (15%)"), and a formatter would either
                   mangle the second or have to guess which is which. The template already formatted
                   them; this is the same rule the `before`/`after` columns below follow. -->
              <tr
                v-for="(r, ri) in simTableRows"
                :key="'t' + ri"
                :style="mx(simTableRule, fx(r, 'rowFrameColor'))"
              >
                <td :style="sx(r, 'label')">{{ r.label }}</td>
                <td class="tsc-num-cell" :style="sx(r, 'before')">{{ r.before }}</td>
                <td class="tsc-num-cell" :style="sx(r, 'after')">{{ r.after }}</td>
              </tr>
            </tbody>
          </table>

          <div v-else class="tsc-sim-cmp">
            <div class="tsc-sim-col">
              <div class="tsc-sim-col-title" :style="sx(simBefore, 'title')">{{ simBefore.title }}</div>
              <div
                v-for="(r, ri) in simBefore.rows"
                :key="'b' + ri"
                class="tsc-sim-col-row"
                :style="fx(r, 'rowFrameColor')"
              >
                <div class="tsc-sim-col-label" :style="sx(r, 'label')">{{ r.label }}</div>
                <div class="tsc-sim-col-value" :style="sx(r, 'value')">{{ r.value }}</div>
              </div>
            </div>
            <div class="tsc-sim-arrow">›</div>
            <div class="tsc-sim-col tsc-sim-col-after">
              <div class="tsc-sim-col-title" :style="sx(simAfter, 'title')">{{ simAfter.title }}</div>
              <div
                v-for="(r, ri) in simAfter.rows"
                :key="'a' + ri"
                class="tsc-sim-col-row"
                :style="fx(r, 'rowFrameColor')"
              >
                <div class="tsc-sim-col-label" :style="sx(r, 'label')">{{ r.label }}</div>
                <div class="tsc-sim-col-value" :style="sx(r, 'value')">{{ r.value }}</div>
              </div>
            </div>
          </div>

          <div v-if="simSavings" class="tsc-sim-savings">
            <span :style="sx(simSavings, 'label')">{{ simSavings.label }}</span>
            <span
              class="tsc-sim-savings-amt"
              :style="sx(simSavings, 'amount')"
            >{{ fmtBaht(simSavings.amount) }} {{ moneyUnit(simSavings) }}</span>
          </div>

          <div v-if="simSelections.length" class="tsc-sim-sel">
            <div
              v-if="simResult.selectionsTitle"
              class="tsc-sim-sel-title"
              :style="sx(simResult, 'selectionsTitle')"
            >{{ simResult.selectionsTitle }}</div>
            <div
              v-for="(s, si) in simSelections"
              :key="'x' + si"
              class="tsc-sim-sel-row"
              :style="fx(s, 'rowFrameColor')"
            >
              <div>
                <div class="tsc-sim-sel-label" :style="sx(s, 'label')">{{ s.label }}</div>
                <div v-if="s.group" class="tsc-sim-sel-grp">{{ s.group }}</div>
              </div>
              <div class="tsc-sim-sel-amt" :style="sx(s, 'amount')">{{ fmtBaht(s.amount) }} {{ moneyUnit(s) }}</div>
            </div>
          </div>

          <!-- The button that ENDS the screen. Clickable only where a postback can
               actually be sent; elsewhere it is drawn so the layout is honest about
               what the app will show. -->
          <button
            v-if="simProductsLabel"
            type="button"
            class="tsc-sim-cta"
            :class="{ 'tsc-sim-cta--static': !simCanEdit }"
            :disabled="!simCanEdit || simBusy"
            data-testid="tpg-card-sim-products"
            :style="simProductsStyle"
            @click="onSimProducts"
          >{{ simProductsLabel }}</button>

          <!-- The button that goes BACK to the sliders. Drawn only when the card shipped one,
               which the backend does only on a result-only card whose author asked for it —
               so this surface never invents it. It carries its own `action`, unlike every
               other button here, and that string is echoed VERBATIM (see onSimEdit). -->
          <button
            v-if="simEditLabel"
            type="button"
            class="tsc-sim-cta tsc-sim-cta--secondary"
            :class="{ 'tsc-sim-cta--static': !simCanEdit }"
            :disabled="!simCanEdit || simBusy"
            data-testid="tpg-card-sim-edit"
            :style="simEditStyle"
            @click="onSimEdit"
          >{{ simEditLabel }}</button>
        </div>
      </div>

      <div v-else class="tsc-body">
        <!-- 🔴 Every string here is authored by the admin in `SummaryTemplateJson`.
             Nothing on this row may be hard-coded or defaulted in the FE: a missing
             key means the template did not say it, and inventing it would create a
             second source of wording. -->
        <div v-if="data.title" class="tsc-title" data-testid="tpg-card-title" :style="sx(data, 'title')">{{ data.title }}</div>

        <!-- 🔴 THREE children of one `space-between` row, not two — the app spreads the three labels
             across the width, and wrapping the last two in a right-hand span bunched them together with
             the unit hanging 6px off the section. `v-if` on all three so an absent one leaves no empty
             span holding a share of the row (the left one had none, and an absent `typeLabel` used to
             push the other two right). -->
        <div v-if="hasMetaRow" class="tsc-meta" data-testid="tpg-card-meta">
          <span v-if="data.typeLabel" :style="sx(data, 'typeLabel')">{{ data.typeLabel }}</span>
          <span v-if="data.sectionLabel" :style="sx(data, 'sectionLabel')">{{ data.sectionLabel }}</span>
          <span v-if="data.unitLabel" :style="sx(data, 'unitLabel')">{{ data.unitLabel }}</span>
        </div>

        <div
          v-if="!categories.length"
          class="tsc-empty"
          data-testid="tpg-card-empty"
        >— การ์ดนี้ไม่มีรายการ —</div>

        <!-- ── categories ─────────────────────────────────────────────────── -->
        <div
          v-for="(cat, i) in categories"
          :key="i"
          class="tsc-cat"
          :data-testid="'tpg-card-cat-' + i"
          :style="catStyle(cat)"
        >
          <button
            type="button"
            class="tsc-cat-head"
            :aria-expanded="isOpen(i) ? 'true' : 'false'"
            :data-testid="'tpg-card-cat-toggle-' + i"
            @click="toggle(i)"
          >
            <!-- 🔴 `code` is NOT in the TAX_INCOME_BREAKDOWN template at all, and in the
                 expense template it is an admin-authored constant. Defaulting it to '40'
                 would make the FE assert a มาตรา 40 grouping the config never claimed —
                 and would print "40" on a deduction card built from this same type. Show
                 the badge line only when the payload actually carries it. -->
            <span
              v-if="cat.code || cat.number"
              class="tsc-badge"
              :class="{ 'tsc-badge--num-only': !cat.code }"
              :style="fx(cat, 'frameColor')"
            >
              <span v-if="cat.code" class="tsc-badge-code" :style="sx(cat, 'code')">{{ cat.code }}</span>
              <span v-if="cat.number" class="tsc-badge-num" :style="sx(cat, 'number')">{{ cat.number }}</span>
            </span>

            <span class="tsc-cat-main">
              <span class="tsc-cat-title" :style="sx(cat, 'title')">{{ cat.title }}</span>
              <span v-if="cat.subtitle" class="tsc-cat-sub" :style="sx(cat, 'subtitle')">{{ cat.subtitle }}</span>
            </span>

            <span class="tsc-cat-amt">
              <span class="tsc-amt" :style="sx(cat, 'amount')">{{ fmtAmount(cat.amount) }}</span>
              <!-- 🔴 STRUCK THROUGH, because that is the claim it makes: this was the figure BEFORE the
                   deduction, and the one above it is what counts. Drawn plain it read as a second, equally
                   valid total. It also takes its own hints now (`grossAmountColor` / `…FontSize` /
                   `…TextAlign`) — it was the last text run on this card with no binding at all. -->
              <span
                v-if="showGross(cat)"
                class="tsc-amt-sub"
                :style="sx(cat, 'grossAmount')"
              >{{ fmtAmount(cat.grossAmount) }}</span>
            </span>

            <v-icon
              class="tsc-chev"
              :class="{ 'tsc-chev--open': isOpen(i) }"
              size="20"
              icon="mdi-chevron-down"
            />
          </button>

          <v-expand-transition>
            <div v-show="isOpen(i)" class="tsc-cat-body" :data-testid="'tpg-card-cat-body-' + i">
              <!-- 🔴 The rule that OPENS the body. The section's own `.tsc-sep` closes the item list;
                   without one here the first item sat flush against the header row it belongs under, and
                   an expanded category read as one block instead of a header plus its detail. -->
              <div class="tsc-sep tsc-sep--head"></div>
              <!-- TAX_INCOME_BREAKDOWN — flat items + one total -->
              <template v-if="isBreakdown">
                <div v-for="(it, j) in itemsOf(cat)" :key="j" class="tsc-line tsc-line--lead" :style="fx(it, 'rowFrameColor')">
                  <span class="tsc-line-label" :style="sx(it, 'label')">{{ it.label }}</span>
                  <span class="tsc-line-lead" aria-hidden="true"></span>
                  <span class="tsc-line-amt" :style="sx(it, 'amount')">{{ fmtAmount(it.amount) }}</span>
                </div>
                <template v-if="hasRow(cat, 'total')">
                  <div class="tsc-sep"></div>
                  <div class="tsc-line tsc-line--bold" :style="fx(cat.total, 'rowFrameColor')">
                    <span class="tsc-line-label" :style="sx(cat.total, 'label')">{{ cat.total.label }}</span>
                    <span class="tsc-line-amt" :style="sx(cat.total, 'amount')">{{ fmtAmount(cat.total.amount) }}</span>
                  </div>
                </template>
              </template>

              <!-- TAX_INCOME_EXPENSE — sections, each with its own gross/deduction/net -->
              <template v-else>
                <div v-for="(sec, k) in sectionsOf(cat)" :key="k" class="tsc-sec">
                  <!-- 🔴 `title` is null for a category with no subTypes: hide the whole
                       line, never print the word "null". -->
                  <div v-if="sec.title" class="tsc-sec-title" :style="sx(sec, 'title')">{{ sec.title }}</div>

                  <!-- 🔴 THE LEADER IS ON THE ITEM ROWS ONLY. The app joins a line's label to its amount
                       with a run of dashes, and does NOT do it on the gross / deduction / net rows below —
                       there the two are a statement, not an entry in a list. `aria-hidden` because a screen
                       reader must read "เงินเดือน 850,000.00", not a row of dashes between them. -->
                  <div v-for="(it, j) in itemsOf(sec)" :key="j" class="tsc-line tsc-line--lead" :style="fx(it, 'rowFrameColor')">
                    <span class="tsc-line-label" :style="sx(it, 'label')">{{ it.label }}</span>
                    <span class="tsc-line-lead" aria-hidden="true"></span>
                    <span class="tsc-line-amt" :style="sx(it, 'amount')">{{ fmtAmount(it.amount) }}</span>
                  </div>

                  <div class="tsc-sep"></div>

                  <template v-if="hasRow(sec, 'gross')">
                    <div class="tsc-line tsc-line--bold" :style="fx(sec.gross, 'rowFrameColor')">
                      <span class="tsc-line-label" :style="sx(sec.gross, 'label')">{{ sec.gross.label }}</span>
                      <span
                        class="tsc-line-amt"
                        :class="toneClass(sec.gross)"
                        :style="sx(sec.gross, 'amount')"
                      >{{ fmtAmount(sec.gross.amount) }}</span>
                    </div>
                    <!-- 🔴 อัตราเหมา ("หักเหมา 30%") อยู่ใน `gross.caption` ตามที่ mockup ฝั่ง mobile กำหนด
                         · `sec.caption` เป็นทรงเดิมที่เคยส่ง เก็บไว้อ่าน payload เก่าในหน้าประวัติ -->
                    <div
                      v-if="captionOf(sec)"
                      class="tsc-caption"
                      :style="sx(captionSource(sec), 'caption')"
                    >{{ captionOf(sec) }}</div>
                  </template>

                  <!-- 🔴 `deduction` / `net` are absent AS KEYS while calc has not written
                       back (@omitWhenNull drops the object). Render only when the key
                       exists — a missing key is not "0". -->
                  <div v-if="hasRow(sec, 'deduction')" class="tsc-line" :style="fx(sec.deduction, 'rowFrameColor')">
                    <span class="tsc-line-label" :style="sx(sec.deduction, 'label')">{{ sec.deduction.label }}</span>
                    <span
                      class="tsc-line-amt tsc-neg"
                      :style="sx(sec.deduction, 'amount')"
                    >{{ fmtAmount(sec.deduction.amount) }}</span>
                  </div>

                  <div v-if="hasRow(sec, 'net')" class="tsc-line tsc-line--bold" :style="fx(sec.net, 'rowFrameColor')">
                    <span class="tsc-line-label" :style="sx(sec.net, 'label')">{{ sec.net.label }}</span>
                    <span class="tsc-line-amt" :style="sx(sec.net, 'amount')">{{ fmtAmount(sec.net.amount) }}</span>
                  </div>
                </div>

                <template v-if="hasRow(cat, 'netTotal')">
                  <div class="tsc-rule"></div>
                  <div class="tsc-line tsc-line--bold" :style="fx(cat.netTotal, 'rowFrameColor')">
                    <span class="tsc-line-label" :style="sx(cat.netTotal, 'label')">{{ cat.netTotal.label }}</span>
                    <span
                      class="tsc-line-amt"
                      :style="sx(cat.netTotal, 'amount')"
                    >{{ fmtAmount(cat.netTotal.amount) }}</span>
                  </div>
                </template>
              </template>
            </div>
          </v-expand-transition>
        </div>
      </div>
    </template>

    <!-- ── footer: identity + the raw payload ───────────────────────────────
         🔴 This screen is where the ON-THE-WIRE format is checked by eye, so the
         real payload must always be reachable — including on the unknown branch. -->
    <div class="tsc-foot" data-testid="tpg-card-foot">
      <v-chip v-if="refId" size="x-small" variant="outlined" data-testid="tpg-card-refid">{{ refId }}</v-chip>
      <v-chip v-if="versionText" size="x-small" variant="outlined" data-testid="tpg-card-version">v{{ versionText }}</v-chip>
      <v-spacer />
      <!-- 🔴 Only on a KNOWN card: the unknown branch already prints its payload above
           and this button's panel is `v-show`n on `isKnownType`, so leaving it visible
           there gives the screen reader an `aria-expanded` that flips while nothing on
           screen moves. -->
      <v-btn
        v-if="isKnownType"
        icon="mdi-code-json"
        size="x-small"
        variant="text"
        :aria-expanded="showJson ? 'true' : 'false'"
        aria-label="ดู JSON ดิบของการ์ดนี้"
        title="ดู JSON ดิบของการ์ดนี้"
        data-testid="tpg-card-json-toggle"
        @click="showJson = !showJson"
      />
    </div>

    <v-expand-transition>
      <pre v-show="showJson && isKnownType" class="tsc-json" data-testid="tpg-card-json">{{ prettyRaw }}</pre>
    </v-expand-transition>
  </div>
</template>

<script>
// One tax summary card of the Tax Playground V2 chat column.
//
// The envelope (`{ type, version, refId, data }`) is written by an ADMIN in
// `SummaryTemplateJson` and rendered by `TaxCardTemplateV2` on the server, so this
// component treats every field as optional and every unknown `type` as expected
// input — a template typo must degrade to "raw JSON + a warning", never to a blank
// chat or a thrown render error.
//
// 🔴 THREE RULES THIS FILE EXISTS TO ENFORCE (source:
//    `TaxQuestionV2-IncomeCards-Config.md` §3.1/§4 — measured output, not a guess):
//
// 1. `amount` CAN BE null AND MUST NEVER RENDER AS 0. Today the calc phase never
//    writes back, so `headline.amount`, `stats[].amount` and `categories[].amount`
//    arrive as literal `null`. On a tax card "0" reads as "you have no income" —
//    a financial claim about the customer — while `null` only means "not known
//    yet". `formatAmount` therefore returns an em dash and there is deliberately no
//    `|| 0` anywhere in this file.
//
// 2. THE SAME FACT ARRIVES IN TWO SHAPES. `@omitWhenNull` can drop an *object*
//    (so `deduction` / `net` / `netTotal` vanish key and all) but cannot drop a
//    *scalar* (so `headline.amount` stays and is null). Rendering therefore asks
//    `hasRow()` (own-property check) for the object rows and `fmtAmount()` for the
//    scalars — the two are not interchangeable.
//
// 3. NO THAI LABEL FROM THE TEMPLATE IS HARD-CODED HERE. `typeLabel`,
//    `sectionLabel`, `unitLabel`, `title`, `code`, and every row label come from the
//    payload; a card that lacks one simply does not show it. (`บาท` on the headline
//    is the design's unit suffix, not an admin-authored label.)
//
// Rules 1 and 2 live in `../utils/taxCardFormat` — a template cannot be unit-tested,
// so the functions that decide "unknown vs zero" and "key absent vs key null" are kept
// outside the SFC and pinned by tests/taxCardFormat.test.mjs. Do not re-implement them
// here.
import { EM_DASH, amountSign, fillStyle, hintColor, formatAmount, hasObjectRow, isTruthy, mergeStyles, spacingStyle, textStyle } from '../utils/taxCardFormat';
// The simulation slider's arithmetic — extracted for the same reason as the three above:
// a template cannot be unit-tested, and every one of these decides a number the customer
// reads. Pinned by tests/taxSimulationCard.test.mjs. Do not re-implement any of it here.
import {
  addedValue as simAddedValue,
  allocate as simAllocate,
  badgeFor as simBadgeFor,
  deductibleTotal as simDeductibleTotal,
  fillNote as simFillNote,
  groupBar as simGroupBarOf,
  groupKeyOf as simGroupKeyOf,
  groupSlot as simGroupSlotOf,
  isDirty as simIsDirty,
  isEditable as simIsEditable,
  newMoneyTotal as simNewMoneyTotal,
  rowGeometry as simRowGeometry,
  scaleMax as simScaleMax,
  seedValues as simSeedValues,
  sliderMin as simSliderMin,
  sliderTop as simSliderTop,
  sliderValue as simSliderValue,
  snapAdded as simSnapAdded,
  snapValue as simSnapValue,
  stepOf as simStepOf,
  submissionValues as simSubmissionValues,
  taxSaved as simTaxSavedOf,
  valueFromAdded as simValueFromAdded,
  visibleItems as simVisibleItems,
} from '../utils/taxSimulationCard';
// The mini-form's wire rules — extracted for the same reason, and with more at stake: the
// three-state `values` contract (digits / '' / absent) is what a submission MEANS to the
// backend, and "send only the boxes that were filled" is a change that looks like a
// harmless optimisation and silently reopens the 2026-08-09 bug. Pinned by
// tests/taxShortFormCard.test.mjs. Do not re-implement any of it here.
import {
  fieldValue as formFieldValue,
  flattenFields as formFlattenFields,
  invalidFieldIds as formInvalidFieldIds,
  renderableGroups as formRenderableGroups,
  seedValues as formSeedValues,
  submissionValues as formSubmissionValuesOf,
} from '../utils/taxShortFormCard';

/**
 * The filing-comparison tiles, from whichever surface is carrying them — its own card
 * (`data`) or merged into the summary (`data.filingOptions`). ONE reader for both, because
 * are the same payload and a second copy of this filter would let the two disagree.
 *
 * 🔴 An option with no readable `amount` is DROPPED rather than shown as ฿0: this asks the
 * customer to commit to a filing method, and a tile priced at zero reads as "this one is
 * free". The backend refuses to build the comparison at all when a total is missing, so
 * reaching here means a hand-edited payload — for which the honest outcome is a card that
 * visibly lost an option, not one quoting a wrong price.
 */
function filingTiles(src) {
  const list = src && Array.isArray(src.options) ? src.options : [];
  return list.filter(o => o && Number.isFinite(Number(o.amount)));
}

/**
 * The lines UNDER one filing tile's amount — what that amount is made of ("ผู้ยื่นหลัก" +
 * "คู่สมรส" on the split tile) or what it costs against the other one ("ต้องจ่ายเพิ่ม").
 *
 * 🔴 The key is always present and MAY BE EMPTY (`details: []` — the backend keeps the shape
 * constant), so an absent list and an empty one are the same thing here: no rule, no rows.
 * 🔴 Unlike `filingTiles`, a row whose `value` is unreadable is KEPT and prints an em dash.
 * A tile is a price the customer is asked to commit to, so a missing one has to cost the
 * option its place; a breakdown line is a detail of a price that is already on screen, and
 * hiding it would quietly change what the tile claims to add up to.
 */
function filingDetails(option) {
  const list = option && Array.isArray(option.details) ? option.details : [];
  return list.filter(d => d && typeof d === 'object' && !Array.isArray(d));
}

/** Is `v` a plain object row — not null, not an array? The one test every list here filters by. */
function isPlainObject(v) {
  return Boolean(v) && typeof v === 'object' && !Array.isArray(v);
}

/**
 * One dp hint (`src[key]`, a non-negative finite number) as `{ [prop]: '<n>px' }`, or null.
 * dp → px 1:1 — the same honest approximation `textStyle` makes for sp. Null rather than `{}`
 * so a `:style` binding adds nothing when nothing was authored.
 */
function dpStyle(src, key, prop) {
  const v = src && typeof src === 'object' ? src[key] : undefined;
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? { [prop]: v + 'px' } : null;
}

/** Card types this renderer knows. Anything else falls through to the raw view. */
const CARD_TYPES = {
  TAX_INCOME_BREAKDOWN: 'breakdown',
  TAX_INCOME_EXPENSE: 'expense',
  // 🔴 การ์ดค่าลดหย่อน (item 467) **ประกาศตัวเป็น TAX_INCOME_EXPENSE / type6** ใน cardConfig ของมันเอง
  //    เพื่อให้แอปใช้ renderer เดียวกับการ์ดรายได้ ⇒ มันเข้าแถวบนไม่ใช่แถวนี้ · แถวนี้เหลือไว้อ่านการ์ดเก่า
  //    ที่ยัง declare TAX_ALLOWANCE (log / ประวัติเทิร์นก่อน 2026-08-05) และเดินสาย 'expense' เหมือนกัน เพราะ
  //    payload ทรงนี้เอาบรรทัดไปไว้ใน `sections[0].items` ไม่ใช่ `categories[].items` ⇒ อ่านด้วยสาย breakdown
  //    จะได้การ์ดเปล่า · การ์ด source = FIELD ไม่มี deduction/net — @omitWhenNull ตัดคีย์ทิ้ง สาย expense
  //    จึงไม่แสดงบรรทัดหักให้อยู่แล้ว
  TAX_ALLOWANCE: 'expense',
  // 🔴 A NEW KEY HERE IS ONLY HALF THE CHANGE — it must land on a template branch of its
  //    own. The renderer picks "breakdown vs everything else", so a type registered here
  //    with no branch falls into the `v-else` (expense) path, which reads
  //    `data.categories`. A TAX_AMOUNT_FORM has no `categories` at all, so it would draw
  //    a title with "— การ์ดนี้ไม่มีรายการ —" underneath: a plausible-looking EMPTY card
  //    instead of the honest "ยังไม่รองรับการ์ดชนิดนี้" block. See `isForm` below.
  TAX_AMOUNT_FORM: 'form',
  // 🔴 The end-of-flow tax status card. Registered WITH its own branch (see `isSummary`)
  //    because its payload has neither `categories` nor `groups` — falling through to the
  //    expense path would draw a title over "— การ์ดนี้ไม่มีรายการ —", a plausible-looking
  //    empty card instead of an honest one.
  TAX_SUMMARY: 'summary',
  // 🔴 The filing-method choice card. Own branch for the same reason as the two above: its
  //    payload is `data.options[]` — neither `categories` nor `groups` — so the fall-through
  //    path would draw a title over "— การ์ดนี้ไม่มีรายการ —".
  TAX_FILING_COMPARE: 'filing',
  // 🔴 The what-if screen. Own branch again: its payload is `data.picker` / `data.result`
  //    — no `categories`, no `groups` — so the fall-through path would draw a title over
  //    "— การ์ดนี้ไม่มีรายการ —", a plausible-looking empty card instead of an honest one.
  TAX_SIMULATION: 'simulation',
};

/** What the app prints inside a money box whose row authored no `unitLabel` — the card contract's own
 *  `DEFAULT_UNIT_LABEL`. Absent, null and blank all reach it, which is why the preview cannot treat a
 *  blank as "this row has no unit". */
const DEFAULT_UNIT_LABEL = 'บาท';

/** The simulation colours a payload may author, in the order the bands read left to right. */
const SIM_COLOR_KEYS = ['minimum', 'normal', 'maximum', 'maximumAlt', 'maximumZone', 'locked'];

/**
 * What each band is when NOBODY authored it — the app's own built-in palette, hex for hex.
 *
 * 🔴 These are the LAST layer of a three-layer lookup (`group.colors` ?? `data.colors` ??
 * here) that resolves FIELD BY FIELD, not object by object: a group that sends only `normal`
 * still inherits `minimum` and `maximum` from the card, and a card that sends none still
 * draws the phone's colours rather than this component's older greens and greys.
 */
const SIM_DEFAULT_COLORS = {
  normal: '#5C8FCB',      // what the customer adds — the plan slice, the thumb ring, the plan box
  minimum: '#1B3557',     // what they already hold — the locked slice
  maximum: '#D98B2B',     // past the ceiling — the over-thumb, the "เกินสิทธิ์" pill, stripe A
  maximumAlt: '#EAB569',  // stripe B of the over-zone pattern
  maximumZone: '#FBEEDC', // the tint from the ceiling to the end of the track
  locked: '#8A99A5',      // a thumb that cannot move: nothing added, holding already at the cap
};

/** The unfilled remainder of a ceiling bar, and the bare rail under every slider. */
const SIM_TRACK_COLOR = '#E3E7EE';

/** The dashed ceiling line on a bar, and the `dashed` legend swatch that explains it. */
const SIM_CAP_LINE_COLOR = '#6E7F8C';

/**
 * Which column of the bracket ladder holds the money — the third, because a ladder row is
 * always rate / range / amount. That is a fact about the MARKUP below, not about the payload,
 * which is why it is a constant here rather than derived from `columns.length`: the header list
 * is the author's copy and may be any length, while the row always has these three cells.
 */
const BRACKET_AMOUNT_COL = 2;

/**
 * `tone` is template knowledge ("this box is a deduction"), never customer data — a MEANING
 * the payload states, which this map turns into the one colour that meaning wears on this
 * card. The vocabulary is `DEFAULT` / `NEGATIVE` / `POSITIVE`; `DEFAULT` and anything
 * unrecognised answer no class at all, so the row keeps its block's own colour.
 *
 * 🔴 The same three words, the same two colours, as `simToneClass` — the simulation's rows
 * carry `tone` too, and a card that said "NEGATIVE" in orange in one block and in nothing in
 * another would be teaching the customer two meanings for one word. `simToneClass` keeps its
 * own class names only because those runs also set a weight; the colours are these.
 * 🔴 A hint always wins: `<name>Color` lands in an inline `:style`, so an author who coloured
 * the run explicitly is not overruled by the tone it also declared.
 */
const TONE_CLASS = {
  NEGATIVE: 'tsc-neg',
  POSITIVE: 'tsc-pos',
};

export default {
  name: 'TaxSummaryCardV2',
  props: {
    /** One envelope: `{ type, version, refId, data }`. `data` may be missing. */
    card: { type: Object, required: true },
    /** The raw JSON text of this one card, shown verbatim behind the JSON button. */
    raw: { type: String, default: '' },
    /**
     * May the customer ACT on this card? Only the newest turn's card on a live form is
     * true; an older turn is history and a completed form has nothing left to submit.
     * 🔴 False makes the sliders and the mini-form's boxes read-only rather than merely
     * inert: a control that moves but cannot be submitted collects an answer that goes
     * nowhere. It is also the only way to test a card left sitting in the transcript.
     */
    interactive: { type: Boolean, default: false },
    /**
     * Is this the CARD PREVIEW — an author's rendering of a card nobody has been sent?
     *
     * 🔴 It unlocks the simulation's sliders WITHOUT unlocking its buttons, which is the whole
     * distinction `interactive` cannot draw. On the phone the picker is presentation-only: the
     * thumbs, the ceiling bars and the running total move entirely in the client and NOTHING
     * is posted until the customer presses the CTA. An author who cannot drag is therefore not
     * previewing the card the customer gets — they are previewing its first frame — and the
     * three-colour ceiling bar, the borrowed slice and the over-ceiling swap are exactly the
     * things that only appear once something moves.
     *
     * 🔴 NOT a synonym for `interactive`. The buttons stay dead here, because a preview has
     * nowhere to post to and no turn to spend; and `simDirty` stays off, because there are no
     * server-computed figures on this surface to go stale.
     */
    preview: { type: Boolean, default: false },
    /** A submit is already in flight — sliders, boxes and buttons lock until the new card lands. */
    busy: { type: Boolean, default: false },
  },
  emits: ['card-submit'],
  data() {
    return {
      // index -> open?  (seeded from `category.expanded`, see `seedOpen`)
      open: {},
      showJson: false,
      /**
       * Slider id (= the deduction's FieldKey) → the amount the customer has dragged it to,
       * THIS render only. Re-seeded from the card whenever a new one arrives (see the
       * `cardSignature` watcher), which is what makes a server reply the end of a "dirty"
       * state rather than something the component has to reconcile.
       */
      simValues: {},
      /**
       * Slider id → the TEXT in its amount box, while that box has the caret.
       *
       * 🔴 The box's string and the figure behind it are ALLOWED TO DISAGREE while the
       * customer types, and this map is where the disagreement lives. Binding the box
       * straight to the formatted amount rewrites it on every keystroke — "2" becomes "2",
       * "20" becomes "20", "200" becomes "200", "2000" becomes "2,000" and the caret jumps a
       * character left mid-number. Separators and `step` snapping are applied on COMMIT
       * (Enter, blur or a drag), never per keystroke.
       */
      simText: {},
      /** Which amount box holds the caret, or null. `simText` is read for that row alone. */
      simFocusId: null,
      /**
       * The pointer id of a drag in flight, or null. Held so `pointermove` only follows the
       * pointer that actually started on the track — a second finger elsewhere on the card
       * must not move a thumb it never touched.
       */
      simDragId: null,
      /** The slider id of the drag in flight, or null — lights up that row's plan box. */
      simDragItemId: null,
      /**
       * Mini-form box id (= FieldKey) → the TEXT in it, THIS render only. Re-seeded from
       * the card whenever a new one arrives (see the `cardSignature` watcher), for the same
       * reason as `simValues`: a server reply ENDS the local edit rather than being
       * something this component has to reconcile against it.
       */
      formValues: {},
      /**
       * The filing tile the customer has tapped, as its `value` — THIS render only, re-seeded
       * from every new card (see the `cardSignature` watcher) for the same reason the sliders
       * and the mini-form boxes are: the server's reply is the END of a local choice, not
       * something this component has to reconcile against.
       */
      filingPicked: null,
    };
  },
  computed: {
    cardType() {
      return (this.card && this.card.type) || '';
    },
    isKnownType() {
      return Boolean(CARD_TYPES[this.cardType]);
    },
    isBreakdown() {
      return CARD_TYPES[this.cardType] === 'breakdown';
    },
    /** TAX_AMOUNT_FORM — a shape with `groups`, not `categories`; own template branch. */
    isForm() {
      return CARD_TYPES[this.cardType] === 'form';
    },
    /** TAX_SUMMARY — named figures rather than rows; own template branch. */
    isSummary() {
      return CARD_TYPES[this.cardType] === 'summary';
    },
    /** TAX_FILING_COMPARE — two priced options; own template branch. */
    isFilingCompare() {
      return CARD_TYPES[this.cardType] === 'filing';
    },
    /** The two tiles when this card IS the comparison. See `filingTiles` for the drop rule. */
    fcOptions() {
      return filingTiles(this.data);
    },
    /** TAX_SIMULATION — a slider picker and a before/after result; own template branch. */
    isSimulation() {
      return CARD_TYPES[this.cardType] === 'simulation';
    },
    simPicker() {
      return hasObjectRow(this.data, 'picker') ? this.data.picker : null;
    },
    /**
     * `data.colors` → CSS custom properties on the card box, so the simulation's bands can
     * be re-skinned from config without a build.
     *
     * `null` — no inline style at all — whenever the payload authored nothing usable, and
     * every `var(--tsc-sim-*, …)` in the stylesheet then falls back to the palette this
     * file has always used. That is the common case: `colors` ships only when an admin set
     * it, so a card from before 2026-08-20 must look exactly as it did.
     *
     * 🔴 Simulation only. The properties are read by `.tsc-sim-*` rules alone, but binding
     * them from any card's `data` would let a hand-edited payload of another type reach
     * into this component's palette — colours here are a drawing decision, never something
     * an arbitrary card gets to assert.
     *
     * 🔴 Every value goes through `hintColor`: an unset property falls back, a property set
     * to junk does not. A CSS custom property does not fall back when it holds junk —
     * `var(--x, #fff)` uses its fallback only while `--x` is UNSET, so a set-but-invalid
     * value makes the whole declaration invalid at computed-value time and the band loses
     * its colour entirely rather than reverting.
     */
    cardColorVars() {
      if (!this.isSimulation || !hasObjectRow(this.data, 'colors')) return null;
      const authored = this.data.colors;
      const vars = {};
      SIM_COLOR_KEYS.forEach((key) => {
        // 🔴 Through `hintColor`, like every other colour on every other card: it is the one
        // place that reorders Android's `#AARRGGBB` into CSS's `#RRGGBBAA`. Tested here with
        // a 6-digit-only regex, an alpha colour was simply dropped — the band lost the tint
        // the designer authored and nothing said so.
        const css = hintColor(authored[key]);
        if (css) vars['--tsc-sim-' + key] = css;
      });
      return Object.keys(vars).length ? vars : null;
    },
    /**
     * The whole card's own frame — `data.mainFrameColor`, which EVERY type in the app's
     * contract carries (type1 · type3 · type4 · type5 · type6 · type7 · type8 · type9 ·
     * type10), so unlike `cardColorVars` above it is not gated on one type.
     *
     * 🔴 Merged with the simulation variables rather than replacing them: they live on the
     * same root node and one `:style` binding wins outright. Nothing overlaps — one sets
     * `--tsc-sim-*` custom properties, the other a background — so the merge is safe in
     * either order and is written once here so no future hint has to remember.
     */
    cardStyle() {
      return mergeStyles(this.cardColorVars, fillStyle(this.data, 'mainFrameColor'));
    },
    /**
     * The slider groups. A group with no drawable slider is dropped rather than rendered
     * as an empty box — the backend already leaves such a group out, so reaching here
     * means a hand-edited payload, and an empty framed box reads as a section that failed
     * to load rather than one that was never offered.
     *
     * 🔴 Hidden bars are KEPT here: they are not drawn, but they still eat their group's
     * ceiling. `simGroupViews` is what leaves them out of the rows.
     */
    simGroups() {
      const list = (this.simPicker && Array.isArray(this.simPicker.groups)) ? this.simPicker.groups : [];
      return list
        .filter(g => g && Array.isArray(g.items))
        .map(g => ({ ...g, items: g.items.filter(it => it && Number.isFinite(Number(it.value))) }))
        .filter(g => g.items.length > 0);
    },
    simSummary() {
      const list = (this.simPicker && Array.isArray(this.simPicker.summary)) ? this.simPicker.summary : [];
      return list.filter(s => s && Number.isFinite(Number(s.amount)));
    },
    /** The colour key under the subtitle. Entries with no label are not a caption. */
    simLegend() {
      const list = (this.simPicker && Array.isArray(this.simPicker.legend)) ? this.simPicker.legend : [];
      return list.filter(l => l && String(l.label || '').trim() !== '');
    },
    simLegendStyle() {
      return this.dp(this.simPicker, 'legendMarginTop', 'marginTop');
    },
    /** The three figures at the top. A row with no label is not a figure anyone can read. */
    simProfile() {
      const list = (this.simPicker && Array.isArray(this.simPicker.profile)) ? this.simPicker.profile : [];
      return list.filter(p => p && String(p.label || '').trim() !== '');
    },
    simProfileStyle() {
      return mergeStyles(
        fillStyle(this.simPicker, 'profileFrameColor'),
        fillStyle(this.simPicker, 'profileBorderColor', 'borderColor'),
        this.dp(this.simPicker, 'profileCornerRadius', 'borderRadius'),
        this.dp(this.simPicker, 'profileMarginBottom', 'marginBottom'),
      );
    },
    /** The status-pill captions — `picker.badge`, or `{}` so every reader falls back. */
    simBadge() {
      const b = this.simPicker && this.simPicker.badge;
      return b && typeof b === 'object' ? b : {};
    },
    /** The tiles the payload asked for, in its order. A tile with no key cannot be filled. */
    simStats() {
      const list = (this.simPicker && Array.isArray(this.simPicker.stats)) ? this.simPicker.stats : [];
      return list.filter(s => s && String(s.key || '').trim() !== '');
    },
    simStatsStyle() {
      return mergeStyles(
        fillStyle(this.simPicker, 'statsFrameColor'),
        this.dp(this.simPicker, 'statsCornerRadius', 'borderRadius'),
        this.dp(this.simPicker, 'statsMarginTop', 'marginTop'),
        this.dp(this.simPicker, 'statsPaddingTop', 'paddingTop'),
        this.dp(this.simPicker, 'statsPaddingBottom', 'paddingBottom'),
        this.dp(this.simPicker, 'statsPaddingLeft', 'paddingLeft'),
        this.dp(this.simPicker, 'statsPaddingRight', 'paddingRight'),
      );
    },
    /** How far past the tallest figure every bar's scale runs — `picker.scaleHeadroom` / `dragHeadroom`. */
    simHeadroom() {
      return {
        scaleHeadroom: this.simPicker && this.simPicker.scaleHeadroom,
        dragHeadroom: this.simPicker && this.simPicker.dragHeadroom,
      };
    },
    /** The rule that opens every group, and the gap above it — `dividerColor` / `groupSpacing`. */
    simGroupStyle() {
      return mergeStyles(
        fillStyle(this.simPicker, 'dividerColor', 'borderTopColor'),
        this.dp(this.simPicker, 'groupSpacing', 'marginTop'),
      );
    },
    simFootnote() {
      const f = this.simPicker && this.simPicker.footnote;
      return f == null ? '' : String(f);
    },
    simFootnoteStyle() {
      return mergeStyles(textStyle(this.simPicker, 'footnote'), this.dp(this.simPicker, 'footnoteMarginTop', 'marginTop'));
    },
    /** Σ over every group of what counts — the DEDUCTIBLE tile. */
    simDeductible() {
      return simDeductibleTotal(this.simGroups, this.simLocal);
    },
    /** Σ added on the bars the customer can see — the NEW_MONEY tile. */
    simNewMoney() {
      return simNewMoneyTotal(this.simGroups, this.simLocal);
    },
    /**
     * The TAX_SAVED preview, or null when the server shipped no usable `picker.tax`.
     * 🔴 A PREVIEW walked up the server's own ladder; the figure that counts comes back on
     * the result card after submit. Null prints as the em dash, never as 0.
     */
    simTaxSaved() {
      return simTaxSavedOf(this.simPicker && this.simPicker.tax, this.simGroups, this.simLocal);
    },
    /**
     * Where every slider's baht actually lands — the whole card's allocation, computed once.
     *
     * 🔴 THIS, not a per-group sum, is what the ceiling bars and captions read. A slider whose
     * `relation_cap` names another group spends there first, and a bar that summed only its own
     * items would show the same money under two ceilings at once. `simLocal` is null on a card
     * that cannot be dragged (a log transcript, a history row), and `allocate` then reads each
     * bar's authored `value` — the holdings exactly as the server sent them.
     */
    simAlloc() {
      return simAllocate(this.simGroups, this.simLocal);
    },
    /** Group key → the group, so `simColorOf` can paint a slice in ITS OWN group's palette. */
    simGroupIndex() {
      const map = {};
      this.simGroups.forEach((g, gi) => { map[this.simGroupKey(g, gi)] = g; });
      return map;
    },
    /**
     * Everything the template needs per group and per DRAWN bar, computed once per render:
     * the allocation slot, the group bar, the pill, the notes, and for each visible row its
     * geometry, pill and slices. 🔴 One place, so the header and the rows under it can never
     * read two different allocations.
     */
    simGroupViews() {
      return this.simGroups.map((g, gi) => {
        const key = this.simGroupKey(g, gi);
        const slot = this.simSlot(g, gi);
        const bar = simGroupBarOf(slot);
        const badge = slot.cap === null ? null : simBadgeFor(slot.deductible, slot.cap, this.simBadge, this.fmtBaht);
        const rows = simVisibleItems(g).map((it, ii) => {
          const geo = simRowGeometry(it, this.simLocal, this.simHeadroom);
          return {
            it,
            ii,
            geo,
            ariaMax: Math.round(geo.scale),
            badge: geo.cap === null ? null : simBadgeFor(geo.total, geo.cap, this.simBadge, this.fmtBaht),
            segments: this.simRowSegments(it, geo, g, key),
          };
        });
        return {
          g,
          gi,
          key,
          slot,
          bar,
          badge,
          rows,
          segments: bar ? this.simGroupBarSegments(bar, g, key) : [],
          unit: g.unitLabel == null ? '' : String(g.unitLabel).trim(),
          note: this.simGroupNote(g, gi),
          spill: this.simSpillNote(g, slot),
          remarks: Array.isArray(g.remarks)
            ? g.remarks.filter(r => r != null && String(r).trim() !== '').map(r => String(r))
            : [],
        };
      });
    },
    /**
     * The commit button. 🔴 A DEFAULT LABEL rather than hiding the button when the payload
     * carries none: without it the screen has sliders and no way off, and the customer's plan
     * can never reach the server at all.
     */
    simSubmitButton() {
      const b = this.simPicker && this.simPicker.submitButton;
      return b && typeof b === 'object' ? b : null;
    },
    simSubmitLabel() {
      const b = this.simSubmitButton;
      return (b && b.label) || 'สรุปแผนลดหย่อนภาษี';
    },
    simSubmitStyle() {
      // 🔴 Both hints on ONE binding: `:style` is a single slot and a second binding would
      // silently win outright. Same merge the card box does for its own two hint families.
      return mergeStyles(
        textStyle(this.simSubmitButton, 'label'),
        fillStyle(this.simSubmitButton, 'frameColor'),
        this.dp(this.simPicker, 'submitMarginTop', 'marginTop'),
      );
    },
    /**
     * The products button's two hint families, merged the same way.
     *
     * 🔴 It used to bind `label` alone, so `productsButton.frameColor` was accepted by the
     * payload and silently dropped — the button stayed `--tsc-dark` however it was authored,
     * while its twin above obeyed. Two buttons side by side, one honouring a key and one not,
     * is the kind of asymmetry nobody finds until a designer changes one colour and half the
     * card moves.
     */
    simProductsStyle() {
      return mergeStyles(textStyle(this.simProductsButton, 'label'), fillStyle(this.simProductsButton, 'frameColor'));
    },
    /** 🔴 Absent = the what-if run produced nothing. See the template comment. */
    simResult() {
      return hasObjectRow(this.data, 'result') ? this.data.result : null;
    },
    /**
     * The before/after comparison stated as ONE grid.
     *
     * 🔴 Its PRESENCE is what selects the shape — a card carrying `table` draws the grid, a card
     * carrying `before`/`after` draws the two stacked columns. Not a `displayStyle`-style flag,
     * because the two shapes read different keys entirely: there is nothing for a flag to switch
     * between when only one of them has any data.
     */
    simTable() {
      return hasObjectRow(this.simResult, 'table') ? this.simResult.table : null;
    },
    /** The grid's rows. A row with neither figure is a row with nothing to compare. */
    simTableRows() {
      const rows = this.simTable && this.simTable.rows;
      if (!Array.isArray(rows)) return [];
      return rows.filter((r) => r && (r.before != null || r.after != null));
    },
    /**
     * The line between two rows of the grid.
     *
     * 🔴 `borderBottomColor`, not the default `backgroundColor` — `dividerColor` names a RULE, and
     * `fillStyle` painting it as a fill is the bug this branch already has twice over (see
     * `.tsc-sim-grp` and `.tsc-sim-total`, where the picker's own `dividerColor` floods the whole
     * block instead of drawing its edge).
     */
    simTableRule() {
      return fillStyle(this.simTable, 'dividerColor', 'borderBottomColor');
    },
    simBefore() {
      return this.simColumn('before', 'ก่อนจำลอง');
    },
    simAfter() {
      return this.simColumn('after', 'หลังจำลอง');
    },
    simSavings() {
      const s = this.simResult && this.simResult.savings;
      return s && Number.isFinite(Number(s.amount)) ? s : null;
    },
    simSelections() {
      const list = (this.simResult && Array.isArray(this.simResult.selections)) ? this.simResult.selections : [];
      return list.filter(s => s && Number.isFinite(Number(s.amount)));
    },
    /** The button object itself — the label AND its hints come off the same node. */
    simProductsButton() {
      const b = this.simResult && this.simResult.productsButton;
      return b && typeof b === 'object' ? b : null;
    },
    simProductsLabel() {
      const b = this.simProductsButton;
      return b && b.label ? b.label : '';
    },
    /** The "go back and re-plan" button, or null when this card did not ship one. */
    simEditButton() {
      const b = this.simResult && this.simResult.editButton;
      return b && typeof b === 'object' ? b : null;
    },
    simEditLabel() {
      const b = this.simEditButton;
      return b && b.label ? b.label : '';
    },
    /** Same two hint families as the products button — see the note on simProductsStyle for
     * why binding `label` alone is not enough. */
    simEditStyle() {
      return mergeStyles(textStyle(this.simEditButton, 'label'), fillStyle(this.simEditButton, 'frameColor'));
    },
    /**
     * May this card SEND anything — a plan, a jump to the product page?
     *
     * 🔴 Only the live card of the newest turn. An older turn is a record, and a card preview
     * has no conversation to post into; a button that moved on either would collect an answer
     * that goes nowhere.
     */
    /**
     * Does the CARD say it can be answered at all? The envelope's `postback` block is the
     * capability flag the mobile contract defines — a card that carries one can be pressed and
     * sent home, a card without one is a picture (TaxShortFormCardV2, the `postback` remark).
     *
     * 🔴 One computed for all three cards, and it is what keeps this surface honest. The page
     * used to fill a missing key in from its own view of the turn (`liveAskedFieldKey`), which
     * no app has — so a card production would never accept a press from was pressable here, and
     * a green run proved nothing. Read it off the envelope or do not offer the control.
     */
    canPostBack() {
      const pb = (this.card && this.card.postback) || null;
      return Boolean(pb && pb.fieldKey);
    },
    simCanEdit() {
      return this.isSimulation && this.interactive && this.canPostBack;
    },
    /**
     * May the customer MOVE the controls — drag a thumb, type in a box?
     *
     * 🔴 A strictly wider permission than `simCanEdit`, and deliberately so. Dragging sends
     * nothing on any surface: the thumbs, the ceiling bars and the running total are the
     * customer's own arithmetic about their own input, and the phone posts none of it until
     * the CTA is pressed. So the preview gets real sliders and dead buttons — the only
     * combination that shows an author the card the customer actually holds.
     */
    simCanDrag() {
      return this.isSimulation && (this.interactive || this.preview);
    },
    simBusy() {
      return Boolean(this.busy);
    },
    /**
     * Every DRAWN slider, flattened — the set a submission speaks for.
     * 🔴 Hidden bars are not sent: the phone sends what it drew, and the backend leaves an
     * absent key at the scenario's stored value, which for a bar nobody could move is
     * exactly right.
     */
    simItems() {
      return this.simGroups.reduce((all, g) => all.concat(simVisibleItems(g)), []);
    },
    /**
     * Has any slider left the position the server last costed? While true, every figure
     * the SERVER produced is about a different plan from the one on screen.
     *
     * 🔴 Only ever true on the LIVE card. A drag outlives the turn it happened in — the
     * bubble stays mounted with the customer's position still in `simValues` — so an old
     * card went on wearing "ตัวเลขภาษีด้านล่างยังเป็นของแผนก่อนหน้า" and a greyed-out
     * result for the rest of the session, about a plan that had already been costed and
     * answered. A card nobody can act on is a RECORD, and a record shows exactly what the
     * server sent.
     */
    simDirty() {
      return this.simCanEdit && simIsDirty(this.simItems, this.simValues);
    },
    /**
     * The positions to DRAW from. `null` on a card nobody can act on, which makes every
     * reader fall back to the card's own values.
     * 🔴 Not merely cosmetic: a drag outlives its turn (the bubble stays mounted), so
     * without this an old card would keep drawing the slider where the customer left it,
     * beside the figures the server computed for a different position — the two halves of
     * one card disagreeing about the same plan.
     */
    simLocal() {
      return this.simCanDrag ? this.simValues : null;
    },
    /**
     * 🔴 The summary's own blocks, each read through `hasObjectRow` so an ABSENT key and a
     * key holding null behave the same way: the block does not render. That is the whole
     * contract with the template's `@omitWhenNull` — a figure no formula produced must
     * leave nothing on screen, not a ฿0 the customer would read as "you owe nothing".
     */
    sumHeadline() {
      return hasObjectRow(this.data, 'headline') ? this.data.headline : null;
    },
    /** 🔴 TOP-LEVEL `data.stats`, not `headline.stats` — that is the other cards' shape. */
    sumStats() {
      return Array.isArray(this.data.stats) ? this.data.stats : [];
    },
    sumBrackets() {
      return hasObjectRow(this.data, 'brackets') ? this.data.brackets : null;
    },
    /**
     * Does the ladder render as a GRID rather than as the bar chart?
     *
     * 🔴 Compared trimmed and upper-cased, like every other piece of template vocabulary on
     * these cards (`unit`, `tone`, `control`). Anything unrecognised — including the key being
     * absent — keeps the bar shape, which is what every card authored before `displayStyle` was
     * read expects.
     */
    sumBracketsIsTable() {
      const s = this.sumBrackets && this.sumBrackets.displayStyle;
      return String(s == null ? '' : s).trim().toUpperCase() === 'TABLE';
    },
    /** The ladder's own column headers. Only the table shape has anywhere to put them. */
    sumBracketColumns() {
      const list = this.sumBrackets && this.sumBrackets.columns;
      return Array.isArray(list) ? list : [];
    },
    /**
     * 🔴 A block that does not say it collapses is always open — `collapsible` is the payload
     * asking for the control, and without it `expanded: false` would hide the ladder behind a
     * chevron nobody drew.
     */
    bracketsOpen() {
      return !(this.sumBrackets && this.sumBrackets.collapsible) || this.isOpen('b');
    },
    sumBracketRows() {
      const rows = this.sumBrackets && this.sumBrackets.rows;
      return Array.isArray(rows) ? rows : [];
    },
    /**
     * The per-filer ladders (type3.2 — a couple filing SEPARATELY): one entry per filer, each
     * carrying its own `rows[]` / `columns[]` / `cells[]`. Empty on every other payload.
     * 🔴 Only OBJECT entries: a null or a list in `filers[]` has no title, cells or rows to
     * draw, and the template reads `g.filer.title` unguarded.
     */
    sumBracketFilers() {
      const list = this.sumBrackets && this.sumBrackets.filers;
      return Array.isArray(list) ? list.filter((f) => f && typeof f === 'object' && !Array.isArray(f)) : [];
    },
    /**
     * What the ladder block iterates — the filers when there are any, else ONE group standing
     * for the legacy shape.
     *
     * 🔴 One list for both shapes so the table markup exists once in the template. The legacy
     * group has no `filer`, and that absence is what keeps its old behaviour: `bracketsOpen`
     * instead of a per-group key, and the bar chart when `displayStyle` is not TABLE. `hints`
     * is where the column hints (`columnsColor`…) are read from — a filer authors its own, the
     * block authors the legacy ones.
     */
    sumBracketGroups() {
      const filers = this.sumBracketFilers;
      if (!filers.length) {
        return [{
          key: 'b',
          filer: null,
          hints: this.sumBrackets,
          rows: this.sumBracketRows,
          columns: this.sumBracketColumns,
          cells: [],
        }];
      }
      return filers.map((f, i) => ({
        key: 'bf' + i,
        filer: f,
        hints: f,
        rows: Array.isArray(f.rows) ? f.rows : [],
        columns: Array.isArray(f.columns) ? f.columns : [],
        cells: Array.isArray(f.cells) ? f.cells : [],
      }));
    },
    /**
     * `valueColumnWidth` (dp, drawn 1:1) as the CSS var the filer head's figure columns share with
     * the header line above them — one width, decided once, so the headers sit over their figures.
     */
    sumBracketFilerVars() {
      const w = Number(this.sumBrackets && this.sumBrackets.valueColumnWidth);
      return Number.isFinite(w) && w > 0 ? { '--tsc-br-fcw': `${w}px` } : null;
    },
    sumBreakdown() {
      return hasObjectRow(this.data, 'breakdown') ? this.data.breakdown : null;
    },
    sumSections() {
      const list = this.sumBreakdown && this.sumBreakdown.sections;
      return Array.isArray(list) ? list : [];
    },
    /**
     * 🔴 The column set is the TEMPLATE's decision, not this component's. Two templates
     * exist — with and without a spouse column — and which one ran is decided by the
     * question item's own VisibleWhen gate on JOINT_FILING. Deriving the columns from the
     * longest `values` array instead would put a คู่สมรส header on a single filer's card
     * the first time one row happened to carry a stray second element.
     */
    sumColumns() {
      const list = this.sumBreakdown && this.sumBreakdown.columns;
      return Array.isArray(list) ? list : [];
    },
    /** The per-filer breakdowns (type3.2): each carries its own `sections[]`. Objects only, see `sumBracketFilers`. */
    sumBreakdownFilers() {
      const list = this.sumBreakdown && this.sumBreakdown.filers;
      return Array.isArray(list) ? list.filter((f) => f && typeof f === 'object' && !Array.isArray(f)) : [];
    },
    /**
     * What the breakdown block iterates — one group per filer, or ONE group standing for the
     * legacy shape.
     *
     * 🔴 `key` is the group's own open-state key AND the prefix its sections and items are keyed
     * under (`secKey` / `secItemKey`), so two filers' section 0 cannot collide in the one `open`
     * map. The legacy group's key is '' — which makes its section keys `'s0'`, `'i0-0'`… exactly
     * what they were before filers existed. `tid` does the same for the test ids.
     */
    sumSectionGroups() {
      const filers = this.sumBreakdownFilers;
      if (!filers.length) return [{ key: '', tid: '', filer: null, sections: this.sumSections }];
      return filers.map((f, fi) => ({
        key: 'f' + fi,
        tid: fi + '-',
        filer: f,
        sections: Array.isArray(f.sections) ? f.sections : [],
      }));
    },
    /** The breakdown's bottom line — "เงินได้สุทธิ", the figure the tax is actually computed from. */
    sumFooter() {
      return hasObjectRow(this.sumBreakdown, 'footer') ? this.sumBreakdown.footer : null;
    },
    sumAllowances() {
      return hasObjectRow(this.data, 'allowances') ? this.data.allowances : null;
    },
    /**
     * The filing comparison when it arrives INSIDE the summary card (design 2.3b1).
     * 🔴 Read from an added key rather than from a reshaped one: every other section here
     * comes out of the authored template and the merge must leave all of them alone, so an
     * old payload with no `filing` simply renders the card it always did.
     */
    sumFiling() {
      return hasObjectRow(this.data, 'filingOptions') ? this.data.filingOptions : null;
    },
    sumFilingOptions() {
      return filingTiles(this.sumFiling);
    },
    /**
     * Is this "summary" card nothing BUT the filing choice? Then it is the standalone
     * type3.1 card, which since 2026-08-27 travels in a `TAX_SUMMARY` envelope, and the
     * branch's tinted ground and white box are chrome around a card that has no summary in
     * it. See `tsc-sum--bare`.
     *
     * 🔴 Asked of the SECTIONS this branch would draw, not of `Object.keys(data)`: a payload
     * may carry `mainFrameColor`, `dividerColor` or a key this renderer ignores entirely, and
     * none of those puts anything on screen. What decides is whether a single other section
     * renders — the same six `v-if`s the template asks.
     */
    sumIsFilingOnly() {
      if (!this.sumFilingOptions.length) return false;
      return !this.sumHeadline
        && !this.sumStats.length
        && !this.sumBrackets
        && !this.sumBreakdown
        && !this.sumAllowances;
    },
    sumAllowanceItems() {
      const list = this.sumAllowances && this.sumAllowances.items;
      return Array.isArray(list) ? list : [];
    },
    /** 🔴 `data` is admin-authored and may be absent — never dereference it raw. */
    data() {
      return (this.card && this.card.data) || {};
    },
    categories() {
      const list = this.data.categories;
      return Array.isArray(list) ? list : [];
    },
    /**
     * TAX_AMOUNT_FORM rows, nested one level: `groups[].fields[]`.
     *
     * 🔴 A field with no `id` is DROPPED, and a group left with none goes with it (the rule
     * lives in `taxShortFormCard`). `id` IS the FieldKey a submission writes to, so a box
     * without one could be typed in and then be missing from `values` — a screen that shows
     * more than it can send, which is the one thing the three-state rule below cannot
     * survive. The backend never builds such a field, so reaching here means a hand-edited
     * payload, for which a visibly missing row is the honest outcome.
     */
    formGroups() {
      return formRenderableGroups(this.data.groups);
    },
    /**
     * The rule between two groups — `null` when the card did not ask for one, which is the normal case.
     *
     * 🔴 OPT-IN, and that is the whole point of the computed. The app separates groups with SPACE, not a line;
     * this screen drew a 1px rule on every card ever authored, so a boundary the customer reads as a gap was
     * read here as a divider. `dividerColor` is the one way a card can say it wants the line, and because the
     * hint IS the colour there is nothing to draw without it.
     */
    formDividerStyle() {
      return fillStyle(this.data, 'dividerColor', 'borderTopColor');
    },
    /** Typable here? Only an interactive mini-form card — never any other type. */
    formCanEdit() {
      return this.isForm && this.interactive && this.canPostBack;
    },
    formBusy() {
      return Boolean(this.busy);
    },
    /** Every box on screen, flattened — the set a submission speaks for. */
    formFields() {
      return formFlattenFields(this.data.groups);
    },
    /**
     * Boxes holding text that is not an amount. Empty boxes are NOT in here — an empty box
     * is an answer ("I have none"), a typo is not.
     *
     * 🔴 The reason this is on screen at all: an unreadable value is dropped by the backend,
     * that row is then read as one that was never displayed, and the checkpoint still closes
     * as answered. Nothing anywhere would tell the customer their number did not land.
     */
    formInvalidIds() {
      return formInvalidFieldIds(this.formFields, this.formValues);
    },
    /**
     * 🔴 Both buttons are OPTIONAL and both are optional in TWO ways: the whole
     * `submit` / `secondary` object can be absent, and the label inside it can be the
     * empty string — which is how the config says "this card has no ไม่มีรายการเหล่านี้
     * button". Falling back to any wording of our own would put a button on screen that
     * the app will not draw, so an empty label simply means: no button.
     */
    submitLabel() {
      return (hasObjectRow(this.data, 'submit') && this.data.submit.label) || '';
    },
    secondaryLabel() {
      return (hasObjectRow(this.data, 'secondary') && this.data.secondary.label) || '';
    },
    headline() {
      return hasObjectRow(this.data, 'headline') ? this.data.headline : null;
    },
    stats() {
      const list = this.headline && this.headline.stats;
      return Array.isArray(list) ? list : [];
    },
    /** Only render the meta row when the template actually supplied something. */
    hasMetaRow() {
      return Boolean(this.data.typeLabel || this.data.sectionLabel || this.data.unitLabel);
    },
    refId() {
      return (this.card && this.card.refId) || '';
    },
    /** `version` may be a number or the string `"1"`; `0` is still a version. */
    versionText() {
      const v = this.card && this.card.version;
      return v === null || v === undefined || v === '' ? '' : String(v);
    },
    /**
     * 🔴 A VALUE fingerprint of this envelope — the thing the accordion re-seeds on.
     * The parent builds its bubble list in a computed that re-runs `splitTaxCards()`,
     * so every unrelated re-render (typing, appending the pending user message) hands
     * this component a freshly `JSON.parse`d object: same content, new identity.
     * Watching `card` directly therefore fires on a keystroke and wipes the reader's
     * open categories back to `expanded`. A string compares BY VALUE, so this stays
     * quiet until the card's content really changes. `raw` is the card's own JSON text
     * and is preferred; `JSON.stringify` covers callers that pass only the object.
     */
    /**
     * The block a press is keyed on — `data.filingOptions.id`, else the envelope's own
     * `postback.fieldKey`.
     *
     * 🔴 Both, in that order, because the two answer different questions: `id` is what the
     * app puts IN `values`, `postback.fieldKey` is which question the submission names. They
     * are the same string on every card the backend builds today (the id falls back to the
     * checkpoint key), and reading the id first is what keeps a config that overrides it
     * working — the server reads that key and no other.
     */
    filingValueKey() {
      const id = this.sumFiling && this.sumFiling.id;
      if (id != null && String(id).trim() !== '') return String(id).trim();
      const pb = (this.card && this.card.postback) || null;
      const key = pb && pb.fieldKey;
      return key == null || String(key).trim() === '' ? '' : String(key).trim();
    },
    /**
     * May a tile be tapped at all? Everything the press needs has to be on the card: a
     * postback block (the routing), a key to send the value under, and tiles carrying values.
     *
     * 🔴 `interactive` alone is not enough. The older filing card — and the summary card that
     * draws the tiles from its own template — carry none of those three, so their tiles stay
     * a picture answered by the chips underneath, exactly as before.
     */
    filingCanPick() {
      if (!this.interactive || this.busy) return false;
      // Same capability flag as the mini-form and the simulation — see `canPostBack`.
      if (!this.canPostBack) return false;
      if (!this.filingValueKey) return false;
      return this.sumFilingOptions.some((o) => o && o.value != null && String(o.value).trim() !== '');
    },
    /** The confirm button as authored, or null when the card ships none. */
    filingSubmitButton() {
      const b = this.sumFiling && this.sumFiling.submitButton;
      return b && typeof b === 'object' ? b : null;
    },
    /**
     * The summary card's own way off the screen — TOP-LEVEL `data.submitButton`, not the
     * filing block's. Null when the card ships none (every summary before 2026-09-10).
     */
    sumSubmitButton() {
      const b = this.data.submitButton;
      return b && typeof b === 'object' && !Array.isArray(b) ? b : null;
    },
    /** 🔴 NO default label — a button with no caption was not authored, see `filingSubmitLabel`. */
    sumSubmitLabel() {
      const label = this.sumSubmitButton && this.sumSubmitButton.label;
      return label == null || String(label).trim() === '' ? '' : String(label);
    },
    /** The chat text the press sends. Falls back to the caption — the mock ships them equal. */
    sumSubmitMessage() {
      const b = this.sumSubmitButton;
      const msg = b && (b.message != null && String(b.message).trim() !== '' ? b.message : b.label);
      return msg == null ? '' : String(msg).trim();
    },
    /**
     * Pressable only on the live turn, and only with something to say. No `canPostBack` here
     * on purpose: this button does not post back, it types — so the `postback` block that
     * gates every other button is not what gates this one.
     */
    sumSubmitCanSend() {
      return Boolean(this.interactive && !this.busy && this.sumSubmitLabel && this.sumSubmitMessage);
    },
    sumSubmitStyle() {
      // Both hint families on ONE binding, plus the card-level gap above the button
      // (`data.submitMarginTop`, a dp like the simulation's).
      return mergeStyles(
        textStyle(this.sumSubmitButton, 'label'),
        fillStyle(this.sumSubmitButton, 'frameColor'),
        this.dp(this.data, 'submitMarginTop', 'marginTop'),
      );
    },
    /**
     * Its label. 🔴 NO default — a blank one means the author wanted no button, and inventing
     * one here would put a control on every card already in the log that nothing will accept
     * a press from. (The opposite call from the simulation's CTA, which is the only way off
     * that screen; this card can always be answered by chip.)
     */
    filingSubmitLabel() {
      const b = this.filingSubmitButton;
      const label = b && b.label;
      return label == null || String(label).trim() === '' ? '' : String(label);
    },
    filingSubmitStyle() {
      // Both hint families on ONE binding — `:style` is a single slot, and a second binding
      // would silently win outright. Same merge the simulation's buttons use.
      return mergeStyles(textStyle(this.filingSubmitButton, 'label'),
                         fillStyle(this.filingSubmitButton, 'frameColor'));
    },
    cardSignature() {
      return this.raw || JSON.stringify(this.card);
    },
    prettyRaw() {
      const text = String(this.raw || '');
      if (!text) return '';
      try {
        return JSON.stringify(JSON.parse(text), null, 2);
      } catch (e) {
        // Malformed JSON is exactly what this view exists to expose — show it as-is.
        return text;
      }
    },
  },
  watch: {
    // A new envelope in the same slot re-seeds the accordion from ITS `expanded`.
    // 🔴 Watch the SIGNATURE, never `card` itself: the prop's identity changes on every
    // parent re-render even when the content is byte-identical (see `cardSignature`),
    // and re-seeding there collapses a card the user had opened — reproducibly, the
    // moment they hit ส่ง, before the request is even sent.
    cardSignature() {
      this.seedOpen();
      // 🔴 A new card is the ANSWER to the last submission, so the local positions are
      // dropped and re-read from it. Merging instead would keep a slider the server
      // rejected or clamped sitting where the customer left it, on top of figures that
      // were computed without it.
      this.seedSimValues();
      // Same rule for the mini-form: the new card already carries whatever the server
      // accepted, so the boxes are re-read from it rather than keeping text it rejected.
      this.seedFormValues();
      this.seedFilingPicked();
    },
  },
  created() {
    // Seeded before the first paint so the card never flashes fully collapsed.
    this.seedOpen();
    this.seedSimValues();
    this.seedFormValues();
    this.seedFilingPicked();
  },
  methods: {
    // ── presentation hints (rule 4 — see utils/taxCardFormat) ──────────────────────────
    /**
     * One run of TEXT: `<prefix>Color` + `<prefix>FontSize` off `src`.
     *
     * 🔴 A method and not a computed, because the same helper serves a card-level key, a
     * row inside a `v-for` and a cell inside a nested one — a computed can hold none of
     * those three. It is pure and cheap (two property reads and a regex) and the imported
     * function it wraps is where the rules are tested.
     * 🔴 It exists only because a `v-bind` expression cannot reach module scope; every
     * decision it makes lives in `textStyle`, not here.
     */
    sx(src, prefix) {
      return textStyle(src, prefix);
    },
    /**
     * `<prefix>FontWeight` as CSS. Kept out of `textStyle` on purpose: most templates send
     * `…FontWeight` on every run and the preview's CSS has never read them, so honouring it
     * everywhere would restyle every card at once. Only runs that opt in call this.
     */
    wx(src, prefix) {
      const w = src && String(src[prefix + 'FontWeight'] || '').toLowerCase();
      if (w === 'bold') return { fontWeight: 700 };
      if (w === 'regular' || w === 'normal') return { fontWeight: 400 };
      return null;
    },
    /** One standalone colour hint — a frame, a pill, a divider. See `fillStyle`. */
    fx(src, key, prop) {
      return fillStyle(src, key, prop);
    },
    /** Several hint styles as one; later arguments win. See `mergeStyles`. */
    mx(...styles) {
      return mergeStyles(...styles);
    },
    /**
     * A type7 field row's margin / padding hints as CSS — numbers in dp, drawn 1:1 as px.
     * Empty object (never null) when nothing was authored. See `spacingStyle`.
     */
    spx(src) {
      return spacingStyle(src);
    },
    /**
     * A §40 category's own frame. `rowFrameColor` fills it; `accentColor` (type6 only)
     * draws the border, which is the one edge the design gives a category its identity on
     * — the badge inside it already carries `frameColor`, so painting both from the same
     * hint would collapse two decisions into one colour.
     */
    catStyle(cat) {
      return mergeStyles(
        fillStyle(cat, 'rowFrameColor'),
        fillStyle(cat, 'accentColor', 'borderColor'),
      );
    },
    /**
     * The money box's OWN text style plus the two placeholder hints, which cannot be set
     * inline at all: `::placeholder` is a pseudo-element and no `:style` binding reaches
     * one. They ride as custom properties the stylesheet reads (`--tsc-ph-*`), which is
     * the only way an authored placeholder colour can be shown here as the app shows it.
     */
    placeholderVars(f) {
      // 🔴 THE TYPED VALUE TAKES NO HINT AT ALL, and that is the app's contract rather than an
      //    omission here: `TaxFundField` carries hints for `label`, `placeholder` and `note` and
      //    there is no `valueColor` / `valueFontSize` anywhere in it. An earlier version of this
      //    method lent the box the LABEL's font size — which grew text in the preview that the
      //    phone would leave alone, i.e. the preview lying about the phone, the one thing it
      //    exists not to do.
      const ph = textStyle(f, 'placeholder');
      if (!ph) return null;
      const style = {};
      if (ph.color) style['--tsc-ph-color'] = ph.color;
      if (ph.fontSize) style['--tsc-ph-size'] = ph.fontSize;
      return Object.keys(style).length ? style : null;
    },
    seedOpen() {
      const next = {};
      this.categories.forEach((c, i) => {
        next[i] = isTruthy(c && c.expanded);
      });
      // 🔴 The summary's sections live in the SAME map under an 's' prefix. Numeric keys
      //    would collide with the category indexes above, and a card cannot be both — but
      //    keeping one map means one seed, one toggle and one watcher for both shapes.
      // 🔴 …and a fourth under the bare key `b`: the bracket ladder collapses as one block, so
      //    it needs no index. Seeded from its own `expanded`, like everything else here.
      if (this.sumBrackets) next.b = isTruthy(this.sumBrackets.expanded);
      // 🔴 …and `bf<n>` / `f<n>` for the type3.2 filers: each filer's ladder and each filer's
      //    breakdown collapses on its own, seeded from that filer's `expanded`. Its sections and
      //    items are keyed under the group (`secKey` / `secItemKey`); for the legacy group those
      //    come out as the bare `'s<i>'` / `'i<i>-<j>'` they always were.
      this.sumBracketFilers.forEach((f, fi) => {
        next['bf' + fi] = isTruthy(f.expanded);
      });
      this.sumSectionGroups.forEach((g) => {
        if (g.filer) next[g.key] = isTruthy(g.filer.expanded);
        g.sections.forEach((sec, i) => {
          next[this.secKey(g, i)] = isTruthy(sec && sec.expanded);
          // 🔴 …and a THIRD kind under `i`: a breakdown item that carries `children[]` collapses
          //    on its own, independently of the section holding it. Seeded only for items that
          //    actually have children — an item with none has nothing to open, and a key for it
          //    would put a chevron on a row that cannot move.
          // 🔴 …and a FOURTH under the group (`secGroupKey`): a section authored as `groups[]`
          //    collapses per group, seeded from that group's own `expanded`. Items are keyed under
          //    their BLOCK (`blockItemKey`) — the legacy block's come out as `secItemKey` always did
          //    — and an item opens for `subItems[]` exactly as it does for `children[]`.
          this.sectionBlocks(sec, g.filer).forEach((blk) => {
            if (blk.group) next[this.secGroupKey(g, i, blk.gi)] = isTruthy(blk.group.expanded);
            blk.items.forEach((it, j) => {
              if (this.subRowsOf(it).length) next[this.blockItemKey(g, i, blk, j)] = isTruthy(it && it.expanded);
            });
          });
        });
      });
      this.open = next;
    },
    isOpen(i) {
      return Boolean(this.open[i]);
    },
    toggle(i) {
      this.open[i] = !this.open[i];
    },
    fmtAmount(v) {
      return formatAmount(v);
    },
    /** One side of the simulation's comparison, with only the rows that carry a value. */
    simColumn(side, fallbackTitle) {
      const col = (this.simResult && this.simResult[side]) || {};
      const rows = Array.isArray(col.rows) ? col.rows.filter(r => r && r.value != null) : [];
      return { title: col.title || fallbackTitle, rows };
    },
    /**
     * Seed the local slider positions from the card the server just sent.
     *
     * 🔴 The half-typed box text goes with them. A string left in `simText` would be printed
     * over the figure the new card carries the moment that row took focus again — the box
     * quoting the plan the server just replaced.
     */
    seedSimValues() {
      this.simValues = simSeedValues(this.simItems);
      this.simText = {};
      this.simFocusId = null;
      this.simDragId = null;
    },
    /** Where one slider sits right now — the customer's drag if there is one, else the
     * position the card arrived with. */
    simValue(item) {
      return simSliderValue(item, this.simLocal);
    },
    /**
     * The ONE submission this card ever makes: "แสดงผลิตภัณฑ์" with the whole plan.
     * 🔴 The only way the tax figures on this card can change is a round trip — a plan
     * costed in the browser would be a second tax engine, and the first thing it would
     * disagree with is the return the customer files.
     */
    onSimProducts() {
      if (!this.simCanEdit || this.simBusy) return;
      this.emitSimSubmit('products');
    },
    /**
     * "แก้ไขแผนลดหย่อน" — the one press on this card that walks the form BACKWARDS.
     *
     * 🔴 The action is read OFF THE BUTTON and echoed verbatim, never spelled here. Every
     * other emitter on this page hard-codes its verb because the app knows which button it
     * drew; this one exists precisely so the server names it, and a literal typed here would
     * be a second spelling free to drift from the constant the reader matches against
     * (`TaxSimulationCardV2.PostbackEdit`). No `action` on the button ⇒ no press: a button
     * that cannot say what it is must not send a submission the backend will read as an
     * adjustment carrying no sliders.
     *
     * 🔴 It still sends `values`, and they are still the whole slider set — which on a
     * result-only card is empty. That is not a special case worth branching for: the reader
     * drops keys this card does not own either way, and going through `emitSimSubmit` keeps
     * the envelope (`kind`/`fieldKey`) built in exactly one place.
     */
    onSimEdit() {
      if (!this.simCanEdit || this.simBusy) return;
      const action = this.simEditButton && this.simEditButton.action;
      if (!action) return;
      this.emitSimSubmit(action);
    },
    /**
     * 🔴 EVERY slider on screen, every time — not just the ones that moved. The backend
     * reads an absent key as "this control was not on their screen" and leaves it alone,
     * which is right for a row gated off and wrong for one the customer deliberately
     * dragged back to zero. Sending the whole set makes the message say exactly what the
     * screen shows, keyed by each slider's `id` — the deduction's real FieldKey, echoed
     * back exactly as the card sent it (TaxPlanningV2-CardPostback-Wire-Design.md §1.4).
     */
    emitSimSubmit(action) {
      if (!this.simCanEdit) return;
      this.$emit('card-submit', {
        ...this.postbackStamp(),
        action,
        values: simSubmissionValues(this.simItems, this.simValues),
      });
    },
    /** A group's key in the allocation — its authored `id`, else its position on the card.
     * 🔴 Delegated, never re-implemented: `allocate` looks its slots up by exactly this
     * string, so a second spelling here would draw a bar from a slot nothing filled. */
    simGroupKey(group, index) {
      return simGroupKeyOf(group, index);
    },
    /** This group's slice of the allocation: `{ cap, used, deductible, over, rawAll, bought, lent, home, contributions }`. */
    simSlot(group, index) {
      return simGroupSlotOf(this.simAlloc, group, index);
    },
    /**
     * A numeric dp hint (`groupSpacing`, `profileMarginBottom`, …) as ONE CSS length, or null
     * when it was not authored or is not a finite number ≥ 0. Numbers only — the same
     * strictness as `spacingStyle`, because the value lands in a `:style` binding and a
     * browser would happily honour a string no phone reads.
     */
    dp(src, key, prop) {
      if (!src || typeof src !== 'object' || src[key] == null) return null;
      const n = Number(src[key]);
      return Number.isFinite(n) && n >= 0 ? { [prop]: n + 'px' } : null;
    },
    /**
     * One colour, resolved the way the contract says: the GROUP's own palette first, then the
     * payload-level `data.colors` (bound as CSS variables on the card box), then this file's
     * literal. Resolved field by field, so a group that only differs in one colour sends one.
     * Six keys since the redesign: the three bar states plus `maximumAlt` (the second stripe
     * colour), `maximumZone` (the tint past the ceiling) and `locked` (a thumb that cannot move).
     */
    simColorOf(groupKey, key) {
      const g = groupKey == null ? null : this.simGroupIndex[groupKey];
      // 🔴 FIELD BY FIELD, not object by object: a group that authored only `normal` keeps the
      // card's `minimum` and `maximum` rather than losing them to this file's literals. The
      // three lookups are tried on the PARSED value, so a blank string and a typo both fall
      // through instead of setting a colour nobody can see.
      return hintColor(g && g.colors && g.colors[key])
        || hintColor(this.data && this.data.colors && this.data.colors[key])
        || SIM_DEFAULT_COLORS[key];
    },
    /** The over-zone pattern: diagonal stripes of `maximum` / `maximumAlt`, as a CSS image. */
    simStripes(groupKey) {
      const a = this.simColorOf(groupKey, 'maximum');
      const b = this.simColorOf(groupKey, 'maximumAlt');
      return `repeating-linear-gradient(-45deg, ${a} 0 4px, ${b} 4px 8px)`;
    },
    /**
     * The fill of an over-the-ceiling slice: stripes unless the payload asked for `solid`
     * (`overPattern` on an item, `capOverPattern` on a group), which paints plain `maximum`.
     */
    simOverFill(groupKey, pattern) {
      const p = String(pattern == null ? '' : pattern).trim().toLowerCase();
      return p === 'solid'
        ? { background: this.simColorOf(groupKey, 'maximum') }
        : { backgroundImage: this.simStripes(groupKey) };
    },
    /** Which of the three legend swatches this row draws. Unknown / absent ⇒ solid. */
    simSwatchKind(entry) {
      const s = String((entry && entry.swatchStyle) || '').trim().toLowerCase();
      return s === 'dashed' || s === 'stripes' ? s : 'solid';
    },
    /**
     * A legend swatch. `solid` wears its own `swatchColor` (else the `minimum` band it almost
     * always describes); `dashed` is the cap line; `stripes` is the over-zone. Geometry hints
     * (`swatchWidth` / `swatchHeight` / `swatchCornerRadius`) are honoured when authored.
     */
    simSwatchStyle(entry) {
      const kind = this.simSwatchKind(entry);
      const style = mergeStyles(
        this.dp(entry, 'swatchWidth', 'width'),
        this.dp(entry, 'swatchHeight', 'height'),
        this.dp(entry, 'swatchCornerRadius', 'borderRadius'),
      ) || {};
      if (kind === 'stripes') {
        style.backgroundImage = this.simStripes(null);
      } else if (kind === 'dashed') {
        style.background = '#fff';
        style.borderRight = '2px dashed ' + (hintColor(entry && entry.swatchBorderColor) || SIM_CAP_LINE_COLOR);
        style.borderRadius = '0';
      } else {
        style.background = hintColor(entry && entry.swatchColor) || this.simColorOf(null, 'minimum');
        const border = hintColor(entry && entry.swatchBorderColor);
        if (border) style.border = '1px solid ' + border;
      }
      return style;
    },
    /** One cell of the profile strip: the picker's padding hints, and the divider before every cell but the first. */
    simProfileCellStyle(index) {
      const style = mergeStyles(
        this.dp(this.simPicker, 'profilePaddingTop', 'paddingTop'),
        this.dp(this.simPicker, 'profilePaddingBottom', 'paddingBottom'),
        this.dp(this.simPicker, 'profilePaddingLeft', 'paddingLeft'),
        this.dp(this.simPicker, 'profilePaddingRight', 'paddingRight'),
      ) || {};
      if (index > 0) {
        const c = hintColor(this.simPicker && this.simPicker.profileDividerColor);
        if (c) style.borderLeftColor = c;
      }
      return Object.keys(style).length ? style : null;
    },
    /** The status pill's state class — `full` and `over` recolour it, `remaining` is the base. */
    simBadgeClass(kind) {
      return { 'tsc-sim-badge--full': kind === 'full', 'tsc-sim-badge--over': kind === 'over' };
    },
    /**
     * The status pill's authored look. The base keys (`labelColor` / `frameColor` / padding /
     * radius) apply to every state; `full*` and `over*` override the two warning states.
     * 🔴 Every colour goes through `hintColor`; an unauthored pill keeps the stylesheet's own.
     */
    simBadgeStyle(kind) {
      const b = this.simBadge;
      const base = mergeStyles(
        textStyle(b, 'label'),
        fillStyle(b, 'frameColor'),
        fillStyle(b, 'borderColor', 'borderColor'),
        this.dp(b, 'cornerRadius', 'borderRadius'),
        this.dp(b, 'paddingTop', 'paddingTop'),
        this.dp(b, 'paddingBottom', 'paddingBottom'),
        this.dp(b, 'paddingLeft', 'paddingLeft'),
        this.dp(b, 'paddingRight', 'paddingRight'),
      );
      if (kind === 'full') return mergeStyles(base, fillStyle(b, 'fullFrameColor'), fillStyle(b, 'fullColor', 'color'));
      if (kind === 'over') return mergeStyles(base, fillStyle(b, 'overFrameColor'), fillStyle(b, 'overColor', 'color'));
      return base;
    },
    /** What the group's own added slice is painted in — `capIncreaseColor`, else its identity colour. */
    simCapIncreaseColor(group, groupKey) {
      return hintColor(group && group.capIncreaseColor) || this.simColorOf(groupKey, 'normal');
    },
    /**
     * The group bar as positioned slices — bought (dark), planned (identity colour), over
     * (stripes from the ceiling). Widths come from `groupBar` (tested); this only paints them.
     */
    simGroupBarSegments(bar, group, groupKey) {
      const out = [];
      if (bar.boughtPct > 0) {
        out.push({
          kind: 'bought',
          style: { left: '0%', width: bar.boughtPct + '%', background: this.simColorOf(groupKey, 'minimum'), minWidth: '4px' },
        });
      }
      if (bar.plannedPct > 0) {
        out.push({
          kind: 'planned',
          style: { left: bar.boughtPct + '%', width: bar.plannedPct + '%', background: this.simCapIncreaseColor(group, groupKey) },
        });
      }
      if (bar.overPct > 0) {
        out.push({
          kind: 'over',
          style: { left: bar.capPct + '%', width: bar.overPct + '%', ...this.simOverFill(groupKey, group && group.capOverPattern) },
        });
      }
      return out;
    },
    /**
     * One row's track as positioned slices, straight from `rowGeometry` (tested): the dark
     * bought slice, the striped part of the holding already past the ceiling, what was added,
     * and what was added past the ceiling. The white divider and the tinted zone are drawn by
     * the template from the same geometry.
     */
    simRowSegments(item, geo, group, groupKey) {
      const min = this.simColorOf(groupKey, 'minimum');
      const normal = this.simColorOf(groupKey, 'normal');
      const over = this.simOverFill(groupKey, item && item.overPattern);
      const segs = [];
      if (geo.bought > 0) {
        segs.push({
          kind: 'bought',
          style: { left: '0%', width: geo.boughtPct + '%', background: min, minWidth: '5px', borderRadius: '999px 0 0 999px' },
        });
      }
      if (geo.boughtOverPct > 0) {
        segs.push({ kind: 'bought-over', style: { left: geo.boughtOverLeft + '%', width: geo.boughtOverPct + '%', ...over } });
      }
      if (geo.added > 0 && geo.planPct > 0) {
        segs.push({
          kind: 'plan',
          style: { left: geo.planLeft + '%', width: geo.planPct + '%', background: normal, minWidth: '4px' },
        });
      }
      if (geo.planOverPct > 0) {
        segs.push({ kind: 'plan-over', style: { left: geo.planOverLeft + '%', width: geo.planOverPct + '%', ...over } });
      }
      return segs;
    },
    /** The white tick between bought and added — `dividerColor` / `dividerWidth` on the item. */
    simDividerStyle(item, geo) {
      return mergeStyles(
        { left: geo.boughtPct + '%' },
        fillStyle(item, 'dividerColor'),
        this.dp(item, 'dividerWidth', 'width'),
      );
    },
    /** `showCapLine: false` drops the dashed ceiling line and its flag. */
    simShowCapLine(item) {
      return !(item && item.showCapLine === false);
    },
    /** The dashed ceiling line: position from the geometry, colour / width / height / style from the item. */
    simCapLineStyle(item, geo) {
      const style = { left: geo.capPct + '%' };
      const c = hintColor(item && item.capLineColor);
      if (c) style.borderLeftColor = c;
      const w = Number(item && item.capLineWidth);
      if (item && item.capLineWidth != null && Number.isFinite(w) && w >= 0) style.borderLeftWidth = w + 'px';
      const h = Number(item && item.capLineHeight);
      if (item && item.capLineHeight != null && Number.isFinite(h) && h >= 0) style.height = h + 'px';
      if (item && item.capLineDashed === false) style.borderLeftStyle = 'solid';
      return style;
    },
    /** The label beside the ceiling line — `capFlag` ("{cap}") with the figure filled in. */
    simCapFlag(item, geo) {
      const t = item && item.capFlag != null && String(item.capFlag).trim() !== '' ? String(item.capFlag) : '{cap}';
      const cap = this.fmtBaht(geo.cap);
      return simFillNote(t, { cap, amount: cap });
    },
    /** "ซื้อแล้ว {amount} 🔒" under the track — the locked holding, named out loud. */
    simBoughtCaption(item, geo) {
      const t = item && item.boughtCaption != null && String(item.boughtCaption).trim() !== ''
        ? String(item.boughtCaption) : 'ซื้อแล้ว {amount} 🔒';
      return simFillNote(t, { amount: this.fmtBaht(geo.bought), cap: geo.cap === null ? '' : this.fmtBaht(geo.cap) });
    },
    simPlanLabel(item) {
      return item && item.planLabel != null && String(item.planLabel).trim() !== '' ? String(item.planLabel) : 'วางแผนเพิ่ม';
    },
    simCombinedLabel(item) {
      return item && item.combinedLabel != null && String(item.combinedLabel).trim() !== '' ? String(item.combinedLabel) : 'รวมทั้งหมด';
    },
    /** "/ {cap}" beside the combined figure. Nothing on a bar with no ceiling. */
    simCombinedCap(item, geo) {
      if (geo.cap === null) return '';
      const t = item && item.combinedCap != null && String(item.combinedCap).trim() !== '' ? String(item.combinedCap) : '/ {cap}';
      return simFillNote(t, { cap: this.fmtBaht(geo.cap) });
    },
    simTagPrefix(item) {
      return item && item.tagPlanPrefix != null ? String(item.tagPlanPrefix) : '+';
    },
    simShowTag(item) {
      return !(item && item.showTag === false);
    },
    simShowOverZone(item) {
      return !(item && item.showOverZone === false);
    },
    /**
     * The two halves of the live tag: bought in `minimum`, the plan half in the identity colour
     * — `maximum` once the total is past the ceiling. Text hints (`tagColor` / `tagFontSize`)
     * apply to both halves.
     */
    simTagStyle(row, gv, half) {
      const key = gv.key;
      const bg = half === 'a'
        ? this.simColorOf(key, 'minimum')
        : (row.geo.over ? this.simColorOf(key, 'maximum') : this.simColorOf(key, 'normal'));
      return mergeStyles({ background: bg }, textStyle(row.it, 'tag'));
    },
    /**
     * The thumb, centred on the total. Its ring (and grip lines) wear the identity colour,
     * `maximum` past the ceiling, `locked` when nothing was added and the holding already
     * reaches the ceiling — the design's own three states.
     */
    simThumbStyle(row, gv) {
      const it = row.it;
      const geo = row.geo;
      const color = geo.over
        ? this.simColorOf(gv.key, 'maximum')
        : (geo.locked ? this.simColorOf(gv.key, 'locked') : this.simColorOf(gv.key, 'normal'));
      const style = { left: geo.thumbPct + '%', '--tsc-sim-thumb-color': color };
      const bg = hintColor(it && it.thumbColor);
      if (bg) style.background = bg;
      const size = Number(it && it.thumbSize);
      if (it && it.thumbSize != null && Number.isFinite(size) && size > 0) {
        style.width = size + 'px';
        style.height = size + 'px';
      }
      const bw = Number(it && it.thumbBorderWidth);
      if (it && it.thumbBorderWidth != null && Number.isFinite(bw) && bw >= 0) style.borderWidth = bw + 'px';
      return style;
    },
    /** Is this row the one the customer is typing into or dragging? Lights up its plan box. */
    simRowLive(item) {
      const id = item && item.id;
      return id != null && (this.simFocusId === id || this.simDragItemId === id);
    },
    simPlanBoxStyle(item) {
      const live = this.simRowLive(item);
      return mergeStyles(
        fillStyle(item, 'planBoxFrameColor'),
        fillStyle(item, 'planBoxBorderColor', 'borderColor'),
        this.dp(item, 'planBoxCornerRadius', 'borderRadius'),
        live ? fillStyle(item, 'planBoxActiveFrameColor') : null,
        live ? fillStyle(item, 'planBoxActiveBorderColor', 'borderColor') : null,
      );
    },
    simInputStyle(item) {
      const live = this.simRowLive(item);
      return mergeStyles(
        textStyle(item, 'value'),
        fillStyle(item, 'valueFrameColor'),
        fillStyle(item, 'valueBorderColor', 'borderColor'),
        this.dp(item, 'valueCornerRadius', 'borderRadius'),
        this.dp(item, 'valueWidth', 'width'),
        this.dp(item, 'valueHeight', 'height'),
        live ? fillStyle(item, 'valueActiveColor', 'color') : null,
        live ? fillStyle(item, 'valueActiveBorderColor', 'borderColor') : null,
      );
    },
    simCombinedBoxStyle(item) {
      return mergeStyles(
        fillStyle(item, 'combinedBoxFrameColor'),
        fillStyle(item, 'combinedBoxBorderColor', 'borderColor'),
        this.dp(item, 'combinedBoxCornerRadius', 'borderRadius'),
      );
    },
    /** The combined figure — the card's `positiveColor` unless the item authored its own. */
    simCombinedAmountStyle(item) {
      return mergeStyles(fillStyle(this.data, 'positiveColor', 'color'), textStyle(item, 'combinedAmount'));
    },
    simStatKey(stat) {
      return String((stat && stat.key) || '').trim().toUpperCase();
    },
    /**
     * What one tile prints: the live figure with the tile's own `prefix` ("+") and `unitLabel`.
     * 🔴 The em dash when the figure is null — TAX_SAVED with no ladder shipped — never a 0
     * that would read as "this plan saves nothing".
     */
    simStatText(stat) {
      const key = this.simStatKey(stat);
      let n = null;
      if (key === 'DEDUCTIBLE') n = this.simDeductible;
      else if (key === 'NEW_MONEY') n = this.simNewMoney;
      else if (key === 'TAX_SAVED') n = this.simTaxSaved;
      if (n == null || !Number.isFinite(Number(n))) return EM_DASH;
      const prefix = stat.prefix == null ? '' : String(stat.prefix);
      const unit = stat.unitLabel == null || String(stat.unitLabel).trim() === '' ? '' : ' ' + String(stat.unitLabel).trim();
      return prefix + this.fmtBaht(n) + unit;
    },
    /** The saving tile wears the card's `positiveColor`; every tile may author its own `amount*`. */
    simStatAmountStyle(stat) {
      return mergeStyles(
        this.simStatKey(stat) === 'TAX_SAVED' ? fillStyle(this.data, 'positiveColor', 'color') : null,
        textStyle(stat, 'amount'),
      );
    },
    /**
     * The lender's own sentence — `spillNote` with `{lent}` / `{home}` filled from the live
     * allocation. Empty while this group lent nothing: a sentence about 0 baht moving is noise.
     */
    simSpillNote(group, slot) {
      const text = group && group.spillNote;
      if (!text || !slot || !(slot.lent > 0)) return '';
      return simFillNote(text, {
        lent: this.fmtBaht(slot.lent),
        home: this.fmtBaht(slot.home),
        cap: slot.cap === null ? '' : this.fmtBaht(slot.cap),
        used: this.fmtBaht(slot.deductible),
      });
    },
    /** The unfilled remainder of the group bar — authored, else the app's own rail grey. */
    simCapTrackStyle(group) {
      return mergeStyles(
        { background: hintColor(group && group.capTrackColor) || SIM_TRACK_COLOR },
        this.dp(group, 'capBarHeight', 'height'),
      );
    },
    /**
     * The `x / y` figure beside the group bar.
     *
     * 🔴 Past the ceiling the colour is `maximum` and it OVERRIDES the authored `capUsedColor`
     * — the figure has stopped being a status line and become the warning. The authored font
     * size is kept either way: the size is the layout the designer chose, the colour is the
     * state the card is in, and only one of those changes when a slider moves.
     */
    simCapUsedStyle(group, index) {
      const authored = textStyle(group, 'capUsed');
      if (this.simSlot(group, index).over <= 0) return authored;
      return mergeStyles(authored, { color: this.simColorOf(this.simGroupKey(group, index), 'maximum') });
    },
    /**
     * The explanatory line under the group bar: the group's `note`, replaced by `overCapNote`
     * once the ceiling is passed. 🔴 The placeholders are filled from the LIVE figures, so the
     * sentence keeps agreeing with the bar above it as the customer drags. `{used}` is what
     * the group is being asked to hold (uncapped), `{excess}` how far that is past `{cap}`.
     */
    simGroupNote(group, index) {
      const slot = this.simSlot(group, index);
      if (slot.over > 0) {
        const text = (group && group.overCapNote) || '';
        if (!text) {
          return `รวมกลุ่มนี้ ${this.fmtBaht(slot.rawAll)} บาท เกินเพดานรวม ${this.fmtBaht(slot.cap)} บาท `
            + `ส่วนเกิน ${this.fmtBaht(slot.over)} บาท ไม่นับลดหย่อนเพิ่ม`;
        }
        return simFillNote(text, {
          used: this.fmtBaht(slot.rawAll), cap: this.fmtBaht(slot.cap), excess: this.fmtBaht(slot.over),
        });
      }
      return (group && group.note) || '';
    },
    /** Typable here? Only a surface whose controls move, and only a row left editable. */
    simRowEditable(item) {
      return this.simCanDrag && !this.simBusy && simIsEditable(item);
    },
    /**
     * What the box PRINTS.
     *
     * 🔴 The customer's own string while they are typing in it, the formatted figure the rest
     * of the time. These are allowed to disagree: a box bound straight to the formatted amount
     * is rewritten on every keystroke, and the caret jumps a character left the moment a
     * separator appears under it. See `simText`.
     */
    simAddedText(item) {
      const id = item && item.id;
      if (id != null && this.simFocusId === id && this.simText[id] != null) return this.simText[id];
      return this.fmtBaht(simAddedValue(item, this.simLocal));
    },
    /**
     * A keystroke in the amount box. Moves the bar, its group's ceiling bar and the tiles —
     * and leaves the string exactly as typed.
     *
     * 🔴 NO SNAPPING AND NO SEPARATORS HERE. `step` rounding on a keystroke turns "2" into
     * "500" before the customer has finished typing "2,000"; separators move the caret. Both
     * are applied on commit (`onSimBoxCommit`), which is the moment the figure is finished.
     * 🔴 Non-digits are stripped before parsing because the box prints "20,000" and the
     * customer types into THAT string — a parse that choked on the comma would read every
     * edited row as 0 the moment they touched it.
     */
    onSimBox(item, ev) {
      if (!this.simRowEditable(item) || item.id == null) return;
      const typed = String((ev && ev.target && ev.target.value) || '').replace(/[^\d]/g, '');
      this.simText = { ...this.simText, [item.id]: typed };
      this.simValues = { ...this.simValues, [item.id]: simValueFromAdded(item, typed) };
    },
    /**
     * Focus: show the plain digits and select them, so the first keystroke REPLACES the figure
     * instead of appending to "20,000".
     */
    onSimBoxFocus(item, ev) {
      const id = item && item.id;
      if (id == null) return;
      this.simFocusId = id;
      this.simText = { ...this.simText, [id]: String(simAddedValue(item, this.simLocal)) };
      const el = ev && ev.target;
      // The value the box shows is only rewritten on the next render, so the select has to
      // wait for it — selecting now would select the string that is about to be replaced.
      this.$nextTick(() => {
        if (el && typeof el.select === 'function' && document.activeElement === el) el.select();
      });
    },
    /**
     * Commit — Enter, `change`, or losing the caret. Snap to `step`, hold inside the range,
     * and rewrite the box WITH separators.
     */
    onSimBoxCommit(item, ev) {
      const id = item && item.id;
      if (id == null || !this.simRowEditable(item)) return;
      const typed = String((ev && ev.target && ev.target.value) || '').replace(/[^\d]/g, '');
      const value = simSnapAdded(item, typed);
      this.simValues = { ...this.simValues, [id]: value };
      this.simText = { ...this.simText, [id]: this.fmtBaht(simAddedValue({ ...item, value }, null)) };
    },
    /** Blur: commit, then hand the box back to the formatted figure. */
    onSimBoxBlur(item, ev) {
      this.onSimBoxCommit(item, ev);
      if (item && item.id != null && this.simFocusId === item.id) this.simFocusId = null;
    },
    // ── the slider itself ────────────────────────────────────────────────────────────────
    //
    // 🔴 A hand-built track rather than `<input type="range">`, and the reason is geometry:
    // the thumb is centred ON the value and the scale is the design's own `scaleMax` (a little
    // past the ceiling or the holding), not the ceiling. Everything the native control gave
    // us for free is re-stated below: pointer capture so a drag survives leaving the element,
    // `role="slider"` + arrow keys so it is still operable and announced.
    /**
     * Where a pointer sits on the track, as a snapped slider position. 🔴 Measured against the
     * TRACK inside the strip, not the strip itself — the strip carries the tag above and the
     * caption below, and the fill ends where the track does.
     */
    simValueAtPointer(item, el, clientX) {
      const track = el && typeof el.querySelector === 'function' ? el.querySelector('.tsc-sim-track') : null;
      const target = track || el;
      const rect = target && target.getBoundingClientRect ? target.getBoundingClientRect() : null;
      if (!rect || !rect.width) return this.simValue(item);
      const scale = simScaleMax(item, this.simLocal, this.simHeadroom);
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      return simSnapValue(item, ratio * scale);
    },
    /**
     * A pointerdown ANYWHERE on the strip jumps the value and starts the drag — the whole
     * strip is the target, not the thumb. `setPointerCapture` is what keeps the drag alive
     * once the finger leaves the row, and `touch-action: pan-y` in the stylesheet is what
     * stops the page scrolling sideways under it.
     */
    onSimPointerDown(item, ev) {
      if (!this.simRowEditable(item)) return;
      const el = ev.currentTarget;
      this.simDragId = ev.pointerId;
      this.simDragItemId = item && item.id != null ? item.id : null;
      if (el && typeof el.setPointerCapture === 'function') {
        try { el.setPointerCapture(ev.pointerId); } catch (e) { /* capture is best-effort */ }
      }
      this.simSetValue(item, this.simValueAtPointer(item, el, ev.clientX));
    },
    onSimPointerMove(item, ev) {
      if (this.simDragId !== ev.pointerId || !this.simRowEditable(item)) return;
      this.simSetValue(item, this.simValueAtPointer(item, ev.currentTarget, ev.clientX));
    },
    onSimPointerUp(ev) {
      if (this.simDragId !== ev.pointerId) return;
      this.simDragId = null;
      this.simDragItemId = null;
      const el = ev.currentTarget;
      if (el && typeof el.releasePointerCapture === 'function') {
        try { el.releasePointerCapture(ev.pointerId); } catch (e) { /* already released */ }
      }
    },
    /** Arrow keys step by the row's own `step` (×10 with Shift); Home/End park it at either end of the range. */
    onSimKey(item, ev) {
      if (!this.simRowEditable(item)) return;
      const step = simStepOf(item) * (ev.shiftKey ? 10 : 1);
      const min = simSliderMin(item);
      const top = simSliderTop(item);
      let next = null;
      if (ev.key === 'ArrowRight' || ev.key === 'ArrowUp') next = this.simValue(item) + step;
      else if (ev.key === 'ArrowLeft' || ev.key === 'ArrowDown') next = this.simValue(item) - step;
      else if (ev.key === 'Home') next = min;
      else if (ev.key === 'End') next = Number.isFinite(top) ? top : this.simValue(item);
      if (next === null) return;
      ev.preventDefault();
      this.simSetValue(item, simSnapValue(item, next));
    },
    /**
     * One slider's new position, from a drag or a key.
     *
     * 🔴 A drag REWRITES the amount box — plain digits while it holds the caret, grouped when
     * it does not. The box states the same figure the thumb does, and a box left holding the
     * figure from before the drag is the card disagreeing with itself.
     */
    simSetValue(item, value) {
      const id = item && item.id;
      if (id == null) return;
      this.simValues = { ...this.simValues, [id]: value };
      const added = simAddedValue({ ...item, value }, null);
      this.simText = {
        ...this.simText,
        [id]: this.simFocusId === id ? String(added) : this.fmtBaht(added),
      };
    },
    /** Commit the plan — the round trip that produces the figures that count. */
    onSimSubmit() {
      if (!this.simCanEdit || this.simBusy) return;
      this.emitSimSubmit('submit');
    },
    /** The floor of one slider's draggable range — the cover the customer already holds. */
    simMin(item) {
      return simSliderMin(item);
    },
    /** The bare rail past the fill — the item's `trackColor`, else the group's `capTrackColor`, else the app's grey. */
    simRailStyle(group, item) {
      return mergeStyles(
        { background: hintColor(item && item.trackColor) || hintColor(group && group.capTrackColor) || SIM_TRACK_COLOR },
        this.dp(item, 'trackHeight', 'height'),
      );
    },
    /** `tone` is the config's colour hint, never a number this component judged. */
    simToneClass(tone) {
      if (tone === 'NEGATIVE') return 'tsc-sim-stat-neg';
      if (tone === 'POSITIVE') return 'tsc-sim-stat-pos';
      return 'tsc-sim-stat-num';
    },
    /** Seed the boxes from the card the server just sent — its prefilled `value`, else empty. */
    seedFormValues() {
      this.formValues = formSeedValues(this.formFields);
    },
    /** What is IN one box right now: the customer's typing, else what the card prefilled. */
    formValue(f) {
      return formFieldValue(f, this.formValues);
    },
    /** Is this box holding something we cannot send? Drives the red box and its message. */
    formFieldInvalid(f) {
      return Boolean(f && f.id != null && this.formInvalidIds.includes(f.id));
    },
    /**
     * A keystroke. Local only — no request, not even on blur.
     * 🔴 The customer is still filling the screen in: a turn per box would spend a request
     * (and a transcript line) on a form they have not finished. Same contract as the
     * simulation's sliders — fill it in privately, submit it once.
     * 🔴 Stored VERBATIM: the box shows back exactly what was typed, mid-comma and all. The
     * commas / spaces / "บาท" are stripped once, at the moment a payload is built
     * (`taxShortFormCard.normalizeAmount`), because that is where the wire contract applies
     * — doing it per keystroke would rewrite the text under the caret while it is being
     * typed. The backend is still the single decider of what a value means.
     */
    onFormInput(f, event) {
      if (!f || f.id == null) return;
      const raw = (event && event.target && event.target.value) || '';
      this.formValues = { ...this.formValues, [f.id]: String(raw) };
    },
    /**
     * The unit printed after a money figure (headline, simulation read-out / savings / selections): the
     * object's own `unitLabel` text when the payload carries one, else DEFAULT_UNIT_LABEL ("บาท").
     *
     * 🔴 The fallback keeps every card written before `unitLabel` rode on these objects — and every
     * transcript redrawn from history — printing exactly what it printed before. The text arrives already
     * in the turn's language (a language bag collapsed server-side, or translated on the way out).
     */
    moneyUnit(o) {
      const own = o && o.unitLabel;
      if (own != null && String(own).trim() !== '') return String(own).trim();
      // 🔴 …else the CARD's `data.unitLabel` ("Unit: Baht" / "หน่วย: บาท" / "单位：泰铢"), which the server
      //    already sends in the turn's language — minus its "Unit:" caption, so the figure reads
      //    "1,254,442.00 Baht" and not "… Unit: Baht". This is what makes an English card stop ending in
      //    "บาท" without anyone re-authoring a template.
      const card = this.data && this.data.unitLabel;
      if (card != null && String(card).trim() !== '') {
        const text = String(card).trim();
        const cut = text.search(/[:：]/);
        const unit = (cut >= 0 ? text.slice(cut + 1) : text).trim();
        if (unit !== '') return unit;
      }
      return DEFAULT_UNIT_LABEL;
    },
    /**
     * The unit printed at the end of one row's box — "บาท" on money, "คน" on a counter, nothing
     * on a tick. It rides on the ROW now (`f.unitLabel`), because one card mixes rows that do not
     * share a unit and a single card-level label put "บาท" after a headcount.
     *
     * 🔴 The `data.unitLabel` fallback is not dead code and must not be tidied away. This same
     * component draws cards read back out of history (`TaxLogChatPanelV2.vue`,
     * `TaxPlaygroundChatPanelV2.vue`), and those transcripts were written when the unit lived on
     * the card. They cannot be rewritten after the fact, so dropping the fallback would silently
     * strip the unit off every card already in the log. We read it; we no longer emit it.
     */
    unitOf(f) {
      // 🔴 THE APP FILLS THE BLANK, so this must too. `TaxFundField.unitLabel` now defaults to
      //    DEFAULT_UNIT_LABEL: "Absent, null or blank all fall back" — so a money box the author
      //    left empty prints "บาท" on the phone, and a preview that printed nothing there would
      //    be showing a card no customer will ever see. (This used to read as "blank = the admin
      //    said this row has no unit", which was true of the payload and never true of the app.)
      // 🔴 …but only on the money box: the spec is explicit that `"number"` and `"checkbox"` rows
      //    "have no suffix view, so it is ignored there". The child counter therefore shows no
      //    "คน" however the config is authored — see the note on the editor's unit field.
      if (!f || this.isStepperField(f) || this.isCheckboxField(f)) return '';
      const own = f.unitLabel;
      if (own != null && String(own).trim() !== '') return own;
      // The card-level key is GONE from the app's contract (type7 moved the unit per-field). Kept
      // as a fallback for one reason only: this component redraws transcripts written while it
      // still existed, and those cannot be rewritten.
      const legacy = this.data.unitLabel;
      if (legacy != null && String(legacy).trim() !== '') return legacy;
      return DEFAULT_UNIT_LABEL;
    },
    /**
     * A row the card asked to be drawn as − n + rather than as a money box.
     *
     * 🔴 EITHER KEY COUNTS. The card now ships the row's shape twice — `control` ("STEPPER" /
     * "CHECKBOX", absent on a money box) and `type` ("number" / "checkbox" / "textbox", present
     * on every row, the spelling the mobile mock uses). The backend derives both from one
     * predicate so they cannot disagree, but this preview also renders cards read back out of
     * history, which may predate `type` — and it has to keep rendering right the day `control`
     * is dropped from the wire. Matching either is what spans both ends of that window.
     */
    isStepperField(f) {
      if (!f) return false;
      return String(f.control || '').toUpperCase() === 'STEPPER'
        || String(f.type || '').toLowerCase() === 'number';
    },
    /** A row drawn as a single tick — one person who qualifies or does not. Either key, as above. */
    /**
     * A money box — the `v-else` branch, i.e. neither a counter nor a tick.
     *
     * 🔴 Spelled as the ABSENCE of the two named kinds, exactly as the template's `v-if / v-else-if /
     * v-else` chain decides it. A positive test (`type === 'textbox'`) would disagree with the template
     * the moment an unknown `type` arrived: the chain falls through to the money box, and this must fall
     * through with it or `noteAbove` would be drawn twice — once here and once at row level.
     */
    isMoneyField(f) {
      return !this.isCheckboxField(f) && !this.isStepperField(f);
    },
    isCheckboxField(f) {
      if (!f) return false;
      return String(f.control || '').toUpperCase() === 'CHECKBOX'
        || String(f.type || '').toLowerCase() === 'checkbox';
    },
    /** Ticked? Any positive number counts, so a row some other path wrote as "1" still shows ticked. */
    isChecked(f) {
      return Number(this.formValue(f)) > 0;
    },
    /**
     * The ticks under a heading row (`TaxFundField.options`), or an empty list.
     *
     * 🔴 An option with no `id` is DROPPED, the same rule `flattenFields` applies to a row:
     * `id` IS the FieldKey the tick answers, so one without it could be ticked and then be
     * missing from `values` — a control that shows more than it can send.
     * 🔴 EMPTY ⇒ the row falls back to the single tick wearing its own label, which is what
     * every checkbox card authored before options existed still is.
     */
    tickOptions(f) {
      const list = f && Array.isArray(f.options) ? f.options : [];
      return list.filter(o => o && o.id != null && o.id !== '');
    },
    /**
     * One tick.
     * 🔴 Stored as the string "1" / "0", the same plain-digit shape every other row on this card stores, so
     * `formSubmissionValues` and the backend need to know nothing about checkboxes.
     */
    onCheck(f, event) {
      if (!f || f.id == null || !this.formCanEdit || this.formBusy) return;
      const on = Boolean(event && event.target && event.target.checked);
      this.formValues = { ...this.formValues, [f.id]: on ? '1' : '0' };
    },
    /** Where the counter stands: what the customer has pressed it to, else what the card opened at. */
    stepAt(f) {
      const n = Number(this.formValue(f));
      return Number.isFinite(n) ? n : this.stepMin(f);
    },
    stepMin(f) {
      const n = Number(f && f.min);
      return Number.isFinite(n) ? n : 0;
    },
    stepSize(f) {
      const n = Number(f && f.step);
      return Number.isFinite(n) && n > 0 ? n : 1;
    },
    /** Would one more press pass the authored ceiling? No ceiling authored ⇒ never. */
    stepOverMax(f) {
      const max = Number(f && f.max);
      if (!Number.isFinite(max)) return false;
      return this.stepAt(f) + this.stepSize(f) > max;
    },
    /**
     * One press of − / +.
     * 🔴 Stored as the same plain digit STRING an amount box stores, into the same
     * `formValues` map, so `formSubmissionValues` needs to know nothing about counters —
     * the wire carries "2" either way and the backend keeps one reader.
     */
    onStep(f, direction) {
      if (!f || f.id == null || !this.formCanEdit || this.formBusy) return;
      const min = this.stepMin(f);
      const max = Number(f.max);
      let next = this.stepAt(f) + (direction * this.stepSize(f));
      if (next < min) next = min;
      if (Number.isFinite(max) && next > max) next = max;
      this.formValues = { ...this.formValues, [f.id]: String(next) };
    },
    /**
     * "บันทึก" — the boxes as they stand.
     * 🔴 Held back while a box holds text that is not an amount: see `formInvalidIds` for
     * what sending it would silently cost. The button is disabled too, so this is the
     * belt to that brace rather than the only guard.
     */
    onFormSubmit() {
      if (this.formInvalidIds.length) return;
      this.emitFormSubmit('submit');
    },
    /**
     * "ไม่มีรายการเหล่านี้" — an answer of "none", not a way out of the card.
     * 🔴 Deliberately NOT gated on `formInvalidIds`: it sends every box blank, so nothing
     * unreadable goes on the wire, and it is the customer's way past a box they cannot get
     * right. Blocking it would leave them stuck on a card with no answer they can give.
     */
    onFormDecline() {
      this.emitFormSubmit('decline');
    },
    emitFormSubmit(action) {
      if (!this.formCanEdit || this.formBusy) return;
      this.$emit('card-submit', {
        ...this.postbackStamp(),
        action,
        values: this.formSubmissionValues(action),
      });
    },

    /**
     * The tile the card arrives PRE-SELECTED on: the recommended one.
     *
     * 🔴 Pre-selected rather than blank, because the badge already says which one we think
     * they should take — a card that recommends an option and then requires the customer to
     * tap it before the button lights up asks them to confirm a choice the screen has
     * already made for them. They can still tap the other tile; the point is that the
     * default and the recommendation are the same thing.
     * 🔴 …and only ever a value that is ON THIS CARD. A stale pick from the previous card
     * would submit an option the customer is no longer looking at.
     */
    seedFilingPicked() {
      const options = this.sumFilingOptions || [];
      const values = options
        .map((o) => (o && o.value != null ? String(o.value).trim() : ''))
        .filter((v) => v !== '');
      if (!values.length) { this.filingPicked = null; return; }

      const best = options.find((o) => o && o.recommended && o.value != null && String(o.value).trim() !== '');
      this.filingPicked = best ? String(best.value).trim() : values[0];
    },
    onFilingPick(option) {
      if (!this.filingCanPick) return;
      const value = option && option.value != null ? String(option.value).trim() : '';
      if (value === '') return;
      this.filingPicked = value;
    },
    /**
     * "ยืนยัน" — the chosen option's CODE, under the card's own key.
     *
     * 🔴 One key, one value, and both come off the card: the code is what the tile carried
     * (never its label, which is translated per turn and matches nothing the server gates
     * on), and the key is `data.filingOptions.id` — the only key the reader on the other end
     * looks at. See `postbackStamp` on why `kind`/`fieldKey` are read off the envelope too:
     * a press from here has to travel exactly as a press from the phone does.
     */
    /**
     * The summary's «บันทึกและดำเนินการต่อ». 🔴 A MESSAGE, not a postback: the payload carries
     * `message` alone, and the page sends it the way it sends a quick reply (TaxPlaygroundV2
     * `onCardSubmit`). No `postbackStamp()` — stamping `kind`/`fieldKey` here would make the
     * page post a form submission the phone never sends for this button.
     */
    onSumSubmit() {
      if (!this.sumSubmitCanSend) return;
      this.$emit('card-submit', { message: this.sumSubmitMessage });
    },
    onFilingSubmit() {
      if (!this.filingCanPick || !this.filingPicked) return;
      this.$emit('card-submit', {
        ...this.postbackStamp(),
        action: 'submit',
        values: { [this.filingValueKey]: this.filingPicked },
      });
    },

    /**
     * `kind` + `fieldKey` taken from THE CARD'S OWN envelope — the same and only source a
     * mobile client has.
     *
     * 🔴 This is what makes the playground a test of the mobile contract rather than a
     * private channel that happens to work. The page used to stamp `fieldKey` from its own
     * turn list (`liveAskedFieldKey`), a view no app has: every green run here proved
     * nothing about the app, because the app could not name the key at all. Reading the
     * block the card now carries means a press here travels exactly as a press there does.
     *
     * 🔴 `kind` comes from the card too, never from `card.type` — a form card announces
     * itself as TAX_AMOUNT_FORM while its postback kind is TAX_SHORT_FORM.
     *
     * 🔴 NO FALLBACK, on either key (30 Aug 2026). A card with no `postback` block cannot be
     * pressed at all now — `canPostBack` withholds the buttons, the same way the block gates
     * them on the phone — so there is nothing left for a literal to stand in for, and a
     * literal that DID stand in would be this page inventing a capability production does not
     * grant. What the card carries is what goes on the wire; what it omits, nobody sends.
     */
    postbackStamp() {
      const pb = (this.card && this.card.postback) || {};
      const stamp = {};
      if (pb.kind) stamp.kind = pb.kind;
      if (pb.fieldKey) stamp.fieldKey = pb.fieldKey;
      return stamp;
    },
    /**
     * What goes on the wire — every box on screen, normalised to the plain digits the
     * contract asks for. The whole rule (and why each half of it matters) lives in
     * `taxShortFormCard.submissionValues`; this is only the component's hand-off.
     */
    formSubmissionValues(action) {
      return formSubmissionValuesOf(this.formFields, this.formValues, action);
    },
    hasRow(obj, key) {
      return hasObjectRow(obj, key);
    },
    itemsOf(obj) {
      const list = obj && obj.items;
      return Array.isArray(list) ? list : [];
    },
    /**
     * One money cell of a breakdown row. 🔴 Reads `values[]` BY POSITION against the
     * template's own column list, and returns undefined when the row is shorter — which
     * `fmtAmount` renders as the em dash for "not known". A row that padded itself with 0
     * instead would state that the spouse's figure IS zero.
     */
    valueAt(row, i) {
      const list = row && row.values;
      return Array.isArray(list) ? list[i] : undefined;
    },
    /**
     * The `tone` that describes ONE column — `tone[i]` when the payload sends a list, the
     * single value when it sends a string.
     *
     * 🔴 PER COLUMN, because the sign is. A deduction row is printed whenever EITHER spouse
     * has that income, so the one who has none gets a cell of `0`; a single row-level tone
     * had to call that zero NEGATIVE too and the card said "-0" (dev form 1929, turn
     * 180993). The template now authors one tone per column, paired 1:1 with `values`
     * — `"tone": ["DEFAULT", "NEGATIVE"]` — built by `@omitWhenZero` + `@else` so the
     * RULE lives in the card config, not here.
     * 🔴 The string form still answers, and must: question versions already pinned to
     * customers' in-progress forms carry the old single-tone templates, and their cards are
     * re-rendered by this same component in the log viewer.
     */
    toneAt(row, i) {
      const tone = row && row.tone;
      return Array.isArray(tone) ? tone[i] : tone;
    },
    /**
     * One money cell of a breakdown row, with that COLUMN's `tone` applied to the TEXT.
     *
     * 🔴 A MINUS SIGN, not arithmetic. An expense row authors its figures POSITIVE
     * (`values: [100000, 120000]`) and states their direction in `tone`; negating the
     * number here would fight the payload rather than render it.
     * 🔴 The em dash is never signed: "not known" has no direction, and "-—" is nonsense.
     */
    sumSignedAt(row, i) {
      const text = this.fmtBaht(this.valueAt(row, i));
      if (text === EM_DASH) return text;
      return this.toneAt(row, i) === 'NEGATIVE' ? `-${text}` : text;
    },
    /**
     * The column list a breakdown SECTION renders against.
     *
     * 🔴 A section may carry its own `columns[]` — the allowance section's headers read
     * "ประเภทลดหย่อน" where the income section's read "ประเภทรายได้". The block-level list is
     * the fallback, not the rule. Falling back on a section that authored an EMPTY array is
     * deliberate: `[]` is a template that built no headers, not a request for no columns, and
     * a section with no columns renders no money cells at all (`slice(1)` is empty).
     * 🔴 The FILER's list sits between the two (type3.2 authors `columns` per filer and the
     * block has none), so a section inside a filer falls back to the filer before the block.
     *
     * @param {Object} sec The section.
     * @param {?Object} [filer] The filer group holding it — null for the legacy shape.
     */
    columnsOf(sec, filer) {
      const own = sec && sec.columns;
      if (Array.isArray(own) && own.length) return own;
      const mid = filer && filer.columns;
      return Array.isArray(mid) && mid.length ? mid : this.sumColumns;
    },
    /**
     * How ONE ladder column aligns — asked once and given to the header and to every cell
     * beneath it.
     *
     * 🔴 The two used to be decided separately: the header from `columns.length - 1`, the cells
     * from their own fixed position in the row. They agree only while the author writes exactly
     * three headers, and a ladder with two or four put a right-aligned "ภาษีที่ต้องจ่าย" over a
     * left-aligned figure. A row here is always rate / range / amount — that is the shape of the
     * markup, not of the payload — so the amount column is the one the number lives in.
     */
    brColClass(ci) {
      return ci === BRACKET_AMOUNT_COL ? 'tsc-num-cell' : '';
    },
    /**
     * What the rate chip says — `rateLabel` when the template wrote one, else `ratePercent` with
     * the `%` this file used to weld on.
     *
     * 🔴 The literal `%` was the same mistake as the hero's literal ฿: a ladder whose bands are
     * not whole percentages ("0.5%", "20% + เงินเพิ่ม", a band stated as a range) had no way to
     * say so, because the only number on the row was multiplied by a sign this component chose.
     * 🔴 `ratePercent` is NOT retired by this — it stays the structural key. `.tsc-br-pill--zero`
     * still reads it to decide the 0% band earns no bar, and that is a fact about the ladder, not
     * about the words on the chip. A row may relabel itself without becoming a different band.
     */
    rateTextOf(r) {
      const label = r && r.rateLabel;
      if (label != null && String(label).trim() !== '') return String(label);
      const pct = r && r.ratePercent;
      return pct == null || pct === '' ? '' : `${pct}%`;
    },
    /**
     * The ALIGNMENT a header declares, on its own, for the cells beneath it.
     *
     * 🔴 Alignment cascades where colour and size must not. `columnsTextAlign` is a statement
     * about the COLUMN — where its content sits — so a header the author centred over figures
     * that stayed right-aligned was the table disagreeing with itself. `columnsColor` and
     * `columnsFontSize` are statements about the HEADER TEXT and must not leak down, or every
     * figure would turn header-grey; hence the pick rather than passing the whole style through.
     *
     * @param {...Object} sources Hint carriers, least specific first (block, then section).
     * @returns {?Object} `{ textAlign }`, or null when no header authored one.
     */
    colAlign(...sources) {
      const merged = mergeStyles(...sources.map((s) => textStyle(s, 'columns')));
      return merged && merged.textAlign ? { textAlign: merged.textAlign } : null;
    },
    /** A breakdown item's nested rows (`children[]`), or []. */
    childrenOf(it) {
      const list = it && it.children;
      return Array.isArray(list) ? list : [];
    },
    /**
     * The open-state key for one breakdown ITEM's children.
     *
     * 🔴 Its own prefix, for the same reason the summary's sections use `'s'`: all three
     * kinds of collapsible live in ONE `open` map, so a numeric category index, a section
     * and an item must not be able to collide on a key.
     */
    itemKey(si, j) {
      return `i${si}-${j}`;
    },
    /**
     * A breakdown section's open-state key inside its group (`sumSectionGroups`) — `'s0'` for
     * the legacy group, `'f1s0'` for the spouse's, so the two filers' sections never collide.
     */
    secKey(g, i) {
      return (g && g.key ? g.key : '') + 's' + i;
    },
    /** An item's open-state key inside its group — `itemKey(i, j)` unchanged for the legacy group. */
    secItemKey(g, i, j) {
      return this.itemKey(g && g.key ? g.key + 's' + i : i, j);
    },
    /**
     * The BLOCKS a breakdown section's body iterates — one per `groups[]` entry, or ONE block
     * standing for the legacy `items[]` shape.
     *
     * 🔴 The deduction section (2026-09) authors `groups[]` INSTEAD of `items[]`: each group is
     * its own collapsible head over its own table, with its own `columns[]` and an optional
     * `columnNotes[]` line under them. The legacy shape is the single block with no `group`,
     * whose items, keys and test ids come out exactly as they always did (`tid` is '' there).
     * A group's columns fall back group → section → filer → block; `columnsOf` does the tail.
     * `columnNotes` is kept only when at least one entry says something — a line of empty
     * cells would be a blank stripe under the headers.
     *
     * @param {Object} sec The section.
     * @param {?Object} [filer] The filer group holding it — null for the legacy shape.
     */
    sectionBlocks(sec, filer) {
      const groups = sec && sec.groups;
      const list = Array.isArray(groups) ? groups.filter(isPlainObject) : [];
      if (!list.length) {
        return [{ key: 'all', gi: 0, tid: '', group: null, items: this.itemsOf(sec), columns: this.columnsOf(sec, filer), columnNotes: [], last: true }];
      }
      return list.map((group, gi) => {
        const own = group.columns;
        const notes = group.columnNotes;
        return {
          key: 'g' + gi,
          gi,
          tid: 'g' + gi,
          group,
          items: this.itemsOf(group),
          columns: Array.isArray(own) && own.length ? own : this.columnsOf(sec, filer),
          columnNotes: Array.isArray(notes) && notes.some((n) => this.noteText(n)) ? notes : [],
          last: gi === list.length - 1,
        };
      });
    },
    /** A group's open-state key inside its section — `secKey(g, i) + 'g' + gi`, e.g. `'s1g0'` / `'f1s1g0'`. */
    secGroupKey(g, i, gi) {
      return this.secKey(g, i) + 'g' + gi;
    },
    /**
     * An item's open-state key inside its BLOCK — `secItemKey` unchanged for the legacy block,
     * and keyed under the group otherwise (`'is1g0-0'`), so item 0 of group 0 and item 0 of
     * group 1 never share a chevron.
     */
    blockItemKey(g, i, blk, j) {
      return blk && blk.group ? this.itemKey(this.secGroupKey(g, i, blk.gi), j) : this.secItemKey(g, i, j);
    },
    /** A breakdown item's `subItems[]` (the grouped deduction's detail rows), objects only, or []. */
    subItemsOf(it) {
      const list = it && it.subItems;
      return Array.isArray(list) ? list.filter(isPlainObject) : [];
    },
    /**
     * The rows that open under an item: its `children[]` (numbered, each with an expense) and
     * then its `subItems[]` (notes and chips, indented). ONE list so the sub-row markup exists
     * once; `sub` says which class and indent the row takes.
     */
    subRowsOf(it) {
      const rows = this.childrenOf(it).map((row, k) => ({ key: 'c' + k, row, sub: false }));
      this.subItemsOf(it).forEach((row, k) => rows.push({ key: 'u' + k, row, sub: true }));
      return rows;
    },
    /** A note / chip string, or '' for anything that is not a non-empty string (numbers, null…). */
    noteText(v) {
      return typeof v === 'string' && v.trim() ? v : '';
    },
    /** `row[key][ci]` as note text — '' when the array is missing, short, or holds a non-string. */
    noteAt(row, key, ci) {
      const list = row && row[key];
      return Array.isArray(list) ? this.noteText(list[ci]) : '';
    },
    /** A note's text hints plus its `<prefix>Spacing` (dp → px) as the gap above it. */
    noteStyle(row, prefix) {
      return mergeStyles(textStyle(row, prefix), dpStyle(row, prefix + 'Spacing', 'marginTop'));
    },
    /**
     * A chip's fill, text, border, radius, padding and spacing hints as one style. Every hint
     * is optional — the stylesheet's pill (`.tsc-sum-chip`) stands wherever the payload is silent.
     */
    chipStyle(row, prefix) {
      const width = row && typeof row === 'object' ? row[prefix + 'BorderWidth'] : undefined;
      const border = hintColor(row && typeof row === 'object' ? row[prefix + 'BorderColor'] : null);
      return mergeStyles(
        fillStyle(row, prefix + 'FrameColor'),
        textStyle(row, prefix),
        border && typeof width === 'number' && width > 0 ? { border: `${width}px solid ${border}` } : null,
        dpStyle(row, prefix + 'CornerRadius', 'borderRadius'),
        dpStyle(row, prefix + 'PaddingTop', 'paddingTop'),
        dpStyle(row, prefix + 'PaddingBottom', 'paddingBottom'),
        dpStyle(row, prefix + 'PaddingLeft', 'paddingLeft'),
        dpStyle(row, prefix + 'PaddingRight', 'paddingRight'),
        dpStyle(row, prefix + 'Spacing', 'marginTop'),
      );
    },
    /** The indent a group's head takes (`groupsIndent` on the section, dp → px), or null. */
    groupIndent(sec) {
      return dpStyle(sec, 'groupsIndent', 'paddingLeft');
    },
    /** The gap ABOVE a group after the first (`groupsSpacing` on the section, dp → px), or null. */
    groupGap(sec, gi) {
      return gi > 0 ? dpStyle(sec, 'groupsSpacing', 'marginTop') : null;
    },
    /** The indent a sub-row's label cell takes (`subItemsIndent` on its ITEM, dp → px), or null. */
    subIndent(it) {
      return dpStyle(it, 'subItemsIndent', 'paddingLeft');
    },
    /**
     * A stat's unit is template vocabulary (`PERCENT` / `BAHT`), so the suffix is chosen
     * here rather than written into the label — the same figure is shown as "35%" and as
     * "฿800,000" by the same block.
     */
    fmtStat(s) {
      return s ? this.fmtUnit(s, s.value) : '';
    },
    /**
     * One figure wearing the unit its OWNER declares — `fmtStat`'s rule, reachable by any row
     * that names a `unit`, not just a stat.
     *
     * 🔴 Extracted so the hero can use it. The hero's ฿ was a literal in the template, so the
     * card's headline figure was the one number on the card that could not say what it was
     * measured in — and a template asking for anything else had nowhere to ask.
     * 🔴 An unrecognised or absent `unit` prints the BARE figure. Defaulting to ฿ would put a
     * currency on a percentage the moment a template forgot the key, and this preview's whole
     * job is to show what the payload actually says.
     */
    fmtUnit(src, value) {
      const text = this.fmtBaht(value);
      const unit = src && src.unit;
      if (unit === 'PERCENT') return `${text}%`;
      if (unit === 'BAHT') return `฿${text}`;
      return text;
    },
    /**
     * Whole baht, for the summary card only.
     *
     * 🔴 It does NOT bypass `formatAmount` — that function owns the one rule that matters
     * (unknown ⇒ em dash, and `0` is DATA that must still print). All this does is drop a
     * trailing `.00`, because this card's design states round figures: "เพดาน ฿500,000",
     * not "฿500,000.00". A figure with real satang keeps them.
     * Local to this branch on purpose: the other cards' `.00` is their tested contract.
     */
    fmtBaht(v) {
      const text = formatAmount(v);
      return text.endsWith('.00') ? text.slice(0, -3) : text;
    },
    /**
     * The bracket bar. Length and weight come from the row's POSITION in the ladder, not
     * from the tax it produced: this chart is "which rate band is this", and scaling it by
     * amount would make the 5% band look bigger than the 35% one for most customers.
     */
    barStyle(i) {
      const n = Math.max(this.sumBracketRows.length - 1, 1);
      const t = Math.min(Math.max(i / n, 0), 1);
      return {
        width: `${28 + t * 46}%`,
        background: `linear-gradient(90deg,rgba(94,138,124,${0.35 + t * 0.55}),rgba(46,74,68,${0.55 + t * 0.45}))`,
      };
    },
    /** How much of a deduction cap is spent. Unknown cap ⇒ empty bar, never a full one. */
    capStyle(a) {
      const cap = Number(a && a.cap);
      const used = Number(a && a.used);
      if (!Number.isFinite(cap) || cap <= 0 || !Number.isFinite(used)) return { width: '0%' };
      return { width: `${Math.min(Math.max((used / cap) * 100, 0), 100)}%` };
    },
    /**
     * ข้อความอัตราเหมาใต้บรรทัด "รายได้" — `gross.caption` คือทรงที่ mockup ฝั่ง mobile
     * กำหนดไว้ และเป็นทรงที่ template ส่งมาตอนนี้ · `sec.caption` คือทรงเดิม เก็บไว้
     * อ่าน payload ที่บันทึกไปแล้วในหน้าประวัติ ไม่ใช่ทางที่ของใหม่จะมา
     */
    captionOf(sec) {
      if (!sec) return '';
      return (sec.gross && sec.gross.caption) || sec.caption || '';
    },
    /** Whichever object `captionOf` READ the caption out of — the hints have to come off the
     *  same one, or a legacy payload's caption is printed in the colour authored for a
     *  caption it is not showing. Written beside it so the two cannot drift. */
    captionSource(sec) {
      if (!sec) return null;
      return (sec.gross && sec.gross.caption) ? sec.gross : sec;
    },
    sectionsOf(cat) {
      const list = cat && cat.sections;
      return Array.isArray(list) ? list : [];
    },
    fieldsOf(group) {
      const list = group && group.fields;
      return Array.isArray(list) ? list : [];
    },
    toneClass(row) {
      return (row && TONE_CLASS[row.tone]) || '';
    },
    /** The lines under one filing tile's amount. See `filingDetails` — ONE reader, both lanes. */
    fcDetails(option) {
      return filingDetails(option);
    },
    /**
     * One filing detail row's figure.
     *
     * 🔴 The sign goes BEFORE the currency ("+฿62,175"), because it is a statement about the
     * figure and not part of it — and it is asked for by the row (`signed`), never worked out
     * here from which tile the row is on. `amountSign` holds that rule and is pinned by
     * tests/taxCardFormat.test.mjs.
     * 🔴 An unknown value stays a bare em dash: no ฿, no sign. "not known" is not an amount,
     * so dressing it as one would make it look like a figure that failed to load.
     */
    fcDetailAmount(row) {
      const text = this.fmtBaht(row && row.value);
      if (text === EM_DASH) return text;
      return `${amountSign(row)}฿${text}`;
    },
    /**
     * The gross line under the headline amount is a SECOND number, so show it only
     * when it exists and actually differs — otherwise the row says the same thing
     * twice. Both null (today's common case) counts as "no second number".
     */
    showGross(cat) {
      if (!cat || cat.grossAmount === null || cat.grossAmount === undefined) return false;
      return cat.grossAmount !== cat.amount;
    },
  },
};
</script>

<style scoped>
/* Palette — same hard-coded-hex approach as `.chat-bubble` in the chat panel. */
.tsc {
  --tsc-green: #137f6b;
  --tsc-green-soft: #eaf3ef;
  --tsc-grey: #78908c;
  --tsc-border: #e3e8e6;
  --tsc-dark: #13303d;
  --tsc-orange: #e2632a;
  --tsc-ink: #1a1a1a;
  /* The app's ACTION colour — the mini-form's submit fill and its decline button's border/label.
     🔴 Its own token, not `--tsc-green`: that one means "money" here (titles, amounts), and a
     button wearing it would read as a total. Used only by `.tsc-form-btn-*`. */
  --tsc-action: #7fb3b3;

  /* 🔴 Fluid on purpose: the parent is a narrow chat column on desktop and a tab
     on mobile. No min-width anywhere in this file. */
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(19, 48, 61, 0.12);
  overflow: hidden;
  font-size: 13px;
  color: var(--tsc-ink);
}

.tsc-unknown {
  padding: 12px;
}

/* ── headline band ─────────────────────────────────────────────────────── */
/* 🔴 A PANEL INSIDE the card, not a masthead across the top of it — the margin and radius are what
   the app draws, and they are also what stops the band's authored `frameColor` from fighting the
   card's own `border-radius: 16px` at the corners. `background` stays a DEFAULT: `headline.frameColor`
   arrives inline and wins. */
.tsc-headline {
  background: var(--tsc-dark);
  color: #fff;
  margin: 8px;
  border-radius: 12px;
  padding: 14px 16px 12px;
}

.tsc-hl-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.72);
  word-break: break-word;
}

.tsc-hl-label2 {
  font-weight: 700;
}

.tsc-hl-amount {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
}

.tsc-hl-num {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.15;
  color: #fff;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.tsc-hl-unit {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.72);
}

.tsc-hl-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.tsc-pill {
  flex: 1 1 120px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 8px 10px;
}

.tsc-pill-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.72);
  word-break: break-word;
}

/* The figure and its unit on one baseline — the same arrangement as `.tsc-hl-amount` above.
   `flex-wrap` because a long unit on a narrow tile must drop below the figure rather than
   push it out of the pill. */
.tsc-pill-amt {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 2px;
}

.tsc-pill-num {
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.tsc-pill-unit {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.72);
  white-space: nowrap;
}

/* ── body ──────────────────────────────────────────────────────────────── */
.tsc-body {
  padding: 12px;
}

/* 🔴 A modifier rather than a change to `.tsc-body`: that rule is shared with the summary, filing
   and simulation branches, and the mini-form is the only one being matched to the app here. The
   app gives this card noticeably more room than the chat card's 12px. */
.tsc-body--form {
  padding: 16px;
}

.tsc-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--tsc-green);
  word-break: break-word;
  margin-bottom: 6px;
}

/* Three labels spread across one row. `flex-wrap` stays: the preview column is narrow and a
   three-across row that cannot fit must wrap rather than crush its middle child. */
.tsc-meta {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px 8px;
  font-size: 13px;
  color: var(--tsc-grey);
  margin-bottom: 8px;
}

.tsc-meta > span {
  word-break: break-word;
}

.tsc-empty {
  font-size: 12px;
  color: var(--tsc-grey);
  padding: 6px 2px;
}

/* ── category row ──────────────────────────────────────────────────────── */
.tsc-cat {
  border: 1px solid var(--tsc-border);
  border-radius: 12px;
  overflow: hidden;
}

.tsc-cat + .tsc-cat {
  margin-top: 8px;
}

.tsc-cat-head {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px;
  background: transparent;
  border: 0;
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: inherit;
}

.tsc-cat-head:hover {
  background: rgba(19, 127, 107, 0.04);
}

.tsc-cat-head:focus-visible {
  outline: 2px solid var(--tsc-green);
  outline-offset: -2px;
}

.tsc-badge {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 34px;
  min-height: 38px;
  padding: 4px 8px;
  border-radius: 10px;
  background: var(--tsc-green-soft);
}

/* 🔴 INK, not `--tsc-green`. The green is this component's MONEY colour (amounts, `.tsc-title`);
   "40" is a section number and the category name is a heading, and painting either of them in the
   money colour said they were figures. Defaults only — `codeColor` / `titleColor` still win. */
.tsc-badge-code {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.1;
  color: var(--tsc-ink);
}

.tsc-badge-num {
  font-size: 13px;
  line-height: 1.2;
  color: var(--tsc-grey);
  white-space: nowrap;
}

/* Payload carried no `code` (TAX_INCOME_BREAKDOWN never does). The number is then the
   badge's only content, so it takes the primary token treatment and centres in the same
   38px box — the row height must not change with the shape of the data. */
.tsc-badge--num-only .tsc-badge-num {
  font-size: 13px;
  font-weight: 700;
  line-height: 1.1;
  color: var(--tsc-green);
}

.tsc-cat-main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.tsc-cat-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--tsc-ink);
  word-break: break-word;
}

.tsc-cat-sub {
  font-size: 13px;
  color: var(--tsc-grey);
  word-break: break-word;
}

.tsc-cat-amt {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.tsc-amt {
  font-size: 14px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* The figure BEFORE the deduction. Struck through because that is what it means — drawn plain it
   read as a second, equally valid total sitting under the real one. */
.tsc-amt-sub {
  font-size: 12px;
  color: var(--tsc-grey);
  text-decoration: line-through;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.tsc-chev {
  flex: 0 0 auto;
  color: var(--tsc-grey);
  transition: transform 0.15s ease;
}

.tsc-chev--open {
  transform: rotate(180deg);
}

/* ── expanded content ──────────────────────────────────────────────────── */
.tsc-cat-body {
  padding: 0 10px 10px;
}

.tsc-sec + .tsc-sec {
  margin-top: 10px;
}

.tsc-sec-title {
  font-size: 12px;
  font-weight: 700;
  color: var(--tsc-ink);
  word-break: break-word;
  margin-bottom: 2px;
}

.tsc-line {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 3px 0;
  font-size: 13px;
}

/* A row whose label and amount are joined by a leader. The gap shrinks because the dashes now do
   the separating, and `space-between` has nothing left to push apart — the leader is the element
   that grows. */
.tsc-line--lead {
  gap: 8px;
}

/* 🔴 An empty element, on purpose: a run of literal dots would be read out by a screen reader and
   would not stretch to the width the row actually has. `align-self: center` because `.tsc-line` is
   baseline-aligned — a bottom border on a baseline-aligned box sits under the text, not between
   the two figures. */
.tsc-line-lead {
  flex: 1 1 auto;
  min-width: 12px;
  align-self: center;
  border-bottom: 1px dashed var(--tsc-border);
}

.tsc-line--bold {
  font-weight: 700;
}

.tsc-line-label {
  min-width: 0;
  color: var(--tsc-ink);
  word-break: break-word;
}

.tsc-line-amt {
  flex: 0 0 auto;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.tsc-neg {
  color: var(--tsc-orange);
}

/* The other half of the `tone` vocabulary (see TONE_CLASS). Same green the simulation's
   POSITIVE rows wear — one word, one colour, wherever on this card it is said. */
.tsc-pos {
  color: var(--tsc-green);
}

.tsc-caption {
  display: inline-block;
  background: var(--tsc-green-soft);
  color: var(--tsc-green);
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 11px;
  margin: 2px 0 4px;
  word-break: break-word;
}

/* 🔴 SOLID, and the class is no longer called `dash` for exactly that reason. The only dashed line
   on this card is the LEADER inside an item row (`.tsc-line-lead`) — the separators that open and
   close the item list are solid rules, as the app draws them. Naming a solid rule "dash" is how the
   two would drift back together. */
.tsc-sep {
  border-top: 1px solid var(--tsc-border);
  margin: 6px 0;
}

/* The one that OPENS the body sits directly under the header button, which already has its own
   10px of padding — so it takes no top margin of its own. */
.tsc-sep--head {
  margin-top: 0;
}

.tsc-rule {
  border-top: 2px solid var(--tsc-border);
  margin: 8px 0 4px;
}

/* ── TAX_AMOUNT_FORM (live mini-form) ──────────────────────────────────── */
/* Plain white card: no headline band and no accordion, so the title carries the
   hierarchy on its own and stays ink — the green `.tsc-title` reads as a money total,
   which is exactly what this card is NOT. */
/* 🔴 The sizes below are the APP's, not this component's 13px chat scale. Every one of them is a
   DEFAULT that the matching `<prefix>FontSize` still overrides — they decide what a card that
   authored no hints looks like, and that card has to look like the phone. */
.tsc-form-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--tsc-ink);
  word-break: break-word;
}

.tsc-form-sub {
  font-size: 14px;
  line-height: 1.45;
  color: var(--tsc-grey);
  word-break: break-word;
  margin-top: 4px;
}

/* 🔴 This fires on the FIRST group too, so it is also the gap under the subtitle — the two are
   one number on purpose (the app opens the field list at the same distance from the subtitle as
   it separates two groups by, because a group boundary IS space there and nothing else). */
.tsc-form-group {
  margin-top: 16px;
}

/* Only drawn when the card authored `dividerColor` — see `formDividerStyle`. The width and style
   live here, the colour arrives inline; without the colour the element is not rendered at all. */
.tsc-form-rule {
  border-top-width: 1px;
  border-top-style: solid;
  margin: 0 0 16px;
}

.tsc-form-row + .tsc-form-row {
  margin-top: 16px;
}

/* Label on its own line, box beneath it — the app's stacked layout (mock `2.3b1`). */
.tsc-form-line {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* …EXCEPT a counter, which the app sets beside its label. See the row's own comment. */
.tsc-form-line--inline {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.tsc-form-label {
  min-width: 0;
  font-size: 14px;
  color: var(--tsc-ink);
  word-break: break-word;
}

/* The app's number field — full width, white, bordered, with the unit sitting inside on
   the right, exactly as the customer sees it. The border lives on the WRAPPER so the unit
   sits inside the same frame as the box's own text. */
.tsc-form-box {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 6px;
  border: 1px solid var(--tsc-border);
  border-radius: 8px;
  background: #fff;
  padding: 11px 14px;
}

.tsc-form-box:focus-within {
  border-color: var(--tsc-green);
}

/* An older turn, or a surface with nothing to submit to — the boxes still read, but the
   tint says this card is a record rather than a screen waiting for an answer. */
.tsc-form-box--static {
  background: #fafbfb;
}

/* Text we cannot put on the wire. Same red as the simulation's over-limit line — one
   "this figure is a problem" colour on these cards, not two. The rule beats `:focus-within`
   below it on purpose: the box stays flagged while the customer is fixing it. */
.tsc-form-box--error,
.tsc-form-box--error:focus-within {
  border-color: #d64545;
}

/* Bare input: the frame above is the box, so this contributes only the text. Right-aligned
   and tabular like every other figure on these cards.
   🔴 A typed amount is the customer's own answer, not a hint — ink and weighted like a
   figure, while the placeholder stays grey. Showing both the same way would tell an
   operator the customer's answer is an empty box. */
.tsc-form-input {
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  padding: 0;
  background: transparent;
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  color: var(--tsc-ink);
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.tsc-form-input::placeholder {
  font-weight: 400;
  /* The fallbacks ARE the card's own palette: a payload that authored no placeholder hint
     sets neither property, so this rule resolves to exactly what it did before hints
     existed. See `placeholderVars` for why they arrive as custom properties at all. */
  color: var(--tsc-ph-color, var(--tsc-grey));
  font-size: var(--tsc-ph-size, inherit);
}

/* The wrapper draws the focus ring (`:focus-within`) — a second one inside it would frame
   the text rather than the box. */
.tsc-form-input:focus {
  outline: none;
}

.tsc-form-input:read-only {
  cursor: default;
}

.tsc-form-unit {
  flex: 0 0 auto;
  font-size: 14px;
  color: var(--tsc-grey);
}

/* The tick row. No frame: a checkbox with its label IS the control, and boxing it the way an amount
   row is boxed would make an empty-looking field out of something that is already complete. */
.tsc-check {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

/* A heading over ticks. The label is a heading rather than a control's caption, so it sits on its
   own line with the ⓘ beside it and the ticks below — the layout the app's own mock uses for
   «อุปการะเลี้ยงดูพ่อแม่ (ตนเอง)». */
.tsc-check-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tsc-check-head {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* 🔴 TWO PER ROW, which is the app's contract for this control and not a preference — a third
   tick belongs on the next line however much room is left. `minmax(0, 1fr)` rather than `1fr` so a
   long option label wraps inside its column instead of widening the grid past the card. */
.tsc-check-opts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 12px;
}

.tsc-check-input {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  accent-color: var(--tsc-green);
  cursor: pointer;
}

.tsc-check-input:disabled {
  cursor: default;
}

.tsc-check-label {
  min-width: 0;
  font-size: 14px;
  color: var(--tsc-ink);
  word-break: break-word;
}

/* The tick row's `note`, as the app hangs it: an ⓘ beside the label carrying the text in its
   tooltip rather than a grey paragraph under a box that is not there. `flex: 0 0 auto` so a long
   label wraps around it instead of squeezing it away. */
.tsc-check-info {
  flex: 0 0 auto;
  font-size: 14px;
  line-height: 1;
  color: var(--tsc-grey);
  cursor: help;
}

/* The counter row. Same frame as the money box above so the two kinds of row read as one
   card, but the content is a control rather than a field: − value + sit centred, and the
   figure keeps the tabular figures every other number on these cards uses. */
.tsc-step {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  border: 1px solid var(--tsc-border);
  border-radius: 8px;
  background: #fff;
  padding: 7px 14px;
}

.tsc-step--static {
  background: #fafbfb;
}

.tsc-step-btn {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border: 1px solid var(--tsc-border);
  border-radius: 50%;
  background: #fff;
  font-family: inherit;
  font-size: 17px;
  line-height: 1;
  color: var(--tsc-ink);
  cursor: pointer;
}

.tsc-step-btn:hover:not(:disabled) {
  border-color: var(--tsc-green);
  color: var(--tsc-green);
}

/* A press that would leave the authored range, or a card nobody can answer. Greyed rather
   than hidden: the control still has to read as a control, or the row looks broken. */
.tsc-step-btn:disabled {
  color: var(--tsc-grey);
  border-color: var(--tsc-border);
  background: #f4f6f6;
  cursor: default;
}

.tsc-step-value {
  min-width: 34px;
  font-size: 15px;
  font-weight: 700;
  color: var(--tsc-ink);
  font-variant-numeric: tabular-nums;
  text-align: center;
}

.tsc-step-unit {
  flex: 0 0 auto;
  font-size: 12px;
  color: var(--tsc-grey);
}

/* Closes the row rather than opening it — the gap is on TOP, like the error below it, because the
   note sits under the box it explains. A margin-bottom here would leave the note floating between
   its own box and the next row's label.
   🔴 SHARED by both captions — `--above` adds only what differs. So this rule is where the size and
   colour of BOTH live, and a change here moves both by design. */
.tsc-form-note {
  font-size: 13px;
  line-height: 1.45;
  color: var(--tsc-grey);
  word-break: break-word;
  margin-top: 6px;
}

/* The caption above the box. `margin-bottom`, not `margin-top`: unlike `noteBelow` it OPENS the row.
   🔴 Right-aligned BY DEFAULT, because that is where the app puts it. It is only a default: the row's
   own `noteAboveTextAlign` arrives as an inline style and wins. The dashed left rule that used to be
   here went with the «ยังไม่ขึ้นบนแอป» tag — both said "the phone will not draw this", which stopped
   being true. */
.tsc-form-note--above {
  margin-top: 0;
  margin-bottom: 6px;
  text-align: right;
}

/* Reads like the note but in the row's own red, and closes the row rather than opening it —
   the message belongs to the box directly above it, not to the card, so its gap is on top.
   Weighted so it reads as a correction rather than a second hint. */
.tsc-form-err {
  font-size: 11px;
  line-height: 1.45;
  font-weight: 600;
  color: #d64545;
  word-break: break-word;
  margin-top: 4px;
}

/* Full-width stacked buttons: the chat column is narrow and these two labels are long
   Thai sentences, so a side-by-side pair would wrap into ragged blocks. */
.tsc-form-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
}

/* Rectangle-with-rounded-corners, not a pill, from the app mock. */
.tsc-form-btn {
  display: block;
  width: 100%;
  /* Transparent rather than absent so both buttons are the same height — the secondary
     carries a visible border and the primary does not. */
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 16px 14px;
  text-align: center;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  word-break: break-word;
  cursor: pointer;
}

.tsc-form-btn:disabled {
  cursor: default;
}

/* A LIVE card whose button is held back (a box we cannot send, or a submit already in
   flight) has to look held back — an unchanged button that silently does nothing reads as
   a broken card. `--static` is excluded because it carries its own, gentler tint: that one
   is a record, not a screen refusing an answer. */
.tsc-form-btn:disabled:not(.tsc-form-btn--static) {
  opacity: 0.45;
}

.tsc-form-btn:focus-visible {
  outline: 2px solid var(--tsc-green);
  outline-offset: 2px;
}

/* Drawn but not wired — an older turn, or a surface with nothing to submit to. */
.tsc-form-btn--static {
  opacity: 0.75;
}

/* 🔴 TEAL, and NOT `--tsc-green`. The app's action colour on this card is a soft teal; the card
   palette's green is this component's "money" colour (`.tsc-title`, amounts), and spending it on a
   button would say the button is a total. It is its own token so the two can never drift into each
   other, and it is only a DEFAULT — `submitFrameColor` / `secondaryBorderColor` arrive inline and win. */
.tsc-form-btn--primary {
  background: var(--tsc-action);
  color: #fff;
}

.tsc-form-btn--secondary {
  border-color: var(--tsc-action);
  color: var(--tsc-action);
  background: #fff;
}

/* ── footer ────────────────────────────────────────────────────────────── */
.tsc-foot {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px 8px;
}

.tsc-json {
  font-family: monospace;
  font-size: 11px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  background: #0f1115;
  color: #dbe2ea;
  border-radius: 6px;
  padding: 8px;
  margin: 0 10px 10px;
  max-height: 40vh;
  overflow: auto;
}

/* ══════════════════════════════════════════════════════════════════════════════
   TAX_SUMMARY — the end-of-flow status card (design: 2.3b1)

   🔴 Its own block, sharing only the colour tokens above. The other cards are a list
   of rows in a flat panel; this one is a stack of white cards on a tinted ground with
   a centred hero, so reusing their classes would have meant overriding almost every
   property and leaving both shapes fragile.
   ══════════════════════════════════════════════════════════════════════════════ */
.tsc-sum {
  background: #f4f7f5;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.tsc-sum-hero {
  text-align: center;
  padding: 6px 0 2px;
}
.tsc-sum-hero-label {
  font-size: 12px;
  color: var(--tsc-grey);
}
.tsc-sum-hero-amt {
  font-size: 34px;
  font-weight: 800;
  line-height: 1.15;
  color: var(--tsc-dark);
  letter-spacing: -0.5px;
}
.tsc-sum-hero-cap {
  font-size: 11px;
  color: var(--tsc-grey);
  margin-top: 2px;
}

.tsc-sum-stats {
  display: flex;
  gap: 24px;
  justify-content: space-between;
  padding: 0 6px 2px;
}
.tsc-sum-stat {
  flex: 1 1 0;
}
.tsc-sum-stat:last-child {
  text-align: right;
}
.tsc-sum-stat-label {
  font-size: 11px;
  color: var(--tsc-grey);
}
.tsc-sum-stat-val {
  font-size: 17px;
  font-weight: 700;
  color: var(--tsc-dark);
}

.tsc-sum-box {
  background: #fff;
  border: 1px solid var(--tsc-border);
  border-radius: 12px;
  padding: 12px 14px;
}
.tsc-sum-box-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--tsc-dark);
  margin: 0 0 10px;
}
/* The badge hangs 11 px above its tile (`.tsc-fc-badge`), so the grid needs that back —
   without it the badge sits on the section title instead of on the tile's own edge. */
.tsc-sum-fc {
  margin-top: 12px;
}

/* ── a TAX_SUMMARY that is ONLY the filing choice ───────────────────────────────────
   The standalone type3.1 card rides in this envelope (since 2026-08-27) and has no summary
   in it, so the tinted ground and the white box would frame nothing but the two tiles — a
   card wrapped in a card, on a payload that asks for `mainFrameColor: #00000000`. Stripped
   back to what the TAX_FILING_COMPARE branch draws, which is the same card. See
   `sumIsFilingOnly`. */
.tsc-sum--bare {
  background: transparent;
  padding: 16px 14px 14px;
}
.tsc-sum--bare .tsc-sum-box {
  background: transparent;
  border: 0;
  border-radius: 0;
  padding: 0;
}
/* The section heading IS the card's title here, so it wears `.tsc-fc-title`'s weight.
   2 px + the grid's own 12 px = the 14 px the standalone lane leaves under its title. */
.tsc-sum--bare .tsc-sum-box-title {
  font-size: 17px;
  font-weight: 800;
  color: var(--tsc-ink);
  margin-bottom: 2px;
}

/* ── ขั้นบันได ── */
.tsc-br-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 3px 0;
}
.tsc-br-pill {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  /* width + background come from barStyle() — position in the ladder, never the amount */
}
/* The 0% band earns no bar: there is no tax in it by definition. */
.tsc-br-pill--zero {
  background: none !important;
  width: auto !important;
  color: var(--tsc-grey);
  padding-left: 0;
}
/* The collapsible title. Resets the button chrome so it reads as the `<h4>` it replaces, and
   pushes the chevron to the far end. */
.tsc-br-head {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  font: inherit;
  text-align: left;
}

.tsc-br-head .tsc-chev {
  margin-left: auto;
}

/* ── PER FILER (type3.2) ── one group per filer under the block title, in the ladder and in the
   breakdown alike. Filers are separated by the same hairline the breakdown's sections use. */
.tsc-sum-filer {
  margin-bottom: 10px;
}
.tsc-sum-filer:last-child {
  margin-bottom: 0;
}
.tsc-sum-filer + .tsc-sum-filer {
  border-top: 1px solid var(--tsc-border);
}
/* The ladder's filer head: title left, one figure per column right, chevron last. The figure
   columns share ONE width (`--tsc-br-fcw`, from `valueColumnWidth`) with the header line above
   them — that is what keeps "ภาษีที่ต้องชำระ" over the tax and not over the rate. */
.tsc-br-filer-head {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  background: none;
  border: 0;
  padding: 8px 0;
  cursor: pointer;
  font: inherit;
  text-align: left;
}
.tsc-br-filer-title {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--tsc-ink);
}
.tsc-br-filer-cell {
  flex: 0 0 var(--tsc-br-fcw, 72px);
  width: var(--tsc-br-fcw, 72px);
  text-align: right;
  font-size: 13px;
  font-weight: 700;
  color: var(--tsc-dark);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
/* The 4-column header line drawn once above the filer groups (title column + one per cell). */
.tsc-br-filer-cols {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 0 0 4px;
  border-bottom: 1px solid var(--tsc-border);
  font-size: 12px;
  font-weight: 500;
  color: var(--tsc-grey);
}
/* The header's cells borrow the head's WIDTHS but not its weight or colour: `font: inherit`
   hands them the line's own hints (`columnsColor` / `columnsFontSize`, inline on the line). */
.tsc-br-filer-cols .tsc-br-filer-title,
.tsc-br-filer-cols .tsc-br-filer-cell {
  font: inherit;
  color: inherit;
}
/* 🔴 A blank the width of the head's chevron, so the last header sits over the figure beneath
   it rather than over the chevron. */
.tsc-br-filer-cols::after {
  content: '';
  flex: 0 0 18px;
}

/* ── the TABLE shape of the ladder ── */
/* First column shrinks to its chip, the middle takes the slack, the amount stays on one line. */
.tsc-br-tbl td:first-child,
.tsc-br-tbl th:first-child {
  width: 1%;
  white-space: nowrap;
}

/* 🔴 Columns need air BETWEEN them. `.tsc-sum-tbl` sets vertical padding only — right for the
   breakdown, whose money columns are pushed apart by `padding-left` on the numeric cells, but it
   leaves this table's three columns touching. Trailing padding on the last column would push the
   right-aligned amount off the table's edge, so it is cleared.
   🔴 BOTH class names in the selector, and that is the whole point of it: this table carries
   `tsc-sum-tbl tsc-br-tbl`, and `.tsc-sum-tbl th { padding: 4px 0 }` further down the file has
   the SAME specificity as a single-class rule would — so source order won and the shorthand
   reset the padding-right to zero. The header read "อัตราภาษีช่วงรายได้สุทธิ" as one word. */
.tsc-sum-tbl.tsc-br-tbl th,
.tsc-sum-tbl.tsc-br-tbl td {
  padding-right: 14px;
}

.tsc-sum-tbl.tsc-br-tbl th:last-child,
.tsc-sum-tbl.tsc-br-tbl td:last-child {
  padding-right: 0;
}

/* The rate as a solid chip. Unlike `.tsc-br-pill` it has no gradient and no computed width, so
   an authored `pillColor` is the whole fill rather than a layer under one. */
.tsc-br-chip {
  display: inline-block;
  min-width: 38px;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--tsc-green-soft);
  font-size: 11px;
  font-weight: 700;
  text-align: center;
}

/* The income band the rate applies to. `min-width: 0` + `flex: 1 1 auto` so it takes the space
   between the pill and the amount and wraps rather than shoving the figure off the row. */
.tsc-br-range {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12px;
  color: var(--tsc-ink);
  word-break: break-word;
}

.tsc-br-amt {
  margin-left: auto;
  font-size: 13px;
  font-weight: 600;
  color: var(--tsc-dark);
  font-variant-numeric: tabular-nums;
}

/* ── รายละเอียดสถานะภาษี ── */
.tsc-sum-sec + .tsc-sum-sec {
  border-top: 1px solid var(--tsc-border);
}
.tsc-sum-sec-head {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: 0;
  padding: 10px 0;
  cursor: pointer;
  text-align: left;
}
.tsc-sum-sec-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--tsc-ink);
}
.tsc-sum-sec-amt {
  margin-left: auto;
  font-size: 14px;
  font-weight: 700;
  color: var(--tsc-dark);
  font-variant-numeric: tabular-nums;
}
/* `accent` is the template saying "this figure REDUCES the tax" — the card's one
   colour-carries-meaning rule, and it comes from the payload, never from the key name. */
.tsc-sum-accent {
  color: var(--tsc-green);
}
.tsc-sum-sec-body {
  padding-bottom: 10px;
}

/* The breakdown's per-filer head (type3.2): `.tsc-sum-sec-head` one weight up, with the
   caption ("หลังหักค่าใช้จ่าย + ลดหย่อน") under the label. Its sections nest beneath it. */
.tsc-sum-filer-head {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: 0;
  padding: 10px 0;
  cursor: pointer;
  text-align: left;
  font: inherit;
}
.tsc-sum-filer-head .tsc-sum-sec-label {
  font-size: 14px;
  font-weight: 700;
}
.tsc-sum-filer-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.tsc-sum-filer-cap {
  font-size: 11px;
  color: var(--tsc-grey);
}

.tsc-sum-tbl {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.tsc-sum-tbl th {
  font-weight: 500;
  color: var(--tsc-grey);
  text-align: left;
  padding: 4px 0;
  border-bottom: 1px solid var(--tsc-border);
}
/* 🔴 `.tsc-num-cell` is the element-agnostic third name, and it is what lets a header and the
   cells under it be given the SAME class from the SAME decision. The two `th`/`td` names are
   kept because the breakdown table addresses them directly. */
.tsc-sum-th-num,
.tsc-sum-td-num,
.tsc-num-cell {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  padding-left: 10px;
}
.tsc-sum-tbl td {
  padding: 5px 0;
  color: var(--tsc-ink);
}
/* 🔴 `background` here would DEFEAT an authored `rowFrameColor`: that hint lands inline on the
   `<tr>`, and a cell painted over it hides the row behind it. `inherit` lets the row's own fill
   through and falls back to this rule's own default on the `<tr>` below. */
.tsc-sum-total td {
  background: inherit;
  color: #fff;
  font-weight: 700;
  padding: 7px 8px;
}

.tsc-sum-total {
  background: var(--tsc-dark);
}

/* ── the breakdown's own rows: the §40 chip, the expense line, the numbered children ── */

/* The chip sits ABOVE the label on its own line — the label is a sentence and the tag is a
   code, and setting them side by side made every long label wrap around a 40px block. */
.tsc-sum-tag {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  background: var(--tsc-green-soft);
  font-size: 10px;
  line-height: 1.6;
  white-space: nowrap;
}

.tsc-sum-item-label {
  display: block;
  margin-top: 2px;
  word-break: break-word;
}

/* What the income cost to earn. No colour of its own: `tone` is authored per row and arrives
   as `labelColor` / `valuesColor`, so a card that says nothing gets the table's ink rather
   than a red this file invented. */
.tsc-sum-exp td {
  padding-top: 0;
}

.tsc-sum-exp--child td:first-child {
  padding-left: 22px;
}

/* A child row is indented under the item it belongs to; its circle carries the number. */
.tsc-sum-child td:first-child {
  padding-left: 22px;
}

.tsc-sum-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-right: 6px;
  border-radius: 50%;
  background: var(--tsc-dark);
  color: #fff;
  font-size: 9px;
  line-height: 1;
  vertical-align: middle;
}

.tsc-sum-childtoggle td {
  padding: 0;
}

.tsc-sum-childbtn {
  display: flex;
  width: 100%;
  justify-content: center;
  padding: 2px 0;
  background: none;
  border: 0;
  cursor: pointer;
}

/* ── the grouped deduction section: `groups[]` under a section, notes and chips under rows ── */

/* One group = its own head over its own table. `groupsSpacing` / `groupsIndent` land inline
   when authored; these are the defaults for a payload that says nothing. */
.tsc-sum-group {
  margin-top: 4px;
}
.tsc-sum-group + .tsc-sum-group {
  margin-top: 8px;
}
/* `.tsc-sum-sec-head` one weight DOWN: a group is a heading inside a section, not a section. */
.tsc-sum-group-head {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: 0;
  padding: 8px 0;
  cursor: pointer;
  text-align: left;
  font: inherit;
}
.tsc-sum-group-head .tsc-sum-sec-label {
  font-weight: 500;
}
.tsc-sum-group-head .tsc-sum-sec-amt {
  font-weight: 600;
}
/* The "(จำนวนที่ระบุ/เพดาน)" line under the column headers — muted, smaller, hugging the row
   above it. 🔴 The divider moves DOWN to it: a header row that is not the last in its `<thead>`
   gives up its bottom rule, or the notes would sit between two lines. */
.tsc-sum-tbl thead tr:not(:last-child) th {
  border-bottom: 0;
  padding-bottom: 0;
}
.tsc-sum-colnotes th {
  font-size: 11px;
  font-weight: 400;
  color: var(--tsc-grey);
  padding-top: 0;
}
/* "9k /10.5k" under a label or a figure: its own line, muted. */
.tsc-sum-note {
  display: block;
  font-size: 11px;
  line-height: 1.2;
  color: #8a8f9c;
}
/* "ลดหย่อนเพิ่มได้อีก": a pill under the note. The payload's `*ChipFrameColor` / `*ChipColor`
   repaint it; these are the defaults. */
.tsc-sum-chip {
  display: inline-block;
  margin-top: 2px;
  padding: 1px 6px;
  border-radius: 999px;
  background: #e8f5ec;
  font-size: 10px;
  line-height: 1.5;
  color: #1e7b3a;
  white-space: nowrap;
}
/* A grouped item's `subItems[]` row: indented under its item like a child, and the item's
   `subItemsIndent` overrides the default inline. */
.tsc-sum-sub td:first-child {
  padding-left: 22px;
}

/* The breakdown's bottom line, outside every section's collapse. */
.tsc-sum-foot {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding-top: 10px;
  border-top: 1px solid var(--tsc-border);
}

.tsc-sum-foot-label {
  font-size: 13px;
  color: var(--tsc-ink);
  word-break: break-word;
}

.tsc-sum-foot-amt {
  margin-left: auto;
  font-size: 15px;
  font-weight: 700;
  color: var(--tsc-dark);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.tsc-sum-total td:first-child {
  border-radius: 6px 0 0 6px;
}
.tsc-sum-total td:last-child {
  border-radius: 0 6px 6px 0;
}

/* ── เพดานการลดหย่อน ── */
.tsc-alw + .tsc-alw {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--tsc-border);
}
.tsc-alw-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--tsc-ink);
  margin-bottom: 6px;
}
.tsc-alw-track {
  height: 6px;
  border-radius: 999px;
  background: var(--tsc-green-soft);
  overflow: hidden;
}
.tsc-alw-fill {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: var(--tsc-green);
}
.tsc-alw-meta {
  font-size: 11px;
  color: var(--tsc-grey);
  margin-top: 5px;
}
.tsc-alw-left {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-top: 3px;
  font-size: 12px;
  color: var(--tsc-ink);
}
.tsc-alw-left-amt {
  font-size: 14px;
  font-weight: 800;
  color: var(--tsc-green);
  font-variant-numeric: tabular-nums;
}

/* ─────────────────────────────────────────────────────────────────────────
   TAX_FILING_COMPARE — the filing-method choice (design: 2.3b1, lower card)

   Two tiles side by side; the recommended one carries a green outline and a
   badge that overlaps its top edge. 🔴 The winner is styled, never computed —
   `.tsc-fc-best` is applied from `option.recommended`, which a formula decided.
   ───────────────────────────────────────────────────────────────────────── */
.tsc-fc {
  padding: 16px 14px 14px;
}
.tsc-fc-title {
  font-size: 17px;
  font-weight: 800;
  color: var(--tsc-ink);
  margin-bottom: 14px;
}
.tsc-fc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
}
.tsc-fc-opt {
  position: relative;
  border: 1px solid var(--tsc-border);
  border-radius: 12px;
  padding: 18px 14px 14px;
  background: #fff;
}
.tsc-fc-best {
  border: 1.5px solid var(--tsc-green);
}
/* A tile that can be TAPPED — only on a card carrying the postback block that lets the press
   travel (see `filingCanPick`). The older card's tiles are a picture answered by the chips
   underneath, and a cursor promising otherwise would be the first lie on the screen. */
.tsc-fc-opt--pick {
  cursor: pointer;
}
/* 🔴 The SELECTION is drawn apart from the RECOMMENDATION: the green outline says "we think
   this one", the filled ring says "this is what pressing ยืนยัน will send". They coincide on
   arrival (the card opens on the recommended tile) and must not look identical the moment the
   customer picks the other one — otherwise the screen still appears to recommend what they
   just chose against. */
.tsc-fc-opt--picked {
  box-shadow: 0 0 0 2px var(--tsc-cta, #2b3a5b) inset;
}
/* The confirm button, matching the simulation's CTA — the app draws one control for
   "commit this screen", and two different-looking ones would read as two different kinds. */
.tsc-fc-cta {
  display: block;
  width: 100%;
  margin-top: 14px;
  padding: 14px 12px;
  border: 0;
  border-radius: 9px;
  background: var(--tsc-cta, #2b3a5b);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  font-family: inherit;
  text-align: center;
  cursor: pointer;
}
.tsc-fc-cta:disabled {
  cursor: default;
  opacity: 0.75;
}
/* The summary card's own CTA — same control as the filing card's, sitting under the last box. */
.tsc-sum-cta {
  margin-top: 11px;
}
/* Drawn but not wired — history, or a preview with nothing to submit to. */
.tsc-fc-cta--static {
  opacity: 0.75;
}
/* Overlaps the tile's top border, like the mock. */
.tsc-fc-badge {
  position: absolute;
  top: -11px;
  left: 12px;
  background: var(--tsc-green);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  padding: 5px 9px;
  border-radius: 999px;
  white-space: nowrap;
}
.tsc-fc-label {
  font-size: 13px;
  color: var(--tsc-ink);
}
/* What the figure below IS ("ภาษีที่ต้องจ่าย") — a caption for the amount, so it sits
   between the tile's name and the number it introduces. */
.tsc-fc-sub {
  margin-top: 6px;
  font-size: 11px;
  color: var(--tsc-grey);
}
.tsc-fc-amt {
  margin-top: 4px;
  font-size: 24px;
  font-weight: 800;
  color: var(--tsc-ink);
  font-variant-numeric: tabular-nums;
}

/* What the amount above is made of. 🔴 The colour lives on the BLOCK and is inherited, not
   set on `.tsc-fc-det-val`: a row's `tone` class (`.tsc-neg` / `.tsc-pos`) is declared
   earlier in this file and has the same specificity, so a colour written here would win the
   tie by source order and silently swallow every tone the payload states. */
.tsc-fc-det {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid var(--tsc-border);
  color: var(--tsc-grey);
}
.tsc-fc-det-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  font-size: 11px;
}
.tsc-fc-det-row + .tsc-fc-det-row {
  margin-top: 3px;
}
.tsc-fc-det-val {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.tsc-fc-note {
  margin-top: 4px;
  font-size: 12px;
  color: var(--tsc-grey);
}

/* ─────────────────────────────────────────────────────────────────────────
   TAX_SIMULATION — the what-if screen (design: genie-tax-planner-simulation
   2026-09-08 · type4.json)

   ONE card, top to bottom: the profile strip, the legend, one block per
   group (header · group bar · notes · one bar per row), the three tiles,
   the submit button, the footnote — then, when the payload still carries
   them, the legacy read-out and the before/after result block.
   The bars are real controls where the surface can drag them (playground,
   preview) and static everywhere else.
   ───────────────────────────────────────────────────────────────────────── */
.tsc-sim {
  padding: 16px;
}
.tsc-sim-block + .tsc-sim-block {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--tsc-divider, #edf1f4);
}
/* 🔴 Every size below is a DEFAULT the payload's matching `<prefix>FontSize` still overrides.
   They decide what a card that authored no hints looks like, and that card has to look like the
   phone. */
.tsc-sim-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--tsc-ink);
}
.tsc-sim-sub {
  margin-top: 3px;
  font-size: 12px;
  line-height: 1.5;
  color: #5a6b78;
}

/* ── the profile strip ─────────────────────────────────────────────────── */
.tsc-sim-profile {
  display: flex;
  border-radius: 16px;
  overflow: hidden;
  background: #f5f8fa;
  margin-bottom: 14px;
}
.tsc-sim-profile-cell {
  flex: 1 1 0;
  min-width: 0;
  padding: 11px 10px;
  text-align: center;
}
.tsc-sim-profile-cell + .tsc-sim-profile-cell {
  border-left: 1px solid #e7edf1;
}
.tsc-sim-profile-label {
  display: block;
  font-size: 11px;
  color: #8a99a5;
  margin-bottom: 3px;
}
.tsc-sim-profile-value {
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: var(--tsc-ink);
  font-variant-numeric: tabular-nums;
  word-break: break-word;
}

/* ── the colour key ───────────────────────────────────────────────────── */
.tsc-sim-legend {
  margin-top: 13px;
  display: flex;
  flex-direction: column;
  gap: 9px;
}
.tsc-sim-legend-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  line-height: 1.45;
  color: var(--tsc-ink);
}
.tsc-sim-legend-text b {
  font-weight: 600;
}
.tsc-sim-legend-text em {
  font-style: normal;
  color: #5a6b78;
}
.tsc-sim-swatch {
  display: block;
  width: 26px;
  height: 13px;
  border-radius: 4px;
  flex: 0 0 auto;
  margin-top: 3px;
  box-sizing: border-box;
}

/* ── one group ────────────────────────────────────────────────────────── */
/* 🔴 A rule OPENS EVERY GROUP, including the first — that is how the design separates the
   legend from the first group and two groups from each other. `dividerColor` arrives as a
   `borderTopColor`, never a `background`. */
.tsc-sim-grp {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--tsc-divider, #edf1f4);
}
.tsc-sim-ghead-top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 9px;
}
.tsc-sim-grp-title {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--tsc-ink);
  letter-spacing: -0.2px;
}
/* The status pill — on the group header and on every row. `full` / `over` recolour it. */
.tsc-sim-badge {
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 9px;
  border-radius: 999px;
  background: #eff4f8;
  color: #5a6b78;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.tsc-sim-badge--full {
  background: #fdf1e0;
  color: #9a6415;
}
.tsc-sim-badge--over {
  background: var(--tsc-sim-maximum, #d98b2b);
  color: #fff;
}
.tsc-sim-gbar-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
/* 🔴 `overflow: hidden` keeps the slices inside the pill even when rounding drift pushes their
   sum a hair past 100% — visible as a light notch at the right-hand end otherwise. */
.tsc-sim-gtrack {
  position: relative;
  flex: 1 1 auto;
  height: 10px;
  border-radius: 999px;
  overflow: hidden;
}
.tsc-sim-gzone,
.tsc-sim-gseg,
.tsc-sim-zone,
.tsc-sim-seg {
  position: absolute;
  top: 0;
  bottom: 0;
  display: block;
}
.tsc-sim-gzone,
.tsc-sim-zone {
  right: 0;
}
.tsc-sim-gamt {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #5a6b78;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.tsc-sim-over-text {
  color: var(--tsc-sim-maximum, #d98b2b);
}
/* The lines under the group bar: the cross-group note / the over-cap warning, the lender's
   spill sentence, and any authored remarks — a "›" marks each. */
.tsc-sim-gnote {
  position: relative;
  margin-top: 9px;
  padding-left: 15px;
  font-size: 12px;
  line-height: 1.5;
  color: #5a6b78;
}
.tsc-sim-gnote::before {
  content: '›';
  position: absolute;
  left: 3px;
  top: -1px;
  font-size: 14px;
  color: #8a99a5;
}
.tsc-sim-remark {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: #5a6b78;
}

/* ── one bar ──────────────────────────────────────────────────────────── */
.tsc-sim-row {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--tsc-divider, #edf1f4);
}
.tsc-sim-row-head {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 8px;
}
.tsc-sim-row-label {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--tsc-ink);
}
/* 🔴 The strip is TALLER than the 18px track it draws, because the tag sits above the track
   and the bought caption / cap flag below it — and because the whole strip is the pointer
   target. `touch-action: pan-y` lets a vertical page scroll pass through while a horizontal
   drag moves the slider instead of scrolling the chat. */
.tsc-sim-bar {
  position: relative;
  padding-top: 28px;
  padding-bottom: 40px;
  margin-top: 8px;
  touch-action: pan-y;
  cursor: grab;
  outline: none;
  -webkit-user-select: none;
  user-select: none;
}
.tsc-sim-bar:active,
.tsc-sim-bar--dragging {
  cursor: grabbing;
}
.tsc-sim-bar--static {
  cursor: default;
}
.tsc-sim-bar:focus-visible .tsc-sim-track {
  box-shadow: 0 0 0 3px rgba(92, 143, 203, 0.55);
}
.tsc-sim-track {
  position: relative;
  height: 18px;
  border-radius: 999px;
  overflow: hidden;
}
.tsc-sim-divider {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #fff;
}
/* The dashed ceiling line and its figure, both positioned at the cap. The flag is centred on
   the line, and pulled to end on it when the line sits near the right edge. */
.tsc-sim-capline {
  position: absolute;
  top: 24px;
  height: 26px;
  width: 0;
  border-left: 2px dashed #6e7f8c;
  pointer-events: none;
}
.tsc-sim-capflag {
  position: absolute;
  top: 51px;
  transform: translateX(-50%);
  font-size: 11px;
  font-weight: 600;
  color: #5a6b78;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  pointer-events: none;
}
.tsc-sim-capflag--end {
  transform: translateX(-100%);
}
/* The thumb: a white disc with a coloured ring and three grip lines, centred on the total.
   Its colour is one CSS variable set inline per row (`--tsc-sim-thumb-color`), so the ring and
   the grip change together. */
.tsc-sim-thumb {
  --tsc-sim-thumb-color: var(--tsc-sim-normal, #5c8fcb);
  position: absolute;
  top: 37px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #fff;
  border: 2.5px solid var(--tsc-sim-thumb-color);
  box-sizing: border-box;
  transform: translate(-50%, -50%);
  box-shadow: 0 3px 9px rgba(27, 53, 87, 0.28);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2.5px;
  pointer-events: none;
  z-index: 3;
}
.tsc-sim-thumb i {
  display: block;
  width: 2px;
  height: 11px;
  border-radius: 2px;
  background: var(--tsc-sim-thumb-color);
}
.tsc-sim-thumb--plain i {
  display: none;
}
.tsc-sim-bar--dragging .tsc-sim-thumb {
  transform: translate(-50%, -50%) scale(1.06);
}
/* The live tag over the thumb: two figures in two colours, one pill. */
.tsc-sim-tag {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  border-radius: 9px;
  overflow: hidden;
  box-shadow: 0 3px 10px rgba(27, 53, 87, 0.22);
  white-space: nowrap;
  pointer-events: none;
  z-index: 4;
}
.tsc-sim-tag-a,
.tsc-sim-tag-b {
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 8px;
  font-variant-numeric: tabular-nums;
}
.tsc-sim-tag-b {
  font-weight: 700;
}
.tsc-sim-bar--dragging .tsc-sim-tag {
  transform: translateX(-50%) scale(1.06);
}
/* "ซื้อแล้ว 40,000 🔒" under the left end of the track. */
.tsc-sim-boughtcap {
  position: absolute;
  top: 51px;
  left: 0;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #5a6b78;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  pointer-events: none;
}
.tsc-sim-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 2.5px;
  flex: 0 0 auto;
  margin-right: 4px;
  vertical-align: middle;
}
/* The two boxes under a bar: the plan box (with the editable figure) and the combined box. */
.tsc-sim-figs {
  display: flex;
  align-items: stretch;
  gap: 8px;
  margin-top: 8px;
}
.tsc-sim-fig {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f7fafc;
  border-radius: 11px;
  padding: 8px 10px;
  border: 1.5px solid transparent;
  box-sizing: border-box;
  transition: border-color 0.15s, background 0.15s;
}
.tsc-sim-fig--total {
  background: #eaf3ec;
  flex: 1.15 1 0;
}
.tsc-sim-fig--live {
  border-color: var(--tsc-sim-normal, #5c8fcb);
  background: #e8f0f9;
}
.tsc-sim-fig-lbl {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #5a6b78;
  white-space: nowrap;
  flex: 0 0 auto;
}
.tsc-sim-fig-total {
  margin-left: auto;
  font-size: 15px;
  font-weight: 700;
  color: var(--tsc-green);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.3px;
  white-space: nowrap;
}
.tsc-sim-fig-cap {
  font-size: 10px;
  font-weight: 500;
  color: #8a99a5;
  flex: 0 0 auto;
}
.tsc-sim-row-input {
  margin-left: auto;
  width: 84px;
  min-width: 0;
  height: 30px;
  padding: 0 6px;
  border: 1.5px solid #d9e2e9;
  border-radius: 7px;
  background: #fff;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  color: var(--tsc-ink);
  text-align: right;
  font-variant-numeric: tabular-nums;
  box-sizing: border-box;
}
.tsc-sim-row-input:focus {
  outline: none;
  border-color: var(--tsc-sim-normal, #5c8fcb);
  box-shadow: 0 0 0 3px rgba(92, 143, 203, 0.25);
}
.tsc-sim-fig--live .tsc-sim-row-input {
  border-color: var(--tsc-sim-normal, #5c8fcb);
  color: var(--tsc-sim-normal, #5c8fcb);
}
/* 🔴 A locked row keeps the SAME box, only greyed — `editable: false` is the app drawing a
   figure the customer may read and not change, not a control that has been switched off. */
.tsc-sim-row-input:read-only {
  background: var(--tsc-soft, #f7f7f4);
  color: var(--tsc-grey);
}

/* ── the three tiles ──────────────────────────────────────────────────── */
.tsc-sim-tiles {
  display: flex;
  gap: 10px;
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 16px;
  background: #f5f8fa;
}
.tsc-sim-tile {
  flex: 1 1 0;
  min-width: 0;
}
.tsc-sim-tile-lbl {
  display: block;
  font-size: 11px;
  color: #5a6b78;
  margin-bottom: 2px;
}
.tsc-sim-tile-amt {
  display: block;
  font-size: 17px;
  font-weight: 700;
  color: var(--tsc-ink);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.4px;
  word-break: break-word;
}
.tsc-sim-tile-amt--save {
  color: var(--tsc-green);
}
.tsc-sim-footnote {
  margin-top: 8px;
  text-align: center;
  font-size: 11px;
  color: #42565f;
}

/* ── legacy read-out (`summary[]` on an older payload) ────────────────── */
.tsc-sim-stats {
  margin-top: 10px;
  border-radius: 11px;
  background: #f0f3f7;
  padding: 0 14px;
}
.tsc-sim-stat {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  padding: 14px 0;
  font-size: 13px;
  color: var(--tsc-ink);
}
.tsc-sim-stat + .tsc-sim-stat {
  border-top: 1px solid var(--tsc-divider, #e9ecf1);
}
.tsc-sim-stat-num,
.tsc-sim-stat-pos,
.tsc-sim-stat-neg {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.tsc-sim-stat-pos {
  color: var(--tsc-green);
}
.tsc-sim-stat-neg {
  color: var(--tsc-orange);
}
.tsc-sim-cmp {
  display: flex;
  align-items: stretch;
  gap: 6px;
  margin-top: 12px;
}
.tsc-sim-col {
  flex: 1 1 0;
  min-width: 0;
  border-radius: 10px;
  background: #f4f6f5;
  padding: 10px;
}
.tsc-sim-col-after {
  background: var(--tsc-green-soft);
}
.tsc-sim-col-title {
  font-size: 11px;
  color: var(--tsc-grey);
  margin-bottom: 6px;
}
.tsc-sim-col-row + .tsc-sim-col-row {
  margin-top: 8px;
}
.tsc-sim-col-label {
  font-size: 11px;
  color: var(--tsc-grey);
}
.tsc-sim-col-value {
  font-size: 14px;
  font-weight: 800;
  color: var(--tsc-ink);
  font-variant-numeric: tabular-nums;
  word-break: break-word;
}
.tsc-sim-arrow {
  align-self: center;
  color: var(--tsc-grey);
  font-size: 18px;
}
/* The before/after grid. Its own rules rather than `.tsc-sum-tbl`'s: that table lives in the
   summary branch and carries a header underline this one states through `dividerColor`. */
/* 🔴 Three columns in the app's own weights — 1 : 0.42 : 0.42 — so a reader can follow one
   row across and the two figures line up under their headers. `table-layout: fixed` is what
   makes the widths below binding rather than a suggestion the longest Thai label overrides. */
.tsc-sim-tbl {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  margin-top: 14px;
  border-radius: 9px;
  overflow: hidden;
  background: #f7f7f4;
  font-size: 12px;
}

.tsc-sim-tbl th:first-child,
.tsc-sim-tbl td:first-child {
  width: 54%;
}
.tsc-sim-tbl th + th,
.tsc-sim-tbl td + td {
  width: 23%;
}

.tsc-sim-tbl th,
.tsc-sim-tbl td {
  padding: 10px 12px;
  text-align: left;
  line-height: 1.35;
}

/* 🔴 A rule BETWEEN rows, never above the header — the header is the top edge of the panel and
   a line over it would draw a second frame inside the rounded box. The colour arrives per row
   from `dividerColor`; the width and style live here so an unauthored card still separates. */
.tsc-sim-tbl tr + tr,
.tsc-sim-tbl tbody tr:first-child {
  border-top: 1px solid var(--tsc-divider, #e9ecf1);
}

.tsc-sim-tbl th {
  font-weight: 500;
  color: var(--tsc-grey);
}

.tsc-sim-tbl td {
  font-weight: 700;
  color: var(--tsc-ink);
}
/* The label column stays the quiet one: it names the row, the two figures are what is being
   compared. */
.tsc-sim-tbl td:first-child {
  font-size: 12px;
  font-weight: 400;
  color: var(--tsc-grey);
}
/* "หลังจำลอง" — the outcome of the plan, in the same green the total and the saving wear. */
.tsc-sim-tbl td:last-child {
  color: var(--tsc-green);
}

.tsc-sim-savings {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  margin-top: 14px;
  padding: 14px;
  border-radius: 7px;
  background: #e5f5ec;
  font-size: 15px;
  font-weight: 800;
  color: var(--tsc-green);
}
.tsc-sim-savings-amt {
  font-size: 18px;
  font-variant-numeric: tabular-nums;
}
.tsc-sim-sel {
  margin-top: 16px;
}
.tsc-sim-sel-title {
  font-size: 16px;
  font-weight: 800;
  color: var(--tsc-ink);
  margin-bottom: 6px;
}
.tsc-sim-sel-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
}
.tsc-sim-sel-row + .tsc-sim-sel-row {
  border-top: 1px solid var(--tsc-divider, #e9ecf1);
}
.tsc-sim-sel-label {
  font-size: 14px;
  font-weight: 700;
  color: var(--tsc-ink);
}
.tsc-sim-sel-grp {
  font-size: 11px;
  color: var(--tsc-grey);
}
.tsc-sim-sel-amt {
  font-size: 14px;
  color: var(--tsc-ink);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
/* Both CTAs are the same dark rounded rectangle the app draws — the products button used to
   be a pill, which read as a different KIND of control on a card that shows both. */
.tsc-sim-cta {
  display: block;
  width: 100%;
  margin-top: 16px;
  padding: 14px 12px;
  border: 0;
  border-radius: 9px;
  background: var(--tsc-cta, #2b3a5b);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  font-family: inherit;
  text-align: center;
  cursor: pointer;
}
.tsc-sim-cta:disabled {
  cursor: default;
}
/* Drawn but not wired — an older turn, or a preview with nothing to submit to. Drawn anyway,
   so the layout is honest about the screen the customer actually sees on their phone. */
.tsc-sim-cta--static {
  opacity: 0.75;
}
.tsc-sim-cta--submit {
  margin-top: 14px;
}
/* The secondary way off the result card: back to the sliders. An OUTLINE rather than a fill,
   because it sits directly under the filled "แสดงผลิตภัณฑ์" button and two solid blocks of the
   same colour would give the customer no way to tell the way forward from the way back.
   Every one of these values is overridable from `editButtonStyle` — the merged inline style
   wins over the class, which is why the defaults here are the app's own and not a palette. */
.tsc-sim-cta--secondary {
  margin-top: 10px;
  background: #fff;
  color: var(--tsc-cta, #2b3a5b);
  border: 1px solid var(--tsc-cta, #2b3a5b);
}
/* Every SERVER-computed figure, while the sliders sit past the plan it costed. */
.tsc-sim-stale {
  opacity: 0.45;
}
.tsc-sim-note {
  margin-top: 8px;
  font-size: 11px;
  color: var(--tsc-orange);
}
</style>
